// Auto-generated by fetch-gen
import type { FetchClient, FetchResponse } from '@fgrzl/fetch';
import { buildQueryParams } from '@fgrzl/fetch';

/**
 * Creates an API adapter with typed methods for all OpenAPI operations.
 *
 * @param client - The FetchClient instance to use for HTTP requests
 * @returns An object with typed methods for each API operation
 *
 * @example
 * ```typescript
 * import { createAdapter } from './generated';
 * import client from '@fgrzl/fetch';
 *
 * client.setBaseUrl('https://api.example.com');
 * const api = createAdapter(client);
 *
 * const response = await api.getUsers();
 * if (response.ok) {
 *   console.log(response.data);
 * }
 * ```
 */
export function createAdapter(client: FetchClient): {
  /**
   * Aggregate catalog entities in batch (generic form)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param space - Catalog space: accounts, groups, roles, applications, persons, agents, campaigns, access-reviews
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsBatchResult>
   */
  computeEntityStats: (
    tenant_id: string,
    space: string,
    body: EntityStatsBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsBatchResult>>;
  /**
   * Aggregate catalog entities (generic form)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param space - Catalog space: accounts, groups, roles, applications, persons, agents, campaigns, access-reviews
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsResult>
   */
  getEntityStats: (
    tenant_id: string,
    space: string,
    query?: {
      agg?: string;
      value?: string;
      group_by?: string;
      buckets?: string;
      filter?: Array<string>;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsResult>>;
  /**
   * List access-review items across all connectors (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccessReview>
   */
  listAccessReviewsTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      ref?: string;
      include_system?: boolean;
      campaign_ref?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccessReview>>;
  /**
   * Aggregate access-reviews in batch (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsBatchResult>
   */
  computeAccessReviewsEntityStats: (
    tenant_id: string,
    body: EntityStatsBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsBatchResult>>;
  /**
   * Aggregate access-reviews (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsResult>
   */
  getAccessReviewsEntityStats: (
    tenant_id: string,
    query?: {
      agg?: string;
      value?: string;
      group_by?: string;
      buckets?: string;
      filter?: Array<string>;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsResult>>;
  /**
   * Request a point-in-time account hydration
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<accountHydrationResponse>
   */
  requestAccountHydration: (
    tenant_id: string,
    body: accountHydrationRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<accountHydrationResponse>>;
  /**
   * Poll a point-in-time account hydration
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param hydration_id - Hydration id returned by the request
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<accountHydrationResponse>
   */
  getAccountHydration: (
    tenant_id: string,
    hydration_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<accountHydrationResponse>>;
  /**
   * List accounts across all connectors (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageaccountRow>
   */
  listAccountsTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      search?: string;
      filter?: Array<string>;
      ref?: string;
      include_system?: boolean;
      person_ref?: string;
      connector_id?: string;
      facets?: Array<string>;
      hydrate?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageaccountRow>>;
  /**
   * List the applications an account can access
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplication>
   */
  listApplicationsForAccount: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplication>>;
  /**
   * List the classifications assigned to an account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageClassification>
   */
  listClassificationsForAccount: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageClassification>>;
  /**
   * List the groups an account belongs to
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroup>
   */
  listGroupsForAccount: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroup>>;
  /**
   * List the licenses held by an account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageLicense>
   */
  listLicensesForAccount: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageLicense>>;
  /**
   * List the accounts an owner manages (direct reports), as full rows
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccount>
   */
  listAccountManagedAccounts: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccount>>;
  /**
   * Get an account's manager as a full account row (0 or 1)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccount>
   */
  getManagerForAccount: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccount>>;
  /**
   * List an account's multi-factor registrations
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageMultiFactor>
   */
  listMultiFactorsForAccount: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageMultiFactor>>;
  /**
   * List the permissions granted to an account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePermission>
   */
  listPermissionsForAccount: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePermission>>;
  /**
   * List the persons linked to an account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePerson>
   */
  listPersonsForAccount: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePerson>>;
  /**
   * Get an account with its attributes and every relationship as bounded blocks
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the account
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountProfile>
   */
  getAccountProfile: (
    tenant_id: string,
    account_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountProfile>>;
  /**
   * List the roles assigned to an account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param account_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageRole>
   */
  listRolesForAccount: (
    tenant_id: string,
    account_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageRole>>;
  /**
   * Aggregate accounts in batch (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsBatchResult>
   */
  computeAccountsEntityStats: (
    tenant_id: string,
    body: EntityStatsBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsBatchResult>>;
  /**
   * Aggregate accounts (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsResult>
   */
  getAccountsEntityStats: (
    tenant_id: string,
    query?: {
      agg?: string;
      value?: string;
      group_by?: string;
      buckets?: string;
      filter?: Array<string>;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsResult>>;
  /**
   * List AI agents across all connectors (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageagentRow>
   */
  listAgentsTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      search?: string;
      filter?: Array<string>;
      ref?: string;
      connector_id?: string;
      include_system?: boolean;
      facets?: Array<string>;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageagentRow>>;
  /**
   * Get an agent with its runtime identities, owners, capabilities, and hosting applications
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param agent_id - Distinct identifier (UUID) of the agent
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentProfile>
   */
  getAgentProfile: (
    tenant_id: string,
    agent_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentProfile>>;
  /**
   * Aggregate agents in batch (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsBatchResult>
   */
  computeAgentsEntityStats: (
    tenant_id: string,
    body: EntityStatsBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsBatchResult>>;
  /**
   * Aggregate agents (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsResult>
   */
  getAgentsEntityStats: (
    tenant_id: string,
    query?: {
      agg?: string;
      value?: string;
      group_by?: string;
      buckets?: string;
      filter?: Array<string>;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsResult>>;
  /**
   * List applications across all connectors (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageapplicationRow>
   */
  listApplicationsTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      search?: string;
      filter?: Array<string>;
      ref?: string;
      connector_id?: string;
      include_system?: boolean;
      facets?: Array<string>;
      hydrate?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageapplicationRow>>;
  /**
   * List the accounts with access to an application
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param application_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccount>
   */
  listAccountsForApplication: (
    tenant_id: string,
    application_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccount>>;
  /**
   * List the groups with access to an application
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param application_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroup>
   */
  listGroupsForApplication: (
    tenant_id: string,
    application_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroup>>;
  /**
   * List the roles an application defines
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param application_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageRole>
   */
  listRolesForApplication: (
    tenant_id: string,
    application_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageRole>>;
  /**
   * Get application statistics
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param application_id - Application identifier (UUID)
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<applicationStatisticsResponse>
   */
  getApplicationStatistics: (
    tenant_id: string,
    application_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<applicationStatisticsResponse>>;
  /**
   * Aggregate applications in batch (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsBatchResult>
   */
  computeApplicationsEntityStats: (
    tenant_id: string,
    body: EntityStatsBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsBatchResult>>;
  /**
   * Aggregate applications (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsResult>
   */
  getApplicationsEntityStats: (
    tenant_id: string,
    query?: {
      agg?: string;
      value?: string;
      group_by?: string;
      buckets?: string;
      filter?: Array<string>;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsResult>>;
  /**
   * Enumerate the attribute keys actually collected for a tenant, per kind and connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AttributeKeysResult>
   */
  listCollectedAttributeKeys: (
    tenant_id: string,
    query?: { connector_id?: string; kinds?: string; include_system?: boolean },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AttributeKeysResult>>;
  /**
   * List access-review campaigns across all connectors (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageCampaign>
   */
  listCampaignsTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      ref?: string;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageCampaign>>;
  /**
   * Aggregate campaigns in batch (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsBatchResult>
   */
  computeCampaignsEntityStats: (
    tenant_id: string,
    body: EntityStatsBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsBatchResult>>;
  /**
   * Aggregate campaigns (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsResult>
   */
  getCampaignsEntityStats: (
    tenant_id: string,
    query?: {
      agg?: string;
      value?: string;
      group_by?: string;
      buckets?: string;
      filter?: Array<string>;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsResult>>;
  /**
   * List classifications across all connectors (tenant-wide), with per-classification account counts
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageclassificationWithStats>
   */
  listClassificationsTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      ref?: string;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageclassificationWithStats>>;
  /**
   * List the accounts assigned a classification, as full rows
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param classification_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccount>
   */
  listClassificationAccounts: (
    tenant_id: string,
    classification_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccount>>;
  /**
   * Get classification statistics
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param classification_id - Classification identifier (UUID)
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<classificationStatisticsResponse>
   */
  getClassificationStatistics: (
    tenant_id: string,
    classification_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<classificationStatisticsResponse>>;
  /**
   * Get the accounts carrying this classification
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ClassifiedAccountsResponse>
   */
  getClassifiedAccounts: (
    tenant_id: string,
    query?: { ref?: string; limit?: number; cursor?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ClassifiedAccountsResponse>>;
  /**
   * Get the classifications this account carries
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountClassificationsResponse>
   */
  getAccountClassifications: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountClassificationsResponse>>;
  /**
   * Discard a tenant's stored rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ClassificationRulesResponse>
   */
  deleteClassificationRule: (
    tenant_id: string,
    query?: { rule_id?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ClassificationRulesResponse>>;
  /**
   * List the tenant's effective classification rules
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ClassificationRulesResponse>
   */
  listClassificationRules: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ClassificationRulesResponse>>;
  /**
   * Store one classification rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ClassificationRulesResponse>
   */
  putClassificationRule: (
    tenant_id: string,
    body: ClassificationRuleRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ClassificationRulesResponse>>;
  /**
   * List the fields a classification rule can match on
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ClassificationFieldsResponse>
   */
  listClassificationRuleFields: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ClassificationFieldsResponse>>;
  /**
   * Evaluate a classification rule without applying it
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ClassificationPreviewResponse>
   */
  previewClassificationRule: (
    tenant_id: string,
    body: ClassificationPreviewRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ClassificationPreviewResponse>>;
  /**
   * Check whether a classification rule could be stored
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ClassificationRuleValidateResponse>
   */
  validateClassificationRule: (
    tenant_id: string,
    body: ClassificationRuleValidateRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ClassificationRuleValidateResponse>>;
  /**
   * Delete access review by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccessReviewByRef: (
    tenant_id: string,
    connector_id: string,
    query: { campaign_ref: string; review_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List access reviews from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccessReview>
   */
  listAccessReviews: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccessReview>>;
  /**
   * Reconcile access review without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccessReviewWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccessReview,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find access review by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param access_review_id - Unique identifier for the access review
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccessReview>
   */
  findAccessReview: (
    tenant_id: string,
    connector_id: string,
    access_review_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccessReview>>;
  /**
   * Reconcile access review
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param access_review_id - Unique identifier for the access review
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccessReview>
   */
  reconcileAccessReview: (
    tenant_id: string,
    connector_id: string,
    access_review_id: string,
    body: AccessReview,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccessReview>>;
  /**
   * Reconcile access reviews in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccessReviewsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccessReviewsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account attribute by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountAttributeByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; attribute_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account attributes from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountAttribute>
   */
  listAccountAttributes: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountAttribute>>;
  /**
   * Reconcile account attribute without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountAttributeWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountAttribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account attribute by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_attribute_id - Unique identifier for the account attribute
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountAttribute>
   */
  findAccountAttribute: (
    tenant_id: string,
    connector_id: string,
    account_attribute_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountAttribute>>;
  /**
   * Reconcile account attribute
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_attribute_id - Unique identifier for the account attribute
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountAttribute>
   */
  reconcileAccountAttribute: (
    tenant_id: string,
    connector_id: string,
    account_attribute_id: string,
    body: AccountAttribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountAttribute>>;
  /**
   * Reconcile account attributes in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountAttributesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountAttributesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account classification by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountClassificationByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; classification_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account classifications from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountClassification>
   */
  listAccountClassifications: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountClassification>>;
  /**
   * Reconcile account classification without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountClassificationWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountClassification,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account classification by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_classification_id - Unique identifier for the account classification
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountClassification>
   */
  findAccountClassification: (
    tenant_id: string,
    connector_id: string,
    account_classification_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountClassification>>;
  /**
   * Reconcile account classification
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_classification_id - Unique identifier for the account classification
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountClassification>
   */
  reconcileAccountClassification: (
    tenant_id: string,
    connector_id: string,
    account_classification_id: string,
    body: AccountClassification,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountClassification>>;
  /**
   * Reconcile account classifications in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountClassificationsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountClassificationsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account extension by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountExtensionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account extensions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountExtension>
   */
  listAccountExtensions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountExtension>>;
  /**
   * Reconcile account extension without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountExtensionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account extension by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_extension_id - Unique identifier for the account extension
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountExtension>
   */
  findAccountExtension: (
    tenant_id: string,
    connector_id: string,
    account_extension_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountExtension>>;
  /**
   * Reconcile account extension
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_extension_id - Unique identifier for the account extension
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountExtension>
   */
  reconcileAccountExtension: (
    tenant_id: string,
    connector_id: string,
    account_extension_id: string,
    body: AccountExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountExtension>>;
  /**
   * Reconcile account extensions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountExtensionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountExtensionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account license by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountLicenseByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; license_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account licenses from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountLicense>
   */
  listAccountLicenses: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountLicense>>;
  /**
   * Reconcile account license without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountLicenseWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountLicense,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account license by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_license_id - Unique identifier for the account license
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountLicense>
   */
  findAccountLicense: (
    tenant_id: string,
    connector_id: string,
    account_license_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountLicense>>;
  /**
   * Reconcile account license
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_license_id - Unique identifier for the account license
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountLicense>
   */
  reconcileAccountLicense: (
    tenant_id: string,
    connector_id: string,
    account_license_id: string,
    body: AccountLicense,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountLicense>>;
  /**
   * Reconcile account licenses in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountLicensesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountLicensesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account manager by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountManagerByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; manager_account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account managers from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountManager>
   */
  listAccountManagers: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountManager>>;
  /**
   * Reconcile account manager without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountManagerWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountManager,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account manager by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_manager_id - Unique identifier for the account manager
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountManager>
   */
  findAccountManager: (
    tenant_id: string,
    connector_id: string,
    account_manager_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountManager>>;
  /**
   * Reconcile account manager
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_manager_id - Unique identifier for the account manager
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountManager>
   */
  reconcileAccountManager: (
    tenant_id: string,
    connector_id: string,
    account_manager_id: string,
    body: AccountManager,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountManager>>;
  /**
   * Reconcile account managers in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountManagersBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountManagersRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account multi-factor by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountMultiFactorByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; multi_factor_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account multi-factors from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountMultiFactor>
   */
  listAccountMultiFactors: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountMultiFactor>>;
  /**
   * Reconcile account multi-factor without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountMultiFactorWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountMultiFactor,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account multi-factor by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_multi_factor_id - Unique identifier for the account multi-factor
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountMultiFactor>
   */
  findAccountMultiFactor: (
    tenant_id: string,
    connector_id: string,
    account_multi_factor_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountMultiFactor>>;
  /**
   * Reconcile account multi-factor
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_multi_factor_id - Unique identifier for the account multi-factor
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountMultiFactor>
   */
  reconcileAccountMultiFactor: (
    tenant_id: string,
    connector_id: string,
    account_multi_factor_id: string,
    body: AccountMultiFactor,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountMultiFactor>>;
  /**
   * Reconcile account multi-factors in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountMultiFactorsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountMultiFactorsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account permission by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountPermissionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; permission_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account permissions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountPermission>
   */
  listAccountPermissions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountPermission>>;
  /**
   * Reconcile account permission without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountPermissionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountPermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account permission by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_permission_id - Unique identifier for the account permission
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountPermission>
   */
  findAccountPermission: (
    tenant_id: string,
    connector_id: string,
    account_permission_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountPermission>>;
  /**
   * Reconcile account permission
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_permission_id - Unique identifier for the account permission
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountPermission>
   */
  reconcileAccountPermission: (
    tenant_id: string,
    connector_id: string,
    account_permission_id: string,
    body: AccountPermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountPermission>>;
  /**
   * Reconcile account permissions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountPermissionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountPermissionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account policy by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountPolicyByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; policy_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account policies from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountPolicy>
   */
  listAccountPolicies: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountPolicy>>;
  /**
   * Reconcile account policy without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountPolicyWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountPolicy,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account policy by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_policy_id - Unique identifier for the account policy
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountPolicy>
   */
  findAccountPolicy: (
    tenant_id: string,
    connector_id: string,
    account_policy_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountPolicy>>;
  /**
   * Reconcile account policy
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_policy_id - Unique identifier for the account policy
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountPolicy>
   */
  reconcileAccountPolicy: (
    tenant_id: string,
    connector_id: string,
    account_policy_id: string,
    body: AccountPolicy,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountPolicy>>;
  /**
   * Reconcile account policies in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountPoliciesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountPoliciesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account risk factor by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountRiskFactorByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; risk_factor_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account risk factors from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountRiskFactor>
   */
  listAccountRiskFactors: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountRiskFactor>>;
  /**
   * Reconcile account risk factor without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountRiskFactorWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountRiskFactor,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account risk factor by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_risk_factor_id - Unique identifier for the account risk factor
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountRiskFactor>
   */
  findAccountRiskFactor: (
    tenant_id: string,
    connector_id: string,
    account_risk_factor_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountRiskFactor>>;
  /**
   * Reconcile account risk factor
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_risk_factor_id - Unique identifier for the account risk factor
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountRiskFactor>
   */
  reconcileAccountRiskFactor: (
    tenant_id: string,
    connector_id: string,
    account_risk_factor_id: string,
    body: AccountRiskFactor,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountRiskFactor>>;
  /**
   * Reconcile account risk factors in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountRiskFactorsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountRiskFactorsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account role by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountRoleByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; role_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account roles from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountRole>
   */
  listAccountRoles: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountRole>>;
  /**
   * Reconcile account role without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountRoleWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountRole,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account role by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_role_id - Unique identifier for the account role
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountRole>
   */
  findAccountRole: (
    tenant_id: string,
    connector_id: string,
    account_role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountRole>>;
  /**
   * Reconcile account role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_role_id - Unique identifier for the account role
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountRole>
   */
  reconcileAccountRole: (
    tenant_id: string,
    connector_id: string,
    account_role_id: string,
    body: AccountRole,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountRole>>;
  /**
   * Reconcile account roles in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountRolesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountRolesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account secret by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountSecretByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string; secret_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List account secrets from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccountSecret>
   */
  listAccountSecrets: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccountSecret>>;
  /**
   * Reconcile account secret without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountSecretWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AccountSecret,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account secret by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_secret_id - Unique identifier for the account secret
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountSecret>
   */
  findAccountSecret: (
    tenant_id: string,
    connector_id: string,
    account_secret_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountSecret>>;
  /**
   * Reconcile account secret
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_secret_id - Unique identifier for the account secret
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountSecret>
   */
  reconcileAccountSecret: (
    tenant_id: string,
    connector_id: string,
    account_secret_id: string,
    body: AccountSecret,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountSecret>>;
  /**
   * Reconcile account secrets in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountSecretsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountSecretsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete account by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAccountByRef: (
    tenant_id: string,
    connector_id: string,
    query: { account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List accounts from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccount>
   */
  listAccounts: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccount>>;
  /**
   * Reconcile account without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAccountWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Account,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find account by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_id - Unique identifier for the account
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Account>
   */
  findAccount: (
    tenant_id: string,
    connector_id: string,
    account_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Account>>;
  /**
   * Reconcile account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param account_id - Unique identifier for the account
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Account>
   */
  reconcileAccount: (
    tenant_id: string,
    connector_id: string,
    account_id: string,
    body: Account,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Account>>;
  /**
   * Reconcile accounts in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAccountsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAccountsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete agent account by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAgentAccountByRef: (
    tenant_id: string,
    connector_id: string,
    query: { agent_ref: string; account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List agent accounts from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAgentAccount>
   */
  listAgentAccounts: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAgentAccount>>;
  /**
   * Reconcile agent account without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAgentAccountWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AgentAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find agent account by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_account_id - Unique identifier for the agent account
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentAccount>
   */
  findAgentAccount: (
    tenant_id: string,
    connector_id: string,
    agent_account_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentAccount>>;
  /**
   * Reconcile agent account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_account_id - Unique identifier for the agent account
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentAccount>
   */
  reconcileAgentAccount: (
    tenant_id: string,
    connector_id: string,
    agent_account_id: string,
    body: AgentAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentAccount>>;
  /**
   * Reconcile agent accounts in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAgentAccountsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAgentAccountsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete agent capability by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAgentCapabilityByRef: (
    tenant_id: string,
    connector_id: string,
    query: { agent_ref: string; capability_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List agent capabilities from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAgentCapability>
   */
  listAgentCapabilities: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAgentCapability>>;
  /**
   * Reconcile agent capability without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAgentCapabilityWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AgentCapability,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find agent capability by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_capability_id - Unique identifier for the agent capability
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentCapability>
   */
  findAgentCapability: (
    tenant_id: string,
    connector_id: string,
    agent_capability_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentCapability>>;
  /**
   * Reconcile agent capability
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_capability_id - Unique identifier for the agent capability
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentCapability>
   */
  reconcileAgentCapability: (
    tenant_id: string,
    connector_id: string,
    agent_capability_id: string,
    body: AgentCapability,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentCapability>>;
  /**
   * Reconcile agent capabilities in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAgentCapabilitiesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAgentCapabilitiesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete agent deployment attribute by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAgentDeploymentAttributeByRef: (
    tenant_id: string,
    connector_id: string,
    query: { deployment_ref: string; attribute_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List agent deployment attributes from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAgentDeploymentAttribute>
   */
  listAgentDeploymentAttributes: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAgentDeploymentAttribute>>;
  /**
   * Reconcile agent deployment attribute without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAgentDeploymentAttributeWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AgentDeploymentAttribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find agent deployment attribute by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_deployment_attribute_id - Unique identifier for the attribute value
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDeploymentAttribute>
   */
  findAgentDeploymentAttribute: (
    tenant_id: string,
    connector_id: string,
    agent_deployment_attribute_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDeploymentAttribute>>;
  /**
   * Reconcile agent deployment attribute
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_deployment_attribute_id - Unique identifier for the attribute value
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDeploymentAttribute>
   */
  reconcileAgentDeploymentAttribute: (
    tenant_id: string,
    connector_id: string,
    agent_deployment_attribute_id: string,
    body: AgentDeploymentAttribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDeploymentAttribute>>;
  /**
   * Reconcile agent deployment attributes in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAgentDeploymentAttributesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAgentDeploymentAttributesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete agent deployment extension by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAgentDeploymentExtensionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { deployment_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List agent deployment extensions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAgentDeploymentExtension>
   */
  listAgentDeploymentExtensions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAgentDeploymentExtension>>;
  /**
   * Reconcile agent deployment extension without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAgentDeploymentExtensionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AgentDeploymentExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find agent deployment extension by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_deployment_extension_id - Unique identifier for the extension
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDeploymentExtension>
   */
  findAgentDeploymentExtension: (
    tenant_id: string,
    connector_id: string,
    agent_deployment_extension_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDeploymentExtension>>;
  /**
   * Reconcile agent deployment extension
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_deployment_extension_id - Unique identifier for the extension
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDeploymentExtension>
   */
  reconcileAgentDeploymentExtension: (
    tenant_id: string,
    connector_id: string,
    agent_deployment_extension_id: string,
    body: AgentDeploymentExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDeploymentExtension>>;
  /**
   * Reconcile agent deployment extensions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAgentDeploymentExtensionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAgentDeploymentExtensionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete agent deployment resource by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAgentDeploymentResourceByRef: (
    tenant_id: string,
    connector_id: string,
    query: { deployment_ref: string; resource_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List agent deployment resources from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAgentDeploymentResource>
   */
  listAgentDeploymentResources: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAgentDeploymentResource>>;
  /**
   * Reconcile agent deployment resource without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAgentDeploymentResourceWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AgentDeploymentResource,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find agent deployment resource by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_deployment_resource_id - Unique identifier for the binding
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDeploymentResource>
   */
  findAgentDeploymentResource: (
    tenant_id: string,
    connector_id: string,
    agent_deployment_resource_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDeploymentResource>>;
  /**
   * Reconcile agent deployment resource
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_deployment_resource_id - Unique identifier for the binding
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDeploymentResource>
   */
  reconcileAgentDeploymentResource: (
    tenant_id: string,
    connector_id: string,
    agent_deployment_resource_id: string,
    body: AgentDeploymentResource,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDeploymentResource>>;
  /**
   * Reconcile agent deployment resources in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAgentDeploymentResourcesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAgentDeploymentResourcesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete agent deployment by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAgentDeploymentByRef: (
    tenant_id: string,
    connector_id: string,
    query: { deployment_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List agent deployments from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAgentDeployment>
   */
  listAgentDeployments: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAgentDeployment>>;
  /**
   * Reconcile agent deployment without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAgentDeploymentWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AgentDeployment,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find agent deployment by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_deployment_id - Unique identifier for the agent deployment
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDeployment>
   */
  findAgentDeployment: (
    tenant_id: string,
    connector_id: string,
    agent_deployment_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDeployment>>;
  /**
   * Reconcile agent deployment
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_deployment_id - Unique identifier for the agent deployment
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDeployment>
   */
  reconcileAgentDeployment: (
    tenant_id: string,
    connector_id: string,
    agent_deployment_id: string,
    body: AgentDeployment,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDeployment>>;
  /**
   * Reconcile agent deployments in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAgentDeploymentsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAgentDeploymentsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete agent owner by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAgentOwnerByRef: (
    tenant_id: string,
    connector_id: string,
    query: { agent_ref: string; owner_account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List agent owners from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAgentOwner>
   */
  listAgentOwners: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAgentOwner>>;
  /**
   * Reconcile agent owner without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAgentOwnerWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: AgentOwner,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find agent owner by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_owner_id - Unique identifier for the agent owner
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentOwner>
   */
  findAgentOwner: (
    tenant_id: string,
    connector_id: string,
    agent_owner_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentOwner>>;
  /**
   * Reconcile agent owner
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_owner_id - Unique identifier for the agent owner
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentOwner>
   */
  reconcileAgentOwner: (
    tenant_id: string,
    connector_id: string,
    agent_owner_id: string,
    body: AgentOwner,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentOwner>>;
  /**
   * Reconcile agent owners in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAgentOwnersBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAgentOwnersRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete agent by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAgentByRef: (
    tenant_id: string,
    connector_id: string,
    query: { agent_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List agents from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAgent>
   */
  listAgents: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAgent>>;
  /**
   * Reconcile agent without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAgentWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Agent,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find agent by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_id - Unique identifier for the agent
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Agent>
   */
  findAgent: (
    tenant_id: string,
    connector_id: string,
    agent_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Agent>>;
  /**
   * Reconcile agent
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param agent_id - Unique identifier for the agent
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Agent>
   */
  reconcileAgent: (
    tenant_id: string,
    connector_id: string,
    agent_id: string,
    body: Agent,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Agent>>;
  /**
   * Reconcile agents in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAgentsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAgentsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete application account by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteApplicationAccountByRef: (
    tenant_id: string,
    connector_id: string,
    query: { application_ref: string; account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List application accounts from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplicationAccount>
   */
  listApplicationAccounts: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplicationAccount>>;
  /**
   * Reconcile application account without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileApplicationAccountWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ApplicationAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find application account by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_account_id - Unique identifier for the application account
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationAccount>
   */
  findApplicationAccount: (
    tenant_id: string,
    connector_id: string,
    application_account_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationAccount>>;
  /**
   * Reconcile application account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_account_id - Unique identifier for the application account
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationAccount>
   */
  reconcileApplicationAccount: (
    tenant_id: string,
    connector_id: string,
    application_account_id: string,
    body: ApplicationAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationAccount>>;
  /**
   * Reconcile application accounts in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileApplicationAccountsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchApplicationAccountsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete application agent by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteApplicationAgentByRef: (
    tenant_id: string,
    connector_id: string,
    query: { application_ref: string; agent_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List application agents from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplicationAgent>
   */
  listApplicationAgents: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplicationAgent>>;
  /**
   * Reconcile application agent without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileApplicationAgentWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ApplicationAgent,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find application agent by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_agent_id - Unique identifier for the application agent
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationAgent>
   */
  findApplicationAgent: (
    tenant_id: string,
    connector_id: string,
    application_agent_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationAgent>>;
  /**
   * Reconcile application agent
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_agent_id - Unique identifier for the application agent
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationAgent>
   */
  reconcileApplicationAgent: (
    tenant_id: string,
    connector_id: string,
    application_agent_id: string,
    body: ApplicationAgent,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationAgent>>;
  /**
   * Reconcile application agents in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileApplicationAgentsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchApplicationAgentsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete application extension by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteApplicationExtensionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { application_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List application extensions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplicationExtension>
   */
  listApplicationExtensions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplicationExtension>>;
  /**
   * Reconcile application extension without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileApplicationExtensionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ApplicationExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find application extension by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_extension_id - Unique identifier for the application extension
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationExtension>
   */
  findApplicationExtension: (
    tenant_id: string,
    connector_id: string,
    application_extension_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationExtension>>;
  /**
   * Reconcile application extension
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_extension_id - Unique identifier for the application extension
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationExtension>
   */
  reconcileApplicationExtension: (
    tenant_id: string,
    connector_id: string,
    application_extension_id: string,
    body: ApplicationExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationExtension>>;
  /**
   * Reconcile application extensions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileApplicationExtensionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchApplicationExtensionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete application group by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteApplicationGroupByRef: (
    tenant_id: string,
    connector_id: string,
    query: { application_ref: string; group_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List application groups from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplicationGroup>
   */
  listApplicationGroups: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplicationGroup>>;
  /**
   * Reconcile application group without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileApplicationGroupWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ApplicationGroup,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find application group by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_group_id - Unique identifier for the application group
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationGroup>
   */
  findApplicationGroup: (
    tenant_id: string,
    connector_id: string,
    application_group_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationGroup>>;
  /**
   * Reconcile application group
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_group_id - Unique identifier for the application group
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationGroup>
   */
  reconcileApplicationGroup: (
    tenant_id: string,
    connector_id: string,
    application_group_id: string,
    body: ApplicationGroup,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationGroup>>;
  /**
   * Reconcile application groups in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileApplicationGroupsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchApplicationGroupsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete application permission by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteApplicationPermissionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { application_ref: string; permission_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List application permissions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplicationPermission>
   */
  listApplicationPermissions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplicationPermission>>;
  /**
   * Reconcile application permission without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileApplicationPermissionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ApplicationPermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find application permission by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_permission_id - Unique identifier for the application permission
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationPermission>
   */
  findApplicationPermission: (
    tenant_id: string,
    connector_id: string,
    application_permission_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationPermission>>;
  /**
   * Reconcile application permission
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_permission_id - Unique identifier for the application permission
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationPermission>
   */
  reconcileApplicationPermission: (
    tenant_id: string,
    connector_id: string,
    application_permission_id: string,
    body: ApplicationPermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationPermission>>;
  /**
   * Reconcile application permissions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileApplicationPermissionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchApplicationPermissionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete application role by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteApplicationRoleByRef: (
    tenant_id: string,
    connector_id: string,
    query: { application_ref: string; role_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List application roles from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplicationRole>
   */
  listApplicationRoles: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplicationRole>>;
  /**
   * Reconcile application role without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileApplicationRoleWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ApplicationRole,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find application role by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_role_id - Unique identifier for the application role
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationRole>
   */
  findApplicationRole: (
    tenant_id: string,
    connector_id: string,
    application_role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationRole>>;
  /**
   * Reconcile application role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_role_id - Unique identifier for the application role
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ApplicationRole>
   */
  reconcileApplicationRole: (
    tenant_id: string,
    connector_id: string,
    application_role_id: string,
    body: ApplicationRole,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ApplicationRole>>;
  /**
   * Reconcile application roles in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileApplicationRolesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchApplicationRolesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete application by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteApplicationByRef: (
    tenant_id: string,
    connector_id: string,
    query: { application_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List applications from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplication>
   */
  listApplications: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplication>>;
  /**
   * Reconcile application without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileApplicationWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Application,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find application by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_id - Unique identifier for the application
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Application>
   */
  findApplication: (
    tenant_id: string,
    connector_id: string,
    application_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Application>>;
  /**
   * Reconcile application
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param application_id - Unique identifier for the application
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Application>
   */
  reconcileApplication: (
    tenant_id: string,
    connector_id: string,
    application_id: string,
    body: Application,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Application>>;
  /**
   * Reconcile applications in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileApplicationsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchApplicationsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Apply worker-computed catalog changes
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<CatalogApplyResponse>
   */
  applyCatalogChanges: (
    tenant_id: string,
    connector_id: string,
    body: ApplyCatalogChanges,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<CatalogApplyResponse>>;
  /**
   * Delete attribute by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAttributeByRef: (
    tenant_id: string,
    connector_id: string,
    query: { attribute_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List attributes from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAttribute>
   */
  listAttributes: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAttribute>>;
  /**
   * Reconcile attribute without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileAttributeWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Attribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find attribute by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param attribute_id - Unique identifier for the attribute
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Attribute>
   */
  findAttribute: (
    tenant_id: string,
    connector_id: string,
    attribute_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Attribute>>;
  /**
   * Reconcile attribute
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param attribute_id - Unique identifier for the attribute
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Attribute>
   */
  reconcileAttribute: (
    tenant_id: string,
    connector_id: string,
    attribute_id: string,
    body: Attribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Attribute>>;
  /**
   * Reconcile attributes in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileAttributesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchAttributesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete campaign by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteCampaignByRef: (
    tenant_id: string,
    connector_id: string,
    query: { campaign_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List campaigns from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageCampaign>
   */
  listCampaigns: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageCampaign>>;
  /**
   * Reconcile campaign without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileCampaignWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Campaign,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find campaign by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param campaign_id - Unique identifier for the campaign
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Campaign>
   */
  findCampaign: (
    tenant_id: string,
    connector_id: string,
    campaign_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Campaign>>;
  /**
   * Reconcile campaign
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param campaign_id - Unique identifier for the campaign
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Campaign>
   */
  reconcileCampaign: (
    tenant_id: string,
    connector_id: string,
    campaign_id: string,
    body: Campaign,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Campaign>>;
  /**
   * Reconcile campaigns in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileCampaignsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchCampaignsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete channel account by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteChannelAccountByRef: (
    tenant_id: string,
    connector_id: string,
    query: { channel_ref: string; account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List channel accounts from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageChannelAccount>
   */
  listChannelAccounts: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageChannelAccount>>;
  /**
   * Reconcile channel account without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileChannelAccountWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ChannelAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find channel account by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param channel_account_id - Unique identifier for the channel account
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ChannelAccount>
   */
  findChannelAccount: (
    tenant_id: string,
    connector_id: string,
    channel_account_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ChannelAccount>>;
  /**
   * Reconcile channel account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param channel_account_id - Unique identifier for the channel account
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ChannelAccount>
   */
  reconcileChannelAccount: (
    tenant_id: string,
    connector_id: string,
    channel_account_id: string,
    body: ChannelAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ChannelAccount>>;
  /**
   * Reconcile channel accounts in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileChannelAccountsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchChannelAccountsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete channel by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteChannelByRef: (
    tenant_id: string,
    connector_id: string,
    query: { channel_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List channels from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageChannel>
   */
  listChannels: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageChannel>>;
  /**
   * Reconcile channel without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileChannelWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Channel,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find channel by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param channel_id - Unique identifier for the channel
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Channel>
   */
  findChannel: (
    tenant_id: string,
    connector_id: string,
    channel_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Channel>>;
  /**
   * Reconcile channel
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param channel_id - Unique identifier for the channel
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Channel>
   */
  reconcileChannel: (
    tenant_id: string,
    connector_id: string,
    channel_id: string,
    body: Channel,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Channel>>;
  /**
   * Reconcile channels in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileChannelsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchChannelsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete classification by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteClassificationByRef: (
    tenant_id: string,
    connector_id: string,
    query: { classification_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List classifications from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageClassification>
   */
  listClassifications: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageClassification>>;
  /**
   * Reconcile classification without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileClassificationWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Classification,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find classification by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param classification_id - Unique identifier for the classification
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Classification>
   */
  findClassification: (
    tenant_id: string,
    connector_id: string,
    classification_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Classification>>;
  /**
   * Reconcile classification
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param classification_id - Unique identifier for the classification
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Classification>
   */
  reconcileClassification: (
    tenant_id: string,
    connector_id: string,
    classification_id: string,
    body: Classification,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Classification>>;
  /**
   * Reconcile classifications in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileClassificationsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchClassificationsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete compliance baseline by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteComplianceBaselineByRef: (
    tenant_id: string,
    connector_id: string,
    query: { compliance_baseline_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List compliance baselines from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageComplianceBaseline>
   */
  listComplianceBaselines: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageComplianceBaseline>>;
  /**
   * Reconcile compliance baseline without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileComplianceBaselineWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ComplianceBaseline,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find compliance baseline by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param compliance_baseline_id - Unique identifier for the compliance baseline
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ComplianceBaseline>
   */
  findComplianceBaseline: (
    tenant_id: string,
    connector_id: string,
    compliance_baseline_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ComplianceBaseline>>;
  /**
   * Reconcile compliance baseline
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param compliance_baseline_id - Unique identifier for the compliance baseline
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ComplianceBaseline>
   */
  reconcileComplianceBaseline: (
    tenant_id: string,
    connector_id: string,
    compliance_baseline_id: string,
    body: ComplianceBaseline,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ComplianceBaseline>>;
  /**
   * Reconcile compliance baselines in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileComplianceBaselinesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchComplianceBaselinesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete contact by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteContactByRef: (
    tenant_id: string,
    connector_id: string,
    query: { contact_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List contacts from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageContact>
   */
  listContacts: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageContact>>;
  /**
   * Reconcile contact without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileContactWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Contact,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find contact by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param contact_id - Unique identifier for the contact
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Contact>
   */
  findContact: (
    tenant_id: string,
    connector_id: string,
    contact_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Contact>>;
  /**
   * Reconcile contact
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param contact_id - Unique identifier for the contact
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Contact>
   */
  reconcileContact: (
    tenant_id: string,
    connector_id: string,
    contact_id: string,
    body: Contact,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Contact>>;
  /**
   * Reconcile contacts in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileContactsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchContactsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete device account by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteDeviceAccountByRef: (
    tenant_id: string,
    connector_id: string,
    query: { device_ref: string; account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List device accounts from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageDeviceAccount>
   */
  listDeviceAccounts: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageDeviceAccount>>;
  /**
   * Reconcile device account without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileDeviceAccountWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: DeviceAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find device account by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param device_account_id - Unique identifier for the device account
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<DeviceAccount>
   */
  findDeviceAccount: (
    tenant_id: string,
    connector_id: string,
    device_account_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<DeviceAccount>>;
  /**
   * Reconcile device account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param device_account_id - Unique identifier for the device account
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<DeviceAccount>
   */
  reconcileDeviceAccount: (
    tenant_id: string,
    connector_id: string,
    device_account_id: string,
    body: DeviceAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<DeviceAccount>>;
  /**
   * Reconcile device accounts in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileDeviceAccountsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchDeviceAccountsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete device application by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteDeviceApplicationByRef: (
    tenant_id: string,
    connector_id: string,
    query: { device_ref: string; application_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List device applications from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageDeviceApplication>
   */
  listDeviceApplications: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageDeviceApplication>>;
  /**
   * Reconcile device application without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileDeviceApplicationWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: DeviceApplication,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find device application by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param device_application_id - Unique identifier for the device application
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<DeviceApplication>
   */
  findDeviceApplication: (
    tenant_id: string,
    connector_id: string,
    device_application_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<DeviceApplication>>;
  /**
   * Reconcile device application
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param device_application_id - Unique identifier for the device application
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<DeviceApplication>
   */
  reconcileDeviceApplication: (
    tenant_id: string,
    connector_id: string,
    device_application_id: string,
    body: DeviceApplication,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<DeviceApplication>>;
  /**
   * Reconcile device applications in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileDeviceApplicationsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchDeviceApplicationsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete device by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteDeviceByRef: (
    tenant_id: string,
    connector_id: string,
    query: { device_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List devices from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageDevice>
   */
  listDevices: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageDevice>>;
  /**
   * Reconcile device without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileDeviceWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Device,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find device by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param device_id - Unique identifier for the device
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Device>
   */
  findDevice: (
    tenant_id: string,
    connector_id: string,
    device_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Device>>;
  /**
   * Reconcile device
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param device_id - Unique identifier for the device
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Device>
   */
  reconcileDevice: (
    tenant_id: string,
    connector_id: string,
    device_id: string,
    body: Device,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Device>>;
  /**
   * Reconcile devices in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileDevicesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchDevicesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete employee by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteEmployeeByRef: (
    tenant_id: string,
    connector_id: string,
    query: { employee_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List employees from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageEmployee>
   */
  listEmployees: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageEmployee>>;
  /**
   * Reconcile employee without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileEmployeeWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Employee,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find employee by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param employee_id - Unique identifier for the employee
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Employee>
   */
  findEmployee: (
    tenant_id: string,
    connector_id: string,
    employee_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Employee>>;
  /**
   * Reconcile employee
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param employee_id - Unique identifier for the employee
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Employee>
   */
  reconcileEmployee: (
    tenant_id: string,
    connector_id: string,
    employee_id: string,
    body: Employee,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Employee>>;
  /**
   * Reconcile employees in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileEmployeesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchEmployeesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * List entity kinds with counts
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<EntityKindSummary>>
   */
  listEntityKinds: (
    tenant_id: string,
    connector_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<EntityKindSummary>>>;
  /**
   * Delete federation pool–provider edge by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteFederationServiceProviderByRef: (
    tenant_id: string,
    connector_id: string,
    query: { pool_ref: string; provider_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List federation pool–provider edges from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageFederationServiceProvider>
   */
  listFederationServiceProviders: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageFederationServiceProvider>>;
  /**
   * Reconcile federation pool–provider edge without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileFederationServiceProviderWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: FederationServiceProvider,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find federation pool–provider edge by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param federation_service_provider_id - Unique identifier for the federation pool–provider edge
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<FederationServiceProvider>
   */
  findFederationServiceProvider: (
    tenant_id: string,
    connector_id: string,
    federation_service_provider_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<FederationServiceProvider>>;
  /**
   * Reconcile federation pool–provider edge
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param federation_service_provider_id - Unique identifier for the federation pool–provider edge
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<FederationServiceProvider>
   */
  reconcileFederationServiceProvider: (
    tenant_id: string,
    connector_id: string,
    federation_service_provider_id: string,
    body: FederationServiceProvider,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<FederationServiceProvider>>;
  /**
   * Reconcile federation pool–provider edges in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileFederationServiceProvidersBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchFederationServiceProvidersRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete federation service by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteFederationServiceByRef: (
    tenant_id: string,
    connector_id: string,
    query: { federation_service_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List federation services from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageFederationService>
   */
  listFederationServices: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageFederationService>>;
  /**
   * Reconcile federation service without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileFederationServiceWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: FederationService,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find federation service by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param federation_service_id - Unique identifier for the federation service
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<FederationService>
   */
  findFederationService: (
    tenant_id: string,
    connector_id: string,
    federation_service_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<FederationService>>;
  /**
   * Reconcile federation service
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param federation_service_id - Unique identifier for the federation service
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<FederationService>
   */
  reconcileFederationService: (
    tenant_id: string,
    connector_id: string,
    federation_service_id: string,
    body: FederationService,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<FederationService>>;
  /**
   * Reconcile federation services in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileFederationServicesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchFederationServicesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete group attribute by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteGroupAttributeByRef: (
    tenant_id: string,
    connector_id: string,
    query: { group_ref: string; attribute_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List group attributes from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroupAttribute>
   */
  listGroupAttributes: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroupAttribute>>;
  /**
   * Reconcile group attribute without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileGroupAttributeWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: GroupAttribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find group attribute by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_attribute_id - Unique identifier for the group attribute
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupAttribute>
   */
  findGroupAttribute: (
    tenant_id: string,
    connector_id: string,
    group_attribute_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupAttribute>>;
  /**
   * Reconcile group attribute
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_attribute_id - Unique identifier for the group attribute
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupAttribute>
   */
  reconcileGroupAttribute: (
    tenant_id: string,
    connector_id: string,
    group_attribute_id: string,
    body: GroupAttribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupAttribute>>;
  /**
   * Reconcile group attributes in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileGroupAttributesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchGroupAttributesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete group extension by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteGroupExtensionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { group_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List group extensions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroupExtension>
   */
  listGroupExtensions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroupExtension>>;
  /**
   * Reconcile group extension without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileGroupExtensionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: GroupExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find group extension by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_extension_id - Unique identifier for the group extension
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupExtension>
   */
  findGroupExtension: (
    tenant_id: string,
    connector_id: string,
    group_extension_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupExtension>>;
  /**
   * Reconcile group extension
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_extension_id - Unique identifier for the group extension
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupExtension>
   */
  reconcileGroupExtension: (
    tenant_id: string,
    connector_id: string,
    group_extension_id: string,
    body: GroupExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupExtension>>;
  /**
   * Reconcile group extensions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileGroupExtensionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchGroupExtensionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete group license by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteGroupLicenseByRef: (
    tenant_id: string,
    connector_id: string,
    query: { group_ref: string; license_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List group licenses from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroupLicense>
   */
  listGroupLicenses: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroupLicense>>;
  /**
   * Reconcile group license without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileGroupLicenseWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: GroupLicense,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find group license by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_license_id - Unique identifier for the group license
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupLicense>
   */
  findGroupLicense: (
    tenant_id: string,
    connector_id: string,
    group_license_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupLicense>>;
  /**
   * Reconcile group license
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_license_id - Unique identifier for the group license
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupLicense>
   */
  reconcileGroupLicense: (
    tenant_id: string,
    connector_id: string,
    group_license_id: string,
    body: GroupLicense,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupLicense>>;
  /**
   * Reconcile group licenses in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileGroupLicensesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchGroupLicensesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete group member by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteGroupMemberByRef: (
    tenant_id: string,
    connector_id: string,
    query: { group_ref: string; account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List group members from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroupMember>
   */
  listGroupMembers: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroupMember>>;
  /**
   * Reconcile group member without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileGroupMemberWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: GroupMember,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find group member by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_member_id - Unique identifier for the group member
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupMember>
   */
  findGroupMember: (
    tenant_id: string,
    connector_id: string,
    group_member_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupMember>>;
  /**
   * Reconcile group member
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_member_id - Unique identifier for the group member
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupMember>
   */
  reconcileGroupMember: (
    tenant_id: string,
    connector_id: string,
    group_member_id: string,
    body: GroupMember,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupMember>>;
  /**
   * Reconcile group members in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileGroupMembersBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchGroupMembersRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete group membership role by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteGroupMembershipRoleByRef: (
    tenant_id: string,
    connector_id: string,
    query: { group_ref: string; principal_ref: string; role: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List group membership roles from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroupMembershipRole>
   */
  listGroupMembershipRoles: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroupMembershipRole>>;
  /**
   * Reconcile group membership role without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileGroupMembershipRoleWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: GroupMembershipRole,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find group membership role by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_membership_role_id - Unique identifier for the group membership role
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupMembershipRole>
   */
  findGroupMembershipRole: (
    tenant_id: string,
    connector_id: string,
    group_membership_role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupMembershipRole>>;
  /**
   * Reconcile group membership role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_membership_role_id - Unique identifier for the group membership role
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupMembershipRole>
   */
  reconcileGroupMembershipRole: (
    tenant_id: string,
    connector_id: string,
    group_membership_role_id: string,
    body: GroupMembershipRole,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupMembershipRole>>;
  /**
   * Reconcile group membership roles in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileGroupMembershipRolesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchGroupMembershipRolesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete group permission by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteGroupPermissionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { group_ref: string; permission_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List group permissions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroupPermission>
   */
  listGroupPermissions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroupPermission>>;
  /**
   * Reconcile group permission without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileGroupPermissionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: GroupPermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find group permission by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_permission_id - Unique identifier for the group permission
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupPermission>
   */
  findGroupPermission: (
    tenant_id: string,
    connector_id: string,
    group_permission_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupPermission>>;
  /**
   * Reconcile group permission
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_permission_id - Unique identifier for the group permission
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupPermission>
   */
  reconcileGroupPermission: (
    tenant_id: string,
    connector_id: string,
    group_permission_id: string,
    body: GroupPermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupPermission>>;
  /**
   * Reconcile group permissions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileGroupPermissionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchGroupPermissionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete group role by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteGroupRoleByRef: (
    tenant_id: string,
    connector_id: string,
    query: { group_ref: string; role_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List group roles from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroupRole>
   */
  listGroupRoles: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroupRole>>;
  /**
   * Reconcile group role without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileGroupRoleWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: GroupRole,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find group role by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_role_id - Unique identifier for the group role
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupRole>
   */
  findGroupRole: (
    tenant_id: string,
    connector_id: string,
    group_role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupRole>>;
  /**
   * Reconcile group role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_role_id - Unique identifier for the group role
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupRole>
   */
  reconcileGroupRole: (
    tenant_id: string,
    connector_id: string,
    group_role_id: string,
    body: GroupRole,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupRole>>;
  /**
   * Reconcile group roles in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileGroupRolesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchGroupRolesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete group by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteGroupByRef: (
    tenant_id: string,
    connector_id: string,
    query: { group_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List groups from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroup>
   */
  listGroups: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroup>>;
  /**
   * Reconcile group without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileGroupWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Group,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find group by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_id - Unique identifier for the group
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Group>
   */
  findGroup: (
    tenant_id: string,
    connector_id: string,
    group_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Group>>;
  /**
   * Reconcile group
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param group_id - Unique identifier for the group
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Group>
   */
  reconcileGroup: (
    tenant_id: string,
    connector_id: string,
    group_id: string,
    body: Group,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Group>>;
  /**
   * Reconcile groups in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileGroupsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchGroupsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete license by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteLicenseByRef: (
    tenant_id: string,
    connector_id: string,
    query: { license_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List licenses from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageLicense>
   */
  listLicenses: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageLicense>>;
  /**
   * Reconcile license without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileLicenseWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: License,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find license by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param license_id - Unique identifier for the license
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<License>
   */
  findLicense: (
    tenant_id: string,
    connector_id: string,
    license_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<License>>;
  /**
   * Reconcile license
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param license_id - Unique identifier for the license
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<License>
   */
  reconcileLicense: (
    tenant_id: string,
    connector_id: string,
    license_id: string,
    body: License,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<License>>;
  /**
   * Reconcile licenses in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileLicensesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchLicensesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete location by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteLocationByRef: (
    tenant_id: string,
    connector_id: string,
    query: { location_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List locations from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageLocation>
   */
  listLocations: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageLocation>>;
  /**
   * Reconcile location without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileLocationWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Location,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find location by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param location_id - Unique identifier for the location
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Location>
   */
  findLocation: (
    tenant_id: string,
    connector_id: string,
    location_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Location>>;
  /**
   * Reconcile location
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param location_id - Unique identifier for the location
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Location>
   */
  reconcileLocation: (
    tenant_id: string,
    connector_id: string,
    location_id: string,
    body: Location,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Location>>;
  /**
   * Reconcile locations in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileLocationsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchLocationsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete multi-factor by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteMultiFactorByRef: (
    tenant_id: string,
    connector_id: string,
    query: { multi_factor_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List multi-factors from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageMultiFactor>
   */
  listMultiFactors: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageMultiFactor>>;
  /**
   * Reconcile multi-factor without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileMultiFactorWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: MultiFactor,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find multi-factor by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param multi_factor_id - Unique identifier for the multi-factor
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<MultiFactor>
   */
  findMultiFactor: (
    tenant_id: string,
    connector_id: string,
    multi_factor_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<MultiFactor>>;
  /**
   * Reconcile multi-factor
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param multi_factor_id - Unique identifier for the multi-factor
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<MultiFactor>
   */
  reconcileMultiFactor: (
    tenant_id: string,
    connector_id: string,
    multi_factor_id: string,
    body: MultiFactor,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<MultiFactor>>;
  /**
   * Reconcile multi-factors in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileMultiFactorsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchMultiFactorsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete network object by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteNetworkObjectByRef: (
    tenant_id: string,
    connector_id: string,
    query: { network_object_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List network objects from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageNetworkObject>
   */
  listNetworkObjects: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageNetworkObject>>;
  /**
   * Reconcile network object without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileNetworkObjectWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: NetworkObject,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find network object by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param network_object_id - Unique identifier for the network object
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<NetworkObject>
   */
  findNetworkObject: (
    tenant_id: string,
    connector_id: string,
    network_object_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<NetworkObject>>;
  /**
   * Reconcile network object
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param network_object_id - Unique identifier for the network object
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<NetworkObject>
   */
  reconcileNetworkObject: (
    tenant_id: string,
    connector_id: string,
    network_object_id: string,
    body: NetworkObject,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<NetworkObject>>;
  /**
   * Reconcile network objects in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileNetworkObjectsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchNetworkObjectsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete organizational unit member by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteOrganizationalUnitMemberByRef: (
    tenant_id: string,
    connector_id: string,
    query: { ou_ref: string; principal_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List organizational unit members from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageOrganizationalUnitMember>
   */
  listOrganizationalUnitMembers: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageOrganizationalUnitMember>>;
  /**
   * Reconcile organizational unit member without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileOrganizationalUnitMemberWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: OrganizationalUnitMember,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find organizational unit member by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param organizational_unit_member_id - Unique identifier for the organizational unit member
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OrganizationalUnitMember>
   */
  findOrganizationalUnitMember: (
    tenant_id: string,
    connector_id: string,
    organizational_unit_member_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OrganizationalUnitMember>>;
  /**
   * Reconcile organizational unit member
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param organizational_unit_member_id - Unique identifier for the organizational unit member
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OrganizationalUnitMember>
   */
  reconcileOrganizationalUnitMember: (
    tenant_id: string,
    connector_id: string,
    organizational_unit_member_id: string,
    body: OrganizationalUnitMember,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OrganizationalUnitMember>>;
  /**
   * Reconcile organizational unit members in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileOrganizationalUnitMembersBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchOrganizationalUnitMembersRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete organizational unit by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteOrganizationalUnitByRef: (
    tenant_id: string,
    connector_id: string,
    query: { organizational_unit_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List organizational units from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageOrganizationalUnit>
   */
  listOrganizationalUnits: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageOrganizationalUnit>>;
  /**
   * Reconcile organizational unit without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileOrganizationalUnitWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: OrganizationalUnit,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find organizational unit by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param organizational_unit_id - Unique identifier for the organizational unit
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OrganizationalUnit>
   */
  findOrganizationalUnit: (
    tenant_id: string,
    connector_id: string,
    organizational_unit_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OrganizationalUnit>>;
  /**
   * Reconcile organizational unit
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param organizational_unit_id - Unique identifier for the organizational unit
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OrganizationalUnit>
   */
  reconcileOrganizationalUnit: (
    tenant_id: string,
    connector_id: string,
    organizational_unit_id: string,
    body: OrganizationalUnit,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OrganizationalUnit>>;
  /**
   * Reconcile organizational units in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileOrganizationalUnitsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchOrganizationalUnitsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete password policy by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deletePasswordPolicyByRef: (
    tenant_id: string,
    connector_id: string,
    query: { password_policy_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List password policies from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePasswordPolicy>
   */
  listPasswordPolicies: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePasswordPolicy>>;
  /**
   * Reconcile password policy without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcilePasswordPolicyWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: PasswordPolicy,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find password policy by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param password_policy_id - Unique identifier for the password policy
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PasswordPolicy>
   */
  findPasswordPolicy: (
    tenant_id: string,
    connector_id: string,
    password_policy_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PasswordPolicy>>;
  /**
   * Reconcile password policy
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param password_policy_id - Unique identifier for the password policy
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PasswordPolicy>
   */
  reconcilePasswordPolicy: (
    tenant_id: string,
    connector_id: string,
    password_policy_id: string,
    body: PasswordPolicy,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PasswordPolicy>>;
  /**
   * Reconcile password policies in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcilePasswordPoliciesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchPasswordPoliciesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete people extension by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deletePeopleExtensionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { person_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List people extensions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePeopleExtension>
   */
  listPeopleExtensions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePeopleExtension>>;
  /**
   * Reconcile people extension without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcilePeopleExtensionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: PeopleExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find people extension by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param people_extension_id - Unique identifier for the people extension
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PeopleExtension>
   */
  findPeopleExtension: (
    tenant_id: string,
    connector_id: string,
    people_extension_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PeopleExtension>>;
  /**
   * Reconcile people extension
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param people_extension_id - Unique identifier for the people extension
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PeopleExtension>
   */
  reconcilePeopleExtension: (
    tenant_id: string,
    connector_id: string,
    people_extension_id: string,
    body: PeopleExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PeopleExtension>>;
  /**
   * Reconcile people extensions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcilePeopleExtensionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchPeopleExtensionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete permission by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deletePermissionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { permission_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List permissions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePermission>
   */
  listPermissions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePermission>>;
  /**
   * Reconcile permission without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcilePermissionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Permission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find permission by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param permission_id - Unique identifier for the permission
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Permission>
   */
  findPermission: (
    tenant_id: string,
    connector_id: string,
    permission_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Permission>>;
  /**
   * Reconcile permission
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param permission_id - Unique identifier for the permission
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Permission>
   */
  reconcilePermission: (
    tenant_id: string,
    connector_id: string,
    permission_id: string,
    body: Permission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Permission>>;
  /**
   * Reconcile permissions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcilePermissionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchPermissionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete person account by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deletePersonAccountByRef: (
    tenant_id: string,
    connector_id: string,
    query: { person_ref: string; account_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List person accounts from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePersonAccount>
   */
  listPersonAccounts: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePersonAccount>>;
  /**
   * Reconcile person account without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcilePersonAccountWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: PersonAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find person account by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param person_account_id - Unique identifier for the person account
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PersonAccount>
   */
  findPersonAccount: (
    tenant_id: string,
    connector_id: string,
    person_account_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PersonAccount>>;
  /**
   * Reconcile person account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param person_account_id - Unique identifier for the person account
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PersonAccount>
   */
  reconcilePersonAccount: (
    tenant_id: string,
    connector_id: string,
    person_account_id: string,
    body: PersonAccount,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PersonAccount>>;
  /**
   * Reconcile person accounts in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcilePersonAccountsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchPersonAccountsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete person attribute by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deletePersonAttributeByRef: (
    tenant_id: string,
    connector_id: string,
    query: { person_ref: string; attribute_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List person attributes from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePersonAttribute>
   */
  listPersonAttributes: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePersonAttribute>>;
  /**
   * Reconcile person attribute without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcilePersonAttributeWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: PersonAttribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find person attribute by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param person_attribute_id - Unique identifier for the person attribute
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PersonAttribute>
   */
  findPersonAttribute: (
    tenant_id: string,
    connector_id: string,
    person_attribute_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PersonAttribute>>;
  /**
   * Reconcile person attribute
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param person_attribute_id - Unique identifier for the person attribute
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PersonAttribute>
   */
  reconcilePersonAttribute: (
    tenant_id: string,
    connector_id: string,
    person_attribute_id: string,
    body: PersonAttribute,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PersonAttribute>>;
  /**
   * Reconcile person attributes in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcilePersonAttributesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchPersonAttributesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete person by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deletePersonByRef: (
    tenant_id: string,
    connector_id: string,
    query: { person_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List persons from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePerson>
   */
  listPersons: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePerson>>;
  /**
   * Reconcile person without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcilePersonWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Person,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find person by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param person_id - Unique identifier for the person
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Person>
   */
  findPerson: (
    tenant_id: string,
    connector_id: string,
    person_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Person>>;
  /**
   * Reconcile person
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param person_id - Unique identifier for the person
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Person>
   */
  reconcilePerson: (
    tenant_id: string,
    connector_id: string,
    person_id: string,
    body: Person,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Person>>;
  /**
   * Reconcile persons in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcilePersonsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchPersonsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete policy by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deletePolicyByRef: (
    tenant_id: string,
    connector_id: string,
    query: { policy_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List policies from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePolicy>
   */
  listPolicies: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePolicy>>;
  /**
   * Reconcile policy without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcilePolicyWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Policy,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find policy by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param policy_id - Unique identifier for the policy
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Policy>
   */
  findPolicy: (
    tenant_id: string,
    connector_id: string,
    policy_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Policy>>;
  /**
   * Reconcile policy
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param policy_id - Unique identifier for the policy
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Policy>
   */
  reconcilePolicy: (
    tenant_id: string,
    connector_id: string,
    policy_id: string,
    body: Policy,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Policy>>;
  /**
   * Reconcile policies in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcilePoliciesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchPoliciesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete policy extension by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deletePolicyExtensionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { policy_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List policy extensions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePolicyExtension>
   */
  listPolicyExtensions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePolicyExtension>>;
  /**
   * Reconcile policy extension without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcilePolicyExtensionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: PolicyExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find policy extension by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param policy_extension_id - Unique identifier for the policy extension
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PolicyExtension>
   */
  findPolicyExtension: (
    tenant_id: string,
    connector_id: string,
    policy_extension_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PolicyExtension>>;
  /**
   * Reconcile policy extension
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param policy_extension_id - Unique identifier for the policy extension
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PolicyExtension>
   */
  reconcilePolicyExtension: (
    tenant_id: string,
    connector_id: string,
    policy_extension_id: string,
    body: PolicyExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PolicyExtension>>;
  /**
   * Reconcile policy extensions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcilePolicyExtensionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchPolicyExtensionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Get connector catalog projector status
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param space - Catalog space name
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<CatalogProjectorStatusResult>
   */
  getConnectorCatalogProjectorStatus: (
    tenant_id: string,
    connector_id: string,
    space: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<CatalogProjectorStatusResult>>;
  /**
   * Resolve RBAC policy documents for role refs
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<rbacResolveResponse>
   */
  resolveRBACPolicies: (
    tenant_id: string,
    connector_id: string,
    query: { role_ref: Array<string> },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<rbacResolveResponse>>;
  /**
   * Delete resource container extension by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteResourceContainerExtensionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { container_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List resource container extensions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageResourceContainerExtension>
   */
  listResourceContainerExtensions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageResourceContainerExtension>>;
  /**
   * Reconcile resource container extension without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileResourceContainerExtensionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ResourceContainerExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find resource container extension by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_container_extension_id - Unique identifier for the resource container extension
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceContainerExtension>
   */
  findResourceContainerExtension: (
    tenant_id: string,
    connector_id: string,
    resource_container_extension_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceContainerExtension>>;
  /**
   * Reconcile resource container extension
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_container_extension_id - Unique identifier for the resource container extension
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceContainerExtension>
   */
  reconcileResourceContainerExtension: (
    tenant_id: string,
    connector_id: string,
    resource_container_extension_id: string,
    body: ResourceContainerExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceContainerExtension>>;
  /**
   * Reconcile resource container extensions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileResourceContainerExtensionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchResourceContainerExtensionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete container nesting edge by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteResourceContainerResourceContainerByRef: (
    tenant_id: string,
    connector_id: string,
    query: { parent_container_ref: string; child_container_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List container nesting edges from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageResourceContainerResourceContainer>
   */
  listResourceContainerResourceContainers: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageResourceContainerResourceContainer>>;
  /**
   * Reconcile container nesting edge without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileResourceContainerResourceContainerWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ResourceContainerResourceContainer,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find container nesting edge by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_container_resource_container_id - Unique identifier for the container nesting edge
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceContainerResourceContainer>
   */
  findResourceContainerResourceContainer: (
    tenant_id: string,
    connector_id: string,
    resource_container_resource_container_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceContainerResourceContainer>>;
  /**
   * Reconcile container nesting edge
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_container_resource_container_id - Unique identifier for the container nesting edge
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceContainerResourceContainer>
   */
  reconcileResourceContainerResourceContainer: (
    tenant_id: string,
    connector_id: string,
    resource_container_resource_container_id: string,
    body: ResourceContainerResourceContainer,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceContainerResourceContainer>>;
  /**
   * Reconcile container nesting edges in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileResourceContainerResourceContainersBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchResourceContainerResourceContainersRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete container–resource edge by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteResourceContainerResourceByRef: (
    tenant_id: string,
    connector_id: string,
    query: { container_ref: string; resource_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List container–resource edges from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageResourceContainerResource>
   */
  listResourceContainerResources: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageResourceContainerResource>>;
  /**
   * Reconcile container–resource edge without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileResourceContainerResourceWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ResourceContainerResource,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find container–resource edge by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_container_resource_id - Unique identifier for the container–resource edge
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceContainerResource>
   */
  findResourceContainerResource: (
    tenant_id: string,
    connector_id: string,
    resource_container_resource_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceContainerResource>>;
  /**
   * Reconcile container–resource edge
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_container_resource_id - Unique identifier for the container–resource edge
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceContainerResource>
   */
  reconcileResourceContainerResource: (
    tenant_id: string,
    connector_id: string,
    resource_container_resource_id: string,
    body: ResourceContainerResource,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceContainerResource>>;
  /**
   * Reconcile container–resource edges in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileResourceContainerResourcesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchResourceContainerResourcesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete resource container by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteResourceContainerByRef: (
    tenant_id: string,
    connector_id: string,
    query: { container_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List resource containers from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageResourceContainer>
   */
  listResourceContainers: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageResourceContainer>>;
  /**
   * Reconcile resource container without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileResourceContainerWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ResourceContainer,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find resource container by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_container_id - Unique identifier for the resource container
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceContainer>
   */
  findResourceContainer: (
    tenant_id: string,
    connector_id: string,
    resource_container_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceContainer>>;
  /**
   * Reconcile resource container
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_container_id - Unique identifier for the resource container
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceContainer>
   */
  reconcileResourceContainer: (
    tenant_id: string,
    connector_id: string,
    resource_container_id: string,
    body: ResourceContainer,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceContainer>>;
  /**
   * Reconcile resource containers in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileResourceContainersBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchResourceContainersRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete resource extension by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteResourceExtensionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { resource_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List resource extensions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageResourceExtension>
   */
  listResourceExtensions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageResourceExtension>>;
  /**
   * Reconcile resource extension without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileResourceExtensionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ResourceExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find resource extension by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_extension_id - Unique identifier for the resource extension
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceExtension>
   */
  findResourceExtension: (
    tenant_id: string,
    connector_id: string,
    resource_extension_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceExtension>>;
  /**
   * Reconcile resource extension
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_extension_id - Unique identifier for the resource extension
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceExtension>
   */
  reconcileResourceExtension: (
    tenant_id: string,
    connector_id: string,
    resource_extension_id: string,
    body: ResourceExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceExtension>>;
  /**
   * Reconcile resource extensions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileResourceExtensionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchResourceExtensionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete resource permission by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteResourcePermissionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { permission_ref: string; resource_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List resource permissions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageResourcePermission>
   */
  listResourcePermissions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageResourcePermission>>;
  /**
   * Reconcile resource permission without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileResourcePermissionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ResourcePermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find resource permission by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_permission_id - Unique identifier for the resource permission
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourcePermission>
   */
  findResourcePermission: (
    tenant_id: string,
    connector_id: string,
    resource_permission_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourcePermission>>;
  /**
   * Reconcile resource permission
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_permission_id - Unique identifier for the resource permission
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourcePermission>
   */
  reconcileResourcePermission: (
    tenant_id: string,
    connector_id: string,
    resource_permission_id: string,
    body: ResourcePermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourcePermission>>;
  /**
   * Reconcile resource permissions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileResourcePermissionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchResourcePermissionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete resource by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteResourceByRef: (
    tenant_id: string,
    connector_id: string,
    query: { resource_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List resources from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageResource>
   */
  listResources: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageResource>>;
  /**
   * Reconcile resource without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileResourceWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Resource,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find resource by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_id - Unique identifier for the resource
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Resource>
   */
  findResource: (
    tenant_id: string,
    connector_id: string,
    resource_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Resource>>;
  /**
   * Reconcile resource
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param resource_id - Unique identifier for the resource
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Resource>
   */
  reconcileResource: (
    tenant_id: string,
    connector_id: string,
    resource_id: string,
    body: Resource,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Resource>>;
  /**
   * Reconcile resources in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileResourcesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchResourcesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete risk factor by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteRiskFactorByRef: (
    tenant_id: string,
    connector_id: string,
    query: { risk_factor_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List risk factors from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageRiskFactor>
   */
  listRiskFactors: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageRiskFactor>>;
  /**
   * Reconcile risk factor without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileRiskFactorWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: RiskFactor,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find risk factor by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param risk_factor_id - Unique identifier for the risk factor
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RiskFactor>
   */
  findRiskFactor: (
    tenant_id: string,
    connector_id: string,
    risk_factor_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RiskFactor>>;
  /**
   * Reconcile risk factor
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param risk_factor_id - Unique identifier for the risk factor
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RiskFactor>
   */
  reconcileRiskFactor: (
    tenant_id: string,
    connector_id: string,
    risk_factor_id: string,
    body: RiskFactor,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RiskFactor>>;
  /**
   * Reconcile risk factors in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileRiskFactorsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchRiskFactorsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete role assignment type by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteRoleAssignmentTypeByRef: (
    tenant_id: string,
    connector_id: string,
    query: {
      principal_ref: string;
      role_ref: string;
      assignment_type: string;
      scope_ref?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List role assignment types from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageRoleAssignmentType>
   */
  listRoleAssignmentTypes: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageRoleAssignmentType>>;
  /**
   * Reconcile role assignment type without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileRoleAssignmentTypeWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: RoleAssignmentType,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find role assignment type by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param role_assignment_type_id - Unique identifier for the role assignment type
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RoleAssignmentType>
   */
  findRoleAssignmentType: (
    tenant_id: string,
    connector_id: string,
    role_assignment_type_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RoleAssignmentType>>;
  /**
   * Reconcile role assignment type
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param role_assignment_type_id - Unique identifier for the role assignment type
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RoleAssignmentType>
   */
  reconcileRoleAssignmentType: (
    tenant_id: string,
    connector_id: string,
    role_assignment_type_id: string,
    body: RoleAssignmentType,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RoleAssignmentType>>;
  /**
   * Reconcile role assignment types in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileRoleAssignmentTypesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchRoleAssignmentTypesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete role extension by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteRoleExtensionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { role_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List role extensions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageRoleExtension>
   */
  listRoleExtensions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageRoleExtension>>;
  /**
   * Reconcile role extension without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileRoleExtensionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: RoleExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find role extension by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param role_extension_id - Unique identifier for the role extension
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RoleExtension>
   */
  findRoleExtension: (
    tenant_id: string,
    connector_id: string,
    role_extension_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RoleExtension>>;
  /**
   * Reconcile role extension
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param role_extension_id - Unique identifier for the role extension
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RoleExtension>
   */
  reconcileRoleExtension: (
    tenant_id: string,
    connector_id: string,
    role_extension_id: string,
    body: RoleExtension,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RoleExtension>>;
  /**
   * Reconcile role extensions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileRoleExtensionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchRoleExtensionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete role permission by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteRolePermissionByRef: (
    tenant_id: string,
    connector_id: string,
    query: { role_ref: string; permission_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List role permissions from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageRolePermission>
   */
  listRolePermissions: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageRolePermission>>;
  /**
   * Reconcile role permission without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileRolePermissionWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: RolePermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find role permission by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param role_permission_id - Unique identifier for the role permission
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RolePermission>
   */
  findRolePermission: (
    tenant_id: string,
    connector_id: string,
    role_permission_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RolePermission>>;
  /**
   * Reconcile role permission
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param role_permission_id - Unique identifier for the role permission
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RolePermission>
   */
  reconcileRolePermission: (
    tenant_id: string,
    connector_id: string,
    role_permission_id: string,
    body: RolePermission,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RolePermission>>;
  /**
   * Reconcile role permissions in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileRolePermissionsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchRolePermissionsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete role by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteRoleByRef: (
    tenant_id: string,
    connector_id: string,
    query: { role_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List roles from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageRole>
   */
  listRoles: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageRole>>;
  /**
   * Reconcile role without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileRoleWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Role,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find role by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param role_id - Unique identifier for the role
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Role>
   */
  findRole: (
    tenant_id: string,
    connector_id: string,
    role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Role>>;
  /**
   * Reconcile role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param role_id - Unique identifier for the role
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Role>
   */
  reconcileRole: (
    tenant_id: string,
    connector_id: string,
    role_id: string,
    body: Role,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Role>>;
  /**
   * Reconcile roles in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileRolesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchRolesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Get connector RSOS range entries
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param space - Catalog space name
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<RSOSEntry>>
   */
  getConnectorRSOSRangeEntries: (
    tenant_id: string,
    connector_id: string,
    space: string,
    query?: { lo?: string; hi?: string; version?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<RSOSEntry>>>;
  /**
   * Get connector RSOS range fingerprint
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param space - Catalog space name
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RSOSRange>
   */
  getConnectorRSOSRangeFingerprint: (
    tenant_id: string,
    connector_id: string,
    space: string,
    query?: { lo?: string; hi?: string; version?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RSOSRange>>;
  /**
   * Get connector RSOS range split
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param space - Catalog space name
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<RSOSChildRange>>
   */
  getConnectorRSOSRangeSplit: (
    tenant_id: string,
    connector_id: string,
    space: string,
    query?: { lo?: string; hi?: string; k?: number; version?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<RSOSChildRange>>>;
  /**
   * Delete scoped assignment by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteScopedAssignmentByRef: (
    tenant_id: string,
    connector_id: string,
    query: { principal_ref: string; role_ref: string; scope_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List scoped assignments from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageScopedAssignment>
   */
  listScopedAssignments: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageScopedAssignment>>;
  /**
   * Reconcile scoped assignment without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileScopedAssignmentWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ScopedAssignment,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find scoped assignment by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param scoped_assignment_id - Unique identifier for the scoped assignment
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ScopedAssignment>
   */
  findScopedAssignment: (
    tenant_id: string,
    connector_id: string,
    scoped_assignment_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ScopedAssignment>>;
  /**
   * Reconcile scoped assignment
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param scoped_assignment_id - Unique identifier for the scoped assignment
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ScopedAssignment>
   */
  reconcileScopedAssignment: (
    tenant_id: string,
    connector_id: string,
    scoped_assignment_id: string,
    body: ScopedAssignment,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ScopedAssignment>>;
  /**
   * Reconcile scoped assignments in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileScopedAssignmentsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchScopedAssignmentsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete scoped denial by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteScopedDenialByRef: (
    tenant_id: string,
    connector_id: string,
    query: {
      effect: string;
      principal_ref: string;
      permission_ref: string;
      scope_ref: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List scoped denials from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageScopedDenial>
   */
  listScopedDenials: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageScopedDenial>>;
  /**
   * Reconcile scoped denial without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileScopedDenialWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ScopedDenial,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find scoped denial by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param scoped_denial_id - Unique identifier for the scoped denial
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ScopedDenial>
   */
  findScopedDenial: (
    tenant_id: string,
    connector_id: string,
    scoped_denial_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ScopedDenial>>;
  /**
   * Reconcile scoped denial
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param scoped_denial_id - Unique identifier for the scoped denial
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ScopedDenial>
   */
  reconcileScopedDenial: (
    tenant_id: string,
    connector_id: string,
    scoped_denial_id: string,
    body: ScopedDenial,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ScopedDenial>>;
  /**
   * Reconcile scoped denials in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileScopedDenialsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchScopedDenialsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete secret resource by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteSecretResourceByRef: (
    tenant_id: string,
    connector_id: string,
    query: { secret_ref: string; resource_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List secret resources from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageSecretResource>
   */
  listSecretResources: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageSecretResource>>;
  /**
   * Reconcile secret resource without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileSecretResourceWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: SecretResource,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find secret resource by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param secret_resource_id - Unique identifier for the secret resource
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SecretResource>
   */
  findSecretResource: (
    tenant_id: string,
    connector_id: string,
    secret_resource_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SecretResource>>;
  /**
   * Reconcile secret resource
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param secret_resource_id - Unique identifier for the secret resource
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SecretResource>
   */
  reconcileSecretResource: (
    tenant_id: string,
    connector_id: string,
    secret_resource_id: string,
    body: SecretResource,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SecretResource>>;
  /**
   * Reconcile secret resources in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileSecretResourcesBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchSecretResourcesRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete secret by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteSecretByRef: (
    tenant_id: string,
    connector_id: string,
    query: { secret_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List secrets from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageSecret>
   */
  listSecrets: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageSecret>>;
  /**
   * Reconcile secret without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileSecretWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Secret,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find secret by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param secret_id - Unique identifier for the secret
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Secret>
   */
  findSecret: (
    tenant_id: string,
    connector_id: string,
    secret_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Secret>>;
  /**
   * Reconcile secret
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param secret_id - Unique identifier for the secret
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Secret>
   */
  reconcileSecret: (
    tenant_id: string,
    connector_id: string,
    secret_id: string,
    body: Secret,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Secret>>;
  /**
   * Reconcile secrets in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileSecretsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchSecretsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete security configuration by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteSecurityConfigurationByRef: (
    tenant_id: string,
    connector_id: string,
    query: { security_configuration_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List security configurations from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageSecurityConfiguration>
   */
  listSecurityConfigurations: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageSecurityConfiguration>>;
  /**
   * Reconcile security configuration without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileSecurityConfigurationWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: SecurityConfiguration,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find security configuration by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param security_configuration_id - Unique identifier for the security configuration
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SecurityConfiguration>
   */
  findSecurityConfiguration: (
    tenant_id: string,
    connector_id: string,
    security_configuration_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SecurityConfiguration>>;
  /**
   * Reconcile security configuration
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param security_configuration_id - Unique identifier for the security configuration
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SecurityConfiguration>
   */
  reconcileSecurityConfiguration: (
    tenant_id: string,
    connector_id: string,
    security_configuration_id: string,
    body: SecurityConfiguration,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SecurityConfiguration>>;
  /**
   * Reconcile security configurations in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileSecurityConfigurationsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchSecurityConfigurationsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete service account impersonation by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteServiceAccountImpersonationByRef: (
    tenant_id: string,
    connector_id: string,
    query: {
      principal_ref: string;
      role_ref: string;
      service_account_ref: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List service account impersonations from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageServiceAccountImpersonation>
   */
  listServiceAccountImpersonations: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageServiceAccountImpersonation>>;
  /**
   * Reconcile service account impersonation without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileServiceAccountImpersonationWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: ServiceAccountImpersonation,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find service account impersonation by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param service_account_impersonation_id - Unique identifier for the service account impersonation
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ServiceAccountImpersonation>
   */
  findServiceAccountImpersonation: (
    tenant_id: string,
    connector_id: string,
    service_account_impersonation_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ServiceAccountImpersonation>>;
  /**
   * Reconcile service account impersonation
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param service_account_impersonation_id - Unique identifier for the service account impersonation
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ServiceAccountImpersonation>
   */
  reconcileServiceAccountImpersonation: (
    tenant_id: string,
    connector_id: string,
    service_account_impersonation_id: string,
    body: ServiceAccountImpersonation,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ServiceAccountImpersonation>>;
  /**
   * Reconcile service account impersonations in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileServiceAccountImpersonationsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchServiceAccountImpersonationsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete special principal by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteSpecialPrincipalByRef: (
    tenant_id: string,
    connector_id: string,
    query: { principal_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List special principals from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageSpecialPrincipal>
   */
  listSpecialPrincipals: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageSpecialPrincipal>>;
  /**
   * Reconcile special principal without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileSpecialPrincipalWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: SpecialPrincipal,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find special principal by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param special_principal_id - Unique identifier for the special principal
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SpecialPrincipal>
   */
  findSpecialPrincipal: (
    tenant_id: string,
    connector_id: string,
    special_principal_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SpecialPrincipal>>;
  /**
   * Reconcile special principal
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param special_principal_id - Unique identifier for the special principal
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SpecialPrincipal>
   */
  reconcileSpecialPrincipal: (
    tenant_id: string,
    connector_id: string,
    special_principal_id: string,
    body: SpecialPrincipal,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SpecialPrincipal>>;
  /**
   * Reconcile special principals in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileSpecialPrincipalsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchSpecialPrincipalsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete ticket comment by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteTicketCommentByRef: (
    tenant_id: string,
    connector_id: string,
    query: { ticket_ref: string; comment_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List ticket comments from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageTicketComment>
   */
  listTicketComments: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageTicketComment>>;
  /**
   * Reconcile ticket comment without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileTicketCommentWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: TicketComment,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find ticket comment by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param ticket_comment_id - Unique identifier for the ticket comment
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<TicketComment>
   */
  findTicketComment: (
    tenant_id: string,
    connector_id: string,
    ticket_comment_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<TicketComment>>;
  /**
   * Reconcile ticket comment
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param ticket_comment_id - Unique identifier for the ticket comment
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<TicketComment>
   */
  reconcileTicketComment: (
    tenant_id: string,
    connector_id: string,
    ticket_comment_id: string,
    body: TicketComment,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<TicketComment>>;
  /**
   * Reconcile ticket comments in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileTicketCommentsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchTicketCommentsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Delete ticket by reference
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteTicketByRef: (
    tenant_id: string,
    connector_id: string,
    query: { ticket_ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List tickets from connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageTicket>
   */
  listTickets: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageTicket>>;
  /**
   * Reconcile ticket without knowing internal ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityMetadata>
   */
  reconcileTicketWithoutID: (
    tenant_id: string,
    connector_id: string,
    body: Ticket,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityMetadata>>;
  /**
   * Find ticket by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param ticket_id - Unique identifier for the ticket
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Ticket>
   */
  findTicket: (
    tenant_id: string,
    connector_id: string,
    ticket_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Ticket>>;
  /**
   * Reconcile ticket
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param ticket_id - Unique identifier for the ticket
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Ticket>
   */
  reconcileTicket: (
    tenant_id: string,
    connector_id: string,
    ticket_id: string,
    body: Ticket,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Ticket>>;
  /**
   * Reconcile tickets in batch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchReconcileResponse>
   */
  reconcileTicketsBatch: (
    tenant_id: string,
    connector_id: string,
    body: batchTicketsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchReconcileResponse>>;
  /**
   * Get the entitlements of an agent, per runtime account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentAccess>
   */
  getEntitlementAgentAccess: (
    tenant_id: string,
    query?: { ref?: string; limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentAccess>>;
  /**
   * Get the resources an agent reaches through its capabilities
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentCapabilityReach>
   */
  getEntitlementAgentCapabilityReach: (
    tenant_id: string,
    query: {
      ref: string;
      connector_id?: string;
      limit?: number;
      offset?: number;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentCapabilityReach>>;
  /**
   * Get the resources an agent reaches through its deployments
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDeploymentReach>
   */
  getEntitlementAgentDeploymentReach: (
    tenant_id: string,
    query: {
      ref: string;
      connector_id?: string;
      limit?: number;
      offset?: number;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDeploymentReach>>;
  /**
   * Get what an agent reaches through credentials its deployments are handed
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentIndirectAccess>
   */
  getEntitlementAgentIndirectAccess: (
    tenant_id: string,
    query: { ref: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentIndirectAccess>>;
  /**
   * List the tenant's entitlement peer-group clusters
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<clustersResponse>
   */
  listEntitlementClusters: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<clustersResponse>>;
  /**
   * Get one entitlement peer-group cluster with its members
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param cluster_id - Stable cluster identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<clusterDetail>
   */
  getEntitlementCluster: (
    tenant_id: string,
    cluster_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<clusterDetail>>;
  /**
   * Get what a principal reaches INDIRECTLY via vaulted credentials
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<IndirectAccess>
   */
  getEntitlementIndirectAccess: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<IndirectAccess>>;
  /**
   * Get the bounded graph neighborhood around any entity
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Neighborhood>
   */
  getEntitlementNeighborhood: (
    tenant_id: string,
    query: {
      ref: string;
      label?: string;
      direction?: string;
      hops?: number;
      label_limit?: number;
      fanout?: number;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Neighborhood>>;
  /**
   * Explain why a principal has (or is denied) access to a resource
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccessPaths>
   */
  getEntitlementAccessPaths: (
    tenant_id: string,
    query?: { from?: string; to?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccessPaths>>;
  /**
   * Get everything an account or group effectively reaches
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PrincipalAccess>
   */
  getEntitlementPrincipalAccess: (
    tenant_id: string,
    query?: { ref?: string; limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PrincipalAccess>>;
  /**
   * Get an account's privilege and anomaly analysis
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccountAnalysis>
   */
  getAccountAnalysis: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccountAnalysis>>;
  /**
   * Record analyst feedback on a detected account
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<feedbackResponse>
   */
  postPrivilegeFeedback: (
    tenant_id: string,
    body: feedbackRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<feedbackResponse>>;
  /**
   * Get the ranked detection review queue
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<rankingResponse>
   */
  getPrivilegeRanking: (
    tenant_id: string,
    query?: { kind?: string; verdict?: string; limit?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<rankingResponse>>;
  /**
   * Get every principal with effective access to a resource or resource container
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ResourceAccess>
   */
  getEntitlementResourceAccess: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ResourceAccess>>;
  /**
   * Get where a role is actually granting access, and to whom
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RoleAccess>
   */
  getEntitlementRoleAccess: (
    tenant_id: string,
    query?: { ref?: string; limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RoleAccess>>;
  /**
   * Get what a role WOULD grant, regardless of holders
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RoleReach>
   */
  getEntitlementRoleReach: (
    tenant_id: string,
    query: {
      ref: string;
      connector_id?: string;
      limit?: number;
      offset?: number;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RoleReach>>;
  /**
   * Submit catalog events
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param log_name - Log name for the event
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<BatchPublishEventResponse>
   */
  postCatalogEvent: (
    tenant_id: string,
    log_name: string,
    body: catalogEventBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<BatchPublishEventResponse>>;
  /**
   * List catalog event kinds
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<CatalogEventKindsResponse>
   */
  listCatalogEventKinds: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<CatalogEventKindsResponse>>;
  /**
   * Search catalog activity events
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EventSearchResult>
   */
  searchCatalogEvents: (
    tenant_id: string,
    query?: {
      q?: string;
      actor_ref?: string;
      actor_email?: string;
      actor_upn?: string;
      person_ref?: string;
      target_ref?: string;
      campaign_ref?: string;
      review_ref?: string;
      ip_address?: string;
      session_id?: string;
      device_ref?: string;
      risk_level?: string;
      privileged?: boolean;
      kinds?: string;
      spaces?: string;
      connector_ids?: string;
      outcome_result?: string;
      target_types?: Array<string>;
      target_rel?: Array<string>;
      event_ids?: string;
      from?: string;
      to?: string;
      since?: string;
      limit?: number;
      cursor?: string;
      include_facets?: boolean;
      count_only?: boolean;
      hydrate?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EventSearchResult>>;
  /**
   * Aggregate catalog activity events
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EventStatsResult>
   */
  getCatalogEventStats: (
    tenant_id: string,
    query?: {
      from?: string;
      to?: string;
      bucket?: string;
      group_by?: string;
      q?: string;
      actor_ref?: string;
      actor_email?: string;
      actor_upn?: string;
      person_ref?: string;
      target_ref?: string;
      ip_address?: string;
      session_id?: string;
      device_ref?: string;
      risk_level?: string;
      event_ids?: string;
      kinds?: string;
      spaces?: string;
      connector_ids?: string;
      outcome_result?: string;
      target_types?: Array<string>;
      target_rel?: Array<string>;
      privileged?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EventStatsResult>>;
  /**
   * Unified search across catalog kinds
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SearchResult>
   */
  exploreSearchCatalog: (
    tenant_id: string,
    query: {
      q: string;
      limit?: number;
      cursor?: string;
      counts?: boolean;
      counts_only?: boolean;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SearchResult>>;
  /**
   * List groups across all connectors (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagegroupRow>
   */
  listGroupsTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      search?: string;
      filter?: Array<string>;
      ref?: string;
      connector_id?: string;
      include_system?: boolean;
      facets?: Array<string>;
      hydrate?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagegroupRow>>;
  /**
   * List the applications a group grants access to
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param group_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplication>
   */
  listApplicationsForGroup: (
    tenant_id: string,
    group_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplication>>;
  /**
   * List the licenses granted through a group
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param group_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageLicense>
   */
  listLicensesForGroup: (
    tenant_id: string,
    group_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageLicense>>;
  /**
   * List a group's members as full account rows
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param group_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccount>
   */
  listMembersForGroup: (
    tenant_id: string,
    group_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccount>>;
  /**
   * List the permissions granted to a group
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param group_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePermission>
   */
  listPermissionsForGroup: (
    tenant_id: string,
    group_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePermission>>;
  /**
   * Get a group with its attributes and every relationship as bounded blocks
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param group_id - Distinct identifier (UUID) of the group
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<GroupProfile>
   */
  getGroupProfile: (
    tenant_id: string,
    group_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<GroupProfile>>;
  /**
   * List the roles granted to a group
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param group_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageRole>
   */
  listRolesForGroup: (
    tenant_id: string,
    group_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageRole>>;
  /**
   * Aggregate groups in batch (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsBatchResult>
   */
  computeGroupsEntityStats: (
    tenant_id: string,
    body: EntityStatsBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsBatchResult>>;
  /**
   * Aggregate groups (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsResult>
   */
  getGroupsEntityStats: (
    tenant_id: string,
    query?: {
      agg?: string;
      value?: string;
      group_by?: string;
      buckets?: string;
      filter?: Array<string>;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsResult>>;
  /**
   * List catalog activity logs
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ActivityLogPage>
   */
  listActivityLogs: (
    tenant_id: string,
    query?: {
      connector_ids?: string;
      space?: string;
      from?: string;
      to?: string;
      kinds?: string;
      target_ref?: string;
      distinct_id?: string;
      limit?: number;
      next?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ActivityLogPage>>;
  /**
   * Delete an owner-creation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OwnerRulesResponse>
   */
  deleteOwnerRule: (
    tenant_id: string,
    query?: { rule_id?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OwnerRulesResponse>>;
  /**
   * List the tenant's owner-creation rules
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OwnerRulesResponse>
   */
  listOwnerRules: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OwnerRulesResponse>>;
  /**
   * Store one owner-creation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OwnerRulesResponse>
   */
  putOwnerRule: (
    tenant_id: string,
    body: OwnerRuleRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OwnerRulesResponse>>;
  /**
   * List the fields an owner-creation rule can read and fill
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OwnerRuleFieldsResponse>
   */
  listOwnerRuleFields: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OwnerRuleFieldsResponse>>;
  /**
   * Evaluate an owner-creation rule without applying it
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OwnerPreviewResponse>
   */
  previewOwnerRule: (
    tenant_id: string,
    body: OwnerPreviewRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OwnerPreviewResponse>>;
  /**
   * Check whether an owner-creation rule could be stored
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OwnerRuleValidateResponse>
   */
  validateOwnerRule: (
    tenant_id: string,
    body: OwnerRuleRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OwnerRuleValidateResponse>>;
  /**
   * List persons across all connectors (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagepersonRow>
   */
  listPersonsTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      search?: string;
      filter?: Array<string>;
      ref?: string;
      connector_id?: string;
      include_system?: boolean;
      facets?: Array<string>;
      hydrate?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagepersonRow>>;
  /**
   * List the accounts linked to a person
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param person_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccount>
   */
  listAccountsForPerson: (
    tenant_id: string,
    person_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccount>>;
  /**
   * List the accounts a person manages (direct reports)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param person_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccount>
   */
  listDirectReportsForPerson: (
    tenant_id: string,
    person_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccount>>;
  /**
   * Get an owner (person) with the accounts they manage, their manager, direct reports, and roles
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param person_id - Distinct identifier (UUID) of the person (owner)
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PersonOwnership>
   */
  getPersonOwnership: (
    tenant_id: string,
    person_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PersonOwnership>>;
  /**
   * Get a person with their attributes and accounts as bounded blocks
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param person_id - Distinct identifier (UUID) of the person
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PersonProfile>
   */
  getPersonProfile: (
    tenant_id: string,
    person_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PersonProfile>>;
  /**
   * List the roles a person holds through their accounts
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param person_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageRole>
   */
  listRolesForPerson: (
    tenant_id: string,
    person_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageRole>>;
  /**
   * Aggregate persons in batch (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsBatchResult>
   */
  computePersonsEntityStats: (
    tenant_id: string,
    body: EntityStatsBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsBatchResult>>;
  /**
   * Aggregate persons (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsResult>
   */
  getPersonsEntityStats: (
    tenant_id: string,
    query?: {
      agg?: string;
      value?: string;
      group_by?: string;
      buckets?: string;
      filter?: Array<string>;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsResult>>;
  /**
   * Execute named query
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param name - Query name
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<QueryResult>
   */
  executeQuery: (
    tenant_id: string,
    name: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<QueryResult>>;
  /**
   * Execute named query with complex parameters
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param name - Query name
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<QueryResult>
   */
  executeQueryWithBody: (
    tenant_id: string,
    name: string,
    body: QueryRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<QueryResult>>;
  /**
   * Assert a relationship manually
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<relationshipDecisionResponse>
   */
  assertRelationship: (
    tenant_id: string,
    body: relationshipDecisionRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<relationshipDecisionResponse>>;
  /**
   * Clear a previous manual decision
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<relationshipDecisionResponse>
   */
  clearRelationshipDecision: (
    tenant_id: string,
    body: relationshipDecisionRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<relationshipDecisionResponse>>;
  /**
   * Get the accounts this person holds
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<HeldAccountsResponse>
   */
  getHeldAccounts: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<HeldAccountsResponse>>;
  /**
   * Get what this account may act as, and what may act as it
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ImpersonationResponse>
   */
  getAccountImpersonation: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ImpersonationResponse>>;
  /**
   * Get the accounts correlated to this one
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<LinkedAccountsResponse>
   */
  getLinkedAccounts: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<LinkedAccountsResponse>>;
  /**
   * Get who manages a person and who reports to them
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ManagementResponse>
   */
  getPersonManagement: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ManagementResponse>>;
  /**
   * Get the owner of an account and their management chain
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OwnerManagerChainResponse>
   */
  getOwnerManagerChain: (
    tenant_id: string,
    query?: { ref?: string; depth?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OwnerManagerChainResponse>>;
  /**
   * Get the people this account belongs to
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<OwnersResponse>
   */
  getAccountOwners: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<OwnersResponse>>;
  /**
   * Discard a tenant's change to one rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RelationshipRulesResponse>
   */
  deleteRelationshipRule: (
    tenant_id: string,
    query?: { rule_id?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RelationshipRulesResponse>>;
  /**
   * List the tenant's effective correlation rules
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RelationshipRulesResponse>
   */
  listRelationshipRules: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RelationshipRulesResponse>>;
  /**
   * Enable, disable, replace or add a correlation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RelationshipRulesResponse>
   */
  putRelationshipRule: (
    tenant_id: string,
    body: RelationshipRuleOverlayRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RelationshipRulesResponse>>;
  /**
   * Evaluate rules against a principal without changing anything
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RelationshipPreviewResponse>
   */
  previewRelationshipRules: (
    tenant_id: string,
    body: RelationshipPreviewRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RelationshipPreviewResponse>>;
  /**
   * Trace what one selector reads from one record
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RelationshipSelectorPreviewResponse>
   */
  previewRelationshipSelector: (
    tenant_id: string,
    body: RelationshipSelectorPreviewRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RelationshipSelectorPreviewResponse>>;
  /**
   * Check whether a rule could be stored
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RelationshipRuleValidateResponse>
   */
  validateRelationshipRule: (
    tenant_id: string,
    body: RelationshipRuleValidateRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RelationshipRuleValidateResponse>>;
  /**
   * Get what this account authenticates as, and the credentials that authenticate as it
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<VaultedCredentialsResponse>
   */
  getVaultedCredentials: (
    tenant_id: string,
    query?: { ref?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<VaultedCredentialsResponse>>;
  /**
   * Veto a relationship manually
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<relationshipDecisionResponse>
   */
  vetoRelationship: (
    tenant_id: string,
    body: relationshipDecisionRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<relationshipDecisionResponse>>;
  /**
   * List resources across all connectors (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageresourceRow>
   */
  listResourcesTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      search?: string;
      filter?: Array<string>;
      ref?: string;
      connector_id?: string;
      include_system?: boolean;
      facets?: Array<string>;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageresourceRow>>;
  /**
   * List roles across all connectors (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageroleRow>
   */
  listRolesTenantWide: (
    tenant_id: string,
    query?: {
      limit?: number;
      offset?: number;
      sort?: string;
      sort_dir?: string;
      search?: string;
      filter?: Array<string>;
      ref?: string;
      connector_id?: string;
      include_system?: boolean;
      facets?: Array<string>;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageroleRow>>;
  /**
   * List the accounts assigned a role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageAccount>
   */
  listAccountsForRole: (
    tenant_id: string,
    role_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageAccount>>;
  /**
   * List the applications that grant a role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageApplication>
   */
  listApplicationsForRole: (
    tenant_id: string,
    role_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageApplication>>;
  /**
   * List the groups that grant a role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageGroup>
   */
  listGroupsForRole: (
    tenant_id: string,
    role_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageGroup>>;
  /**
   * Get role-to-role overlap analysis
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Role identifier (UUID)
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<roleOverlapAnalysisResponse>
   */
  getRoleOverlapAnalysis: (
    tenant_id: string,
    role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<roleOverlapAnalysisResponse>>;
  /**
   * List the permissions a role carries
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Distinct identifier (UUID) of the parent entity
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPagePermission>
   */
  listPermissionsForRole: (
    tenant_id: string,
    role_id: string,
    query?: { limit?: number; offset?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPagePermission>>;
  /**
   * Get a role with its permissions, accounts, groups and applications as bounded blocks
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Distinct identifier (UUID) of the role
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RoleProfile>
   */
  getRoleProfile: (
    tenant_id: string,
    role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RoleProfile>>;
  /**
   * Aggregate roles in batch (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsBatchResult>
   */
  computeRolesEntityStats: (
    tenant_id: string,
    body: EntityStatsBatchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsBatchResult>>;
  /**
   * Aggregate roles (tenant-wide)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityStatsResult>
   */
  getRolesEntityStats: (
    tenant_id: string,
    query?: {
      agg?: string;
      value?: string;
      group_by?: string;
      buckets?: string;
      filter?: Array<string>;
      include_system?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityStatsResult>>;
  /**
   * Keyword search
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EntityPageSearchResult>
   */
  searchCatalog: (
    tenant_id: string,
    query?: { filter?: string; limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EntityPageSearchResult>>;
} {
  return {
    computeEntityStats: (
      tenant_id: string,
      space: string,
      body: EntityStatsBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsBatchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'computeEntityStats',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/${encodeURIComponent(String(space))}/stats`,
        body,
        undefined,
        finalOptions
      );
    },
    getEntityStats: (
      tenant_id: string,
      space: string,
      query?: {
        agg?: string;
        value?: string;
        group_by?: string;
        buckets?: string;
        filter?: Array<string>;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntityStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/${encodeURIComponent(String(space))}/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listAccessReviewsTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        ref?: string;
        include_system?: boolean;
        campaign_ref?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccessReview>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccessReviewsTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/access-reviews` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    computeAccessReviewsEntityStats: (
      tenant_id: string,
      body: EntityStatsBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsBatchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'computeAccessReviewsEntityStats',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/access-reviews/stats`,
        body,
        undefined,
        finalOptions
      );
    },
    getAccessReviewsEntityStats: (
      tenant_id: string,
      query?: {
        agg?: string;
        value?: string;
        group_by?: string;
        buckets?: string;
        filter?: Array<string>;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAccessReviewsEntityStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/access-reviews/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    requestAccountHydration: (
      tenant_id: string,
      body: accountHydrationRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<accountHydrationResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'requestAccountHydration',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/account-hydrations`,
        body,
        undefined,
        finalOptions
      );
    },
    getAccountHydration: (
      tenant_id: string,
      hydration_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<accountHydrationResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAccountHydration',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/account-hydrations/${encodeURIComponent(String(hydration_id))}`,
        undefined,
        finalOptions
      );
    },
    listAccountsTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        search?: string;
        filter?: Array<string>;
        ref?: string;
        include_system?: boolean;
        person_ref?: string;
        connector_id?: string;
        facets?: Array<string>;
        hydrate?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageaccountRow>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountsTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listApplicationsForAccount: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplication>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationsForAccount',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/applications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listClassificationsForAccount: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageClassification>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listClassificationsForAccount',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/classifications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listGroupsForAccount: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroup>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupsForAccount',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/groups` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listLicensesForAccount: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageLicense>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listLicensesForAccount',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/licenses` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listAccountManagedAccounts: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountManagedAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/managed-accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getManagerForAccount: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getManagerForAccount',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/manager` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listMultiFactorsForAccount: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageMultiFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listMultiFactorsForAccount',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/multi-factors` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listPermissionsForAccount: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPermissionsForAccount',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listPersonsForAccount: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePerson>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPersonsForAccount',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/persons` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getAccountProfile: (
      tenant_id: string,
      account_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountProfile>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAccountProfile',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/profile`,
        undefined,
        finalOptions
      );
    },
    listRolesForAccount: (
      tenant_id: string,
      account_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRolesForAccount',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/${encodeURIComponent(String(account_id))}/roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    computeAccountsEntityStats: (
      tenant_id: string,
      body: EntityStatsBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsBatchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'computeAccountsEntityStats',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/stats`,
        body,
        undefined,
        finalOptions
      );
    },
    getAccountsEntityStats: (
      tenant_id: string,
      query?: {
        agg?: string;
        value?: string;
        group_by?: string;
        buckets?: string;
        filter?: Array<string>;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAccountsEntityStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/accounts/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listAgentsTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        search?: string;
        filter?: Array<string>;
        ref?: string;
        connector_id?: string;
        include_system?: boolean;
        facets?: Array<string>;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageagentRow>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgentsTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/agents` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getAgentProfile: (
      tenant_id: string,
      agent_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentProfile>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAgentProfile',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/agents/${encodeURIComponent(String(agent_id))}/profile`,
        undefined,
        finalOptions
      );
    },
    computeAgentsEntityStats: (
      tenant_id: string,
      body: EntityStatsBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsBatchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'computeAgentsEntityStats',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/agents/stats`,
        body,
        undefined,
        finalOptions
      );
    },
    getAgentsEntityStats: (
      tenant_id: string,
      query?: {
        agg?: string;
        value?: string;
        group_by?: string;
        buckets?: string;
        filter?: Array<string>;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAgentsEntityStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/agents/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listApplicationsTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        search?: string;
        filter?: Array<string>;
        ref?: string;
        connector_id?: string;
        include_system?: boolean;
        facets?: Array<string>;
        hydrate?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageapplicationRow>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationsTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/applications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listAccountsForApplication: (
      tenant_id: string,
      application_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountsForApplication',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/applications/${encodeURIComponent(String(application_id))}/accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listGroupsForApplication: (
      tenant_id: string,
      application_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroup>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupsForApplication',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/applications/${encodeURIComponent(String(application_id))}/groups` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listRolesForApplication: (
      tenant_id: string,
      application_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRolesForApplication',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/applications/${encodeURIComponent(String(application_id))}/roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getApplicationStatistics: (
      tenant_id: string,
      application_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<applicationStatisticsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getApplicationStatistics',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/applications/${encodeURIComponent(String(application_id))}/statistics`,
        undefined,
        finalOptions
      );
    },
    computeApplicationsEntityStats: (
      tenant_id: string,
      body: EntityStatsBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsBatchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'computeApplicationsEntityStats',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/applications/stats`,
        body,
        undefined,
        finalOptions
      );
    },
    getApplicationsEntityStats: (
      tenant_id: string,
      query?: {
        agg?: string;
        value?: string;
        group_by?: string;
        buckets?: string;
        filter?: Array<string>;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getApplicationsEntityStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/applications/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listCollectedAttributeKeys: (
      tenant_id: string,
      query?: {
        connector_id?: string;
        kinds?: string;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AttributeKeysResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listCollectedAttributeKeys',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/attributes/keys` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listCampaignsTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        ref?: string;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageCampaign>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listCampaignsTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    computeCampaignsEntityStats: (
      tenant_id: string,
      body: EntityStatsBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsBatchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'computeCampaignsEntityStats',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/stats`,
        body,
        undefined,
        finalOptions
      );
    },
    getCampaignsEntityStats: (
      tenant_id: string,
      query?: {
        agg?: string;
        value?: string;
        group_by?: string;
        buckets?: string;
        filter?: Array<string>;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getCampaignsEntityStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listClassificationsTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        ref?: string;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageclassificationWithStats>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listClassificationsTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listClassificationAccounts: (
      tenant_id: string,
      classification_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listClassificationAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/${encodeURIComponent(String(classification_id))}/accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getClassificationStatistics: (
      tenant_id: string,
      classification_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<classificationStatisticsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getClassificationStatistics',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/${encodeURIComponent(String(classification_id))}/statistics`,
        undefined,
        finalOptions
      );
    },
    getClassifiedAccounts: (
      tenant_id: string,
      query?: { ref?: string; limit?: number; cursor?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ClassifiedAccountsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getClassifiedAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getAccountClassifications: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountClassificationsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAccountClassifications',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/for-account` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    deleteClassificationRule: (
      tenant_id: string,
      query?: { rule_id?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ClassificationRulesResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteClassificationRule',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/rules` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listClassificationRules: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ClassificationRulesResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listClassificationRules',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/rules`,
        undefined,
        finalOptions
      );
    },
    putClassificationRule: (
      tenant_id: string,
      body: ClassificationRuleRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ClassificationRulesResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'putClassificationRule',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/rules`,
        body,
        undefined,
        finalOptions
      );
    },
    listClassificationRuleFields: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ClassificationFieldsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listClassificationRuleFields',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/rules/fields`,
        undefined,
        finalOptions
      );
    },
    previewClassificationRule: (
      tenant_id: string,
      body: ClassificationPreviewRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ClassificationPreviewResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'previewClassificationRule',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/rules/preview`,
        body,
        undefined,
        finalOptions
      );
    },
    validateClassificationRule: (
      tenant_id: string,
      body: ClassificationRuleValidateRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ClassificationRuleValidateResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'validateClassificationRule',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/classifications/rules/validate`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccessReviewByRef: (
      tenant_id: string,
      connector_id: string,
      query: { campaign_ref: string; review_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccessReviewByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/access-reviews` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccessReviews: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccessReview>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccessReviews',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/access-reviews` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccessReviewWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccessReview,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccessReviewWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/access-reviews`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccessReview: (
      tenant_id: string,
      connector_id: string,
      access_review_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccessReview>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccessReview',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/access-reviews/${encodeURIComponent(String(access_review_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccessReview: (
      tenant_id: string,
      connector_id: string,
      access_review_id: string,
      body: AccessReview,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccessReview>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccessReview',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/access-reviews/${encodeURIComponent(String(access_review_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccessReviewsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccessReviewsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccessReviewsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/access-reviews/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountAttributeByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; attribute_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountAttributeByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-attributes` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountAttributes: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountAttributes',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-attributes` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountAttributeWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountAttribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAccountAttributeWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-attributes`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountAttribute: (
      tenant_id: string,
      connector_id: string,
      account_attribute_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountAttribute',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-attributes/${encodeURIComponent(String(account_attribute_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountAttribute: (
      tenant_id: string,
      connector_id: string,
      account_attribute_id: string,
      body: AccountAttribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountAttribute',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-attributes/${encodeURIComponent(String(account_attribute_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountAttributesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountAttributesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountAttributesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-attributes/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountClassificationByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; classification_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountClassificationByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-classifications` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountClassifications: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountClassification>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountClassifications',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-classifications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountClassificationWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountClassification,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAccountClassificationWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-classifications`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountClassification: (
      tenant_id: string,
      connector_id: string,
      account_classification_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountClassification>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountClassification',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-classifications/${encodeURIComponent(String(account_classification_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountClassification: (
      tenant_id: string,
      connector_id: string,
      account_classification_id: string,
      body: AccountClassification,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountClassification>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountClassification',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-classifications/${encodeURIComponent(String(account_classification_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountClassificationsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountClassificationsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAccountClassificationsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-classifications/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountExtensionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountExtensionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-extensions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountExtensions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountExtensions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-extensions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountExtensionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAccountExtensionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-extensions`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountExtension: (
      tenant_id: string,
      connector_id: string,
      account_extension_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountExtension',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-extensions/${encodeURIComponent(String(account_extension_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountExtension: (
      tenant_id: string,
      connector_id: string,
      account_extension_id: string,
      body: AccountExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountExtension',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-extensions/${encodeURIComponent(String(account_extension_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountExtensionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountExtensionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountExtensionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-extensions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountLicenseByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; license_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountLicenseByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-licenses` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountLicenses: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountLicense>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountLicenses',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-licenses` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountLicenseWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountLicense,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountLicenseWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-licenses`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountLicense: (
      tenant_id: string,
      connector_id: string,
      account_license_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountLicense>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountLicense',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-licenses/${encodeURIComponent(String(account_license_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountLicense: (
      tenant_id: string,
      connector_id: string,
      account_license_id: string,
      body: AccountLicense,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountLicense>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountLicense',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-licenses/${encodeURIComponent(String(account_license_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountLicensesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountLicensesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountLicensesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-licenses/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountManagerByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; manager_account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountManagerByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-managers` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountManagers: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountManager>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountManagers',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-managers` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountManagerWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountManager,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountManagerWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-managers`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountManager: (
      tenant_id: string,
      connector_id: string,
      account_manager_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountManager>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountManager',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-managers/${encodeURIComponent(String(account_manager_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountManager: (
      tenant_id: string,
      connector_id: string,
      account_manager_id: string,
      body: AccountManager,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountManager>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountManager',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-managers/${encodeURIComponent(String(account_manager_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountManagersBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountManagersRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountManagersBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-managers/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountMultiFactorByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; multi_factor_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountMultiFactorByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-multi-factors` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountMultiFactors: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountMultiFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountMultiFactors',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-multi-factors` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountMultiFactorWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountMultiFactor,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAccountMultiFactorWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-multi-factors`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountMultiFactor: (
      tenant_id: string,
      connector_id: string,
      account_multi_factor_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountMultiFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountMultiFactor',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-multi-factors/${encodeURIComponent(String(account_multi_factor_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountMultiFactor: (
      tenant_id: string,
      connector_id: string,
      account_multi_factor_id: string,
      body: AccountMultiFactor,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountMultiFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountMultiFactor',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-multi-factors/${encodeURIComponent(String(account_multi_factor_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountMultiFactorsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountMultiFactorsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAccountMultiFactorsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-multi-factors/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountPermissionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; permission_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountPermissionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-permissions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountPermissions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountPermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountPermissions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountPermissionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountPermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAccountPermissionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-permissions`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountPermission: (
      tenant_id: string,
      connector_id: string,
      account_permission_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountPermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountPermission',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-permissions/${encodeURIComponent(String(account_permission_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountPermission: (
      tenant_id: string,
      connector_id: string,
      account_permission_id: string,
      body: AccountPermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountPermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountPermission',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-permissions/${encodeURIComponent(String(account_permission_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountPermissionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountPermissionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountPermissionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-permissions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountPolicyByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; policy_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountPolicyByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-policies` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountPolicies: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountPolicy>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountPolicies',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-policies` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountPolicyWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountPolicy,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountPolicyWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-policies`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountPolicy: (
      tenant_id: string,
      connector_id: string,
      account_policy_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountPolicy>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountPolicy',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-policies/${encodeURIComponent(String(account_policy_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountPolicy: (
      tenant_id: string,
      connector_id: string,
      account_policy_id: string,
      body: AccountPolicy,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountPolicy>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountPolicy',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-policies/${encodeURIComponent(String(account_policy_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountPoliciesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountPoliciesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountPoliciesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-policies/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountRiskFactorByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; risk_factor_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountRiskFactorByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-risk-factors` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountRiskFactors: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountRiskFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountRiskFactors',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-risk-factors` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountRiskFactorWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountRiskFactor,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAccountRiskFactorWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-risk-factors`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountRiskFactor: (
      tenant_id: string,
      connector_id: string,
      account_risk_factor_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountRiskFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountRiskFactor',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-risk-factors/${encodeURIComponent(String(account_risk_factor_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountRiskFactor: (
      tenant_id: string,
      connector_id: string,
      account_risk_factor_id: string,
      body: AccountRiskFactor,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountRiskFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountRiskFactor',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-risk-factors/${encodeURIComponent(String(account_risk_factor_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountRiskFactorsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountRiskFactorsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountRiskFactorsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-risk-factors/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountRoleByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; role_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountRoleByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-roles` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountRoles: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountRoles',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountRoleWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountRole,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountRoleWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-roles`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountRole: (
      tenant_id: string,
      connector_id: string,
      account_role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountRole',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-roles/${encodeURIComponent(String(account_role_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountRole: (
      tenant_id: string,
      connector_id: string,
      account_role_id: string,
      body: AccountRole,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountRole',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-roles/${encodeURIComponent(String(account_role_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountRolesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountRolesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountRolesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-roles/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountSecretByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string; secret_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountSecretByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-secrets` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccountSecrets: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccountSecret>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountSecrets',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-secrets` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountSecretWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AccountSecret,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountSecretWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-secrets`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccountSecret: (
      tenant_id: string,
      connector_id: string,
      account_secret_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountSecret>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccountSecret',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-secrets/${encodeURIComponent(String(account_secret_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccountSecret: (
      tenant_id: string,
      connector_id: string,
      account_secret_id: string,
      body: AccountSecret,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountSecret>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountSecret',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-secrets/${encodeURIComponent(String(account_secret_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountSecretsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountSecretsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountSecretsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/account-secrets/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAccountByRef: (
      tenant_id: string,
      connector_id: string,
      query: { account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAccountByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/accounts` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAccounts: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAccountWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Account,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/accounts`,
        body,
        undefined,
        finalOptions
      );
    },
    findAccount: (
      tenant_id: string,
      connector_id: string,
      account_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Account>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAccount',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/accounts/${encodeURIComponent(String(account_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAccount: (
      tenant_id: string,
      connector_id: string,
      account_id: string,
      body: Account,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Account>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccount',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/accounts/${encodeURIComponent(String(account_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAccountsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAccountsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAccountsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/accounts/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAgentAccountByRef: (
      tenant_id: string,
      connector_id: string,
      query: { agent_ref: string; account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAgentAccountByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-accounts` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAgentAccounts: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAgentAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgentAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAgentAccountWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AgentAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentAccountWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-accounts`,
        body,
        undefined,
        finalOptions
      );
    },
    findAgentAccount: (
      tenant_id: string,
      connector_id: string,
      agent_account_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAgentAccount',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-accounts/${encodeURIComponent(String(agent_account_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAgentAccount: (
      tenant_id: string,
      connector_id: string,
      agent_account_id: string,
      body: AgentAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentAccount',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-accounts/${encodeURIComponent(String(agent_account_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAgentAccountsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAgentAccountsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentAccountsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-accounts/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAgentCapabilityByRef: (
      tenant_id: string,
      connector_id: string,
      query: { agent_ref: string; capability_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAgentCapabilityByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-capabilities` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAgentCapabilities: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAgentCapability>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgentCapabilities',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-capabilities` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAgentCapabilityWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AgentCapability,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentCapabilityWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-capabilities`,
        body,
        undefined,
        finalOptions
      );
    },
    findAgentCapability: (
      tenant_id: string,
      connector_id: string,
      agent_capability_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentCapability>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAgentCapability',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-capabilities/${encodeURIComponent(String(agent_capability_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAgentCapability: (
      tenant_id: string,
      connector_id: string,
      agent_capability_id: string,
      body: AgentCapability,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentCapability>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentCapability',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-capabilities/${encodeURIComponent(String(agent_capability_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAgentCapabilitiesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAgentCapabilitiesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentCapabilitiesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-capabilities/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAgentDeploymentAttributeByRef: (
      tenant_id: string,
      connector_id: string,
      query: { deployment_ref: string; attribute_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'deleteAgentDeploymentAttributeByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-attributes` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAgentDeploymentAttributes: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAgentDeploymentAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgentDeploymentAttributes',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-attributes` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAgentDeploymentAttributeWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AgentDeploymentAttribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentDeploymentAttributeWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-attributes`,
        body,
        undefined,
        finalOptions
      );
    },
    findAgentDeploymentAttribute: (
      tenant_id: string,
      connector_id: string,
      agent_deployment_attribute_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDeploymentAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAgentDeploymentAttribute',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-attributes/${encodeURIComponent(String(agent_deployment_attribute_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAgentDeploymentAttribute: (
      tenant_id: string,
      connector_id: string,
      agent_deployment_attribute_id: string,
      body: AgentDeploymentAttribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDeploymentAttribute>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentDeploymentAttribute',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-attributes/${encodeURIComponent(String(agent_deployment_attribute_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAgentDeploymentAttributesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAgentDeploymentAttributesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentDeploymentAttributesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-attributes/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAgentDeploymentExtensionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { deployment_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'deleteAgentDeploymentExtensionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-extensions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAgentDeploymentExtensions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAgentDeploymentExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgentDeploymentExtensions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-extensions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAgentDeploymentExtensionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AgentDeploymentExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentDeploymentExtensionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-extensions`,
        body,
        undefined,
        finalOptions
      );
    },
    findAgentDeploymentExtension: (
      tenant_id: string,
      connector_id: string,
      agent_deployment_extension_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDeploymentExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAgentDeploymentExtension',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-extensions/${encodeURIComponent(String(agent_deployment_extension_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAgentDeploymentExtension: (
      tenant_id: string,
      connector_id: string,
      agent_deployment_extension_id: string,
      body: AgentDeploymentExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDeploymentExtension>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentDeploymentExtension',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-extensions/${encodeURIComponent(String(agent_deployment_extension_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAgentDeploymentExtensionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAgentDeploymentExtensionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentDeploymentExtensionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-extensions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAgentDeploymentResourceByRef: (
      tenant_id: string,
      connector_id: string,
      query: { deployment_ref: string; resource_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'deleteAgentDeploymentResourceByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-resources` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAgentDeploymentResources: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAgentDeploymentResource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgentDeploymentResources',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-resources` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAgentDeploymentResourceWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AgentDeploymentResource,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentDeploymentResourceWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-resources`,
        body,
        undefined,
        finalOptions
      );
    },
    findAgentDeploymentResource: (
      tenant_id: string,
      connector_id: string,
      agent_deployment_resource_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDeploymentResource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAgentDeploymentResource',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-resources/${encodeURIComponent(String(agent_deployment_resource_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAgentDeploymentResource: (
      tenant_id: string,
      connector_id: string,
      agent_deployment_resource_id: string,
      body: AgentDeploymentResource,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDeploymentResource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentDeploymentResource',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-resources/${encodeURIComponent(String(agent_deployment_resource_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAgentDeploymentResourcesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAgentDeploymentResourcesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentDeploymentResourcesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployment-resources/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAgentDeploymentByRef: (
      tenant_id: string,
      connector_id: string,
      query: { deployment_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAgentDeploymentByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployments` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAgentDeployments: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAgentDeployment>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgentDeployments',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployments` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAgentDeploymentWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AgentDeployment,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileAgentDeploymentWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployments`,
        body,
        undefined,
        finalOptions
      );
    },
    findAgentDeployment: (
      tenant_id: string,
      connector_id: string,
      agent_deployment_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDeployment>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAgentDeployment',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployments/${encodeURIComponent(String(agent_deployment_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAgentDeployment: (
      tenant_id: string,
      connector_id: string,
      agent_deployment_id: string,
      body: AgentDeployment,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDeployment>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentDeployment',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployments/${encodeURIComponent(String(agent_deployment_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAgentDeploymentsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAgentDeploymentsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentDeploymentsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-deployments/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAgentOwnerByRef: (
      tenant_id: string,
      connector_id: string,
      query: { agent_ref: string; owner_account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAgentOwnerByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-owners` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAgentOwners: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAgentOwner>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgentOwners',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-owners` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAgentOwnerWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: AgentOwner,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentOwnerWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-owners`,
        body,
        undefined,
        finalOptions
      );
    },
    findAgentOwner: (
      tenant_id: string,
      connector_id: string,
      agent_owner_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentOwner>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAgentOwner',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-owners/${encodeURIComponent(String(agent_owner_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAgentOwner: (
      tenant_id: string,
      connector_id: string,
      agent_owner_id: string,
      body: AgentOwner,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentOwner>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentOwner',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-owners/${encodeURIComponent(String(agent_owner_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAgentOwnersBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAgentOwnersRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentOwnersBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agent-owners/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAgentByRef: (
      tenant_id: string,
      connector_id: string,
      query: { agent_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAgentByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agents` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAgents: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAgent>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgents',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agents` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAgentWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Agent,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agents`,
        body,
        undefined,
        finalOptions
      );
    },
    findAgent: (
      tenant_id: string,
      connector_id: string,
      agent_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Agent>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAgent',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agents/${encodeURIComponent(String(agent_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAgent: (
      tenant_id: string,
      connector_id: string,
      agent_id: string,
      body: Agent,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Agent>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgent',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agents/${encodeURIComponent(String(agent_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAgentsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAgentsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAgentsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/agents/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteApplicationAccountByRef: (
      tenant_id: string,
      connector_id: string,
      query: { application_ref: string; account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteApplicationAccountByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-accounts` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listApplicationAccounts: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplicationAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileApplicationAccountWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ApplicationAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileApplicationAccountWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-accounts`,
        body,
        undefined,
        finalOptions
      );
    },
    findApplicationAccount: (
      tenant_id: string,
      connector_id: string,
      application_account_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findApplicationAccount',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-accounts/${encodeURIComponent(String(application_account_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationAccount: (
      tenant_id: string,
      connector_id: string,
      application_account_id: string,
      body: ApplicationAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationAccount',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-accounts/${encodeURIComponent(String(application_account_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationAccountsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchApplicationAccountsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileApplicationAccountsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-accounts/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteApplicationAgentByRef: (
      tenant_id: string,
      connector_id: string,
      query: { application_ref: string; agent_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteApplicationAgentByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-agents` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listApplicationAgents: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplicationAgent>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationAgents',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-agents` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileApplicationAgentWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ApplicationAgent,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileApplicationAgentWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-agents`,
        body,
        undefined,
        finalOptions
      );
    },
    findApplicationAgent: (
      tenant_id: string,
      connector_id: string,
      application_agent_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationAgent>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findApplicationAgent',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-agents/${encodeURIComponent(String(application_agent_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationAgent: (
      tenant_id: string,
      connector_id: string,
      application_agent_id: string,
      body: ApplicationAgent,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationAgent>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationAgent',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-agents/${encodeURIComponent(String(application_agent_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationAgentsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchApplicationAgentsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationAgentsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-agents/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteApplicationExtensionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { application_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteApplicationExtensionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-extensions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listApplicationExtensions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplicationExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationExtensions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-extensions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileApplicationExtensionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ApplicationExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileApplicationExtensionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-extensions`,
        body,
        undefined,
        finalOptions
      );
    },
    findApplicationExtension: (
      tenant_id: string,
      connector_id: string,
      application_extension_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findApplicationExtension',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-extensions/${encodeURIComponent(String(application_extension_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationExtension: (
      tenant_id: string,
      connector_id: string,
      application_extension_id: string,
      body: ApplicationExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationExtension',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-extensions/${encodeURIComponent(String(application_extension_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationExtensionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchApplicationExtensionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileApplicationExtensionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-extensions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteApplicationGroupByRef: (
      tenant_id: string,
      connector_id: string,
      query: { application_ref: string; group_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteApplicationGroupByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-groups` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listApplicationGroups: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplicationGroup>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationGroups',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-groups` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileApplicationGroupWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ApplicationGroup,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileApplicationGroupWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-groups`,
        body,
        undefined,
        finalOptions
      );
    },
    findApplicationGroup: (
      tenant_id: string,
      connector_id: string,
      application_group_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationGroup>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findApplicationGroup',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-groups/${encodeURIComponent(String(application_group_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationGroup: (
      tenant_id: string,
      connector_id: string,
      application_group_id: string,
      body: ApplicationGroup,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationGroup>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationGroup',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-groups/${encodeURIComponent(String(application_group_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationGroupsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchApplicationGroupsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationGroupsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-groups/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteApplicationPermissionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { application_ref: string; permission_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteApplicationPermissionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-permissions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listApplicationPermissions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplicationPermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationPermissions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileApplicationPermissionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ApplicationPermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileApplicationPermissionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-permissions`,
        body,
        undefined,
        finalOptions
      );
    },
    findApplicationPermission: (
      tenant_id: string,
      connector_id: string,
      application_permission_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationPermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findApplicationPermission',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-permissions/${encodeURIComponent(String(application_permission_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationPermission: (
      tenant_id: string,
      connector_id: string,
      application_permission_id: string,
      body: ApplicationPermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationPermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationPermission',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-permissions/${encodeURIComponent(String(application_permission_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationPermissionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchApplicationPermissionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileApplicationPermissionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-permissions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteApplicationRoleByRef: (
      tenant_id: string,
      connector_id: string,
      query: { application_ref: string; role_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteApplicationRoleByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-roles` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listApplicationRoles: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplicationRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationRoles',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileApplicationRoleWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ApplicationRole,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileApplicationRoleWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-roles`,
        body,
        undefined,
        finalOptions
      );
    },
    findApplicationRole: (
      tenant_id: string,
      connector_id: string,
      application_role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findApplicationRole',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-roles/${encodeURIComponent(String(application_role_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationRole: (
      tenant_id: string,
      connector_id: string,
      application_role_id: string,
      body: ApplicationRole,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ApplicationRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationRole',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-roles/${encodeURIComponent(String(application_role_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationRolesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchApplicationRolesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationRolesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/application-roles/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteApplicationByRef: (
      tenant_id: string,
      connector_id: string,
      query: { application_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteApplicationByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/applications` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listApplications: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplication>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplications',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/applications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileApplicationWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Application,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/applications`,
        body,
        undefined,
        finalOptions
      );
    },
    findApplication: (
      tenant_id: string,
      connector_id: string,
      application_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Application>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findApplication',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/applications/${encodeURIComponent(String(application_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileApplication: (
      tenant_id: string,
      connector_id: string,
      application_id: string,
      body: Application,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Application>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplication',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/applications/${encodeURIComponent(String(application_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileApplicationsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchApplicationsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileApplicationsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/applications/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    applyCatalogChanges: (
      tenant_id: string,
      connector_id: string,
      body: ApplyCatalogChanges,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<CatalogApplyResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'applyCatalogChanges',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/apply`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteAttributeByRef: (
      tenant_id: string,
      connector_id: string,
      query: { attribute_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAttributeByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/attributes` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listAttributes: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAttributes',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/attributes` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileAttributeWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Attribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAttributeWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/attributes`,
        body,
        undefined,
        finalOptions
      );
    },
    findAttribute: (
      tenant_id: string,
      connector_id: string,
      attribute_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Attribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAttribute',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/attributes/${encodeURIComponent(String(attribute_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileAttribute: (
      tenant_id: string,
      connector_id: string,
      attribute_id: string,
      body: Attribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Attribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAttribute',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/attributes/${encodeURIComponent(String(attribute_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileAttributesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchAttributesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileAttributesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/attributes/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteCampaignByRef: (
      tenant_id: string,
      connector_id: string,
      query: { campaign_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteCampaignByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/campaigns` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listCampaigns: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageCampaign>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listCampaigns',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/campaigns` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileCampaignWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Campaign,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileCampaignWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/campaigns`,
        body,
        undefined,
        finalOptions
      );
    },
    findCampaign: (
      tenant_id: string,
      connector_id: string,
      campaign_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Campaign>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findCampaign',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/campaigns/${encodeURIComponent(String(campaign_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileCampaign: (
      tenant_id: string,
      connector_id: string,
      campaign_id: string,
      body: Campaign,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Campaign>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileCampaign',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/campaigns/${encodeURIComponent(String(campaign_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileCampaignsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchCampaignsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileCampaignsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/campaigns/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteChannelAccountByRef: (
      tenant_id: string,
      connector_id: string,
      query: { channel_ref: string; account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteChannelAccountByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channel-accounts` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listChannelAccounts: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageChannelAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listChannelAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channel-accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileChannelAccountWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ChannelAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileChannelAccountWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channel-accounts`,
        body,
        undefined,
        finalOptions
      );
    },
    findChannelAccount: (
      tenant_id: string,
      connector_id: string,
      channel_account_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ChannelAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findChannelAccount',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channel-accounts/${encodeURIComponent(String(channel_account_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileChannelAccount: (
      tenant_id: string,
      connector_id: string,
      channel_account_id: string,
      body: ChannelAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ChannelAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileChannelAccount',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channel-accounts/${encodeURIComponent(String(channel_account_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileChannelAccountsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchChannelAccountsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileChannelAccountsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channel-accounts/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteChannelByRef: (
      tenant_id: string,
      connector_id: string,
      query: { channel_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteChannelByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channels` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listChannels: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageChannel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listChannels',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channels` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileChannelWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Channel,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileChannelWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channels`,
        body,
        undefined,
        finalOptions
      );
    },
    findChannel: (
      tenant_id: string,
      connector_id: string,
      channel_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Channel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findChannel',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channels/${encodeURIComponent(String(channel_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileChannel: (
      tenant_id: string,
      connector_id: string,
      channel_id: string,
      body: Channel,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Channel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileChannel',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channels/${encodeURIComponent(String(channel_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileChannelsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchChannelsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileChannelsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/channels/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteClassificationByRef: (
      tenant_id: string,
      connector_id: string,
      query: { classification_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteClassificationByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/classifications` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listClassifications: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageClassification>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listClassifications',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/classifications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileClassificationWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Classification,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileClassificationWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/classifications`,
        body,
        undefined,
        finalOptions
      );
    },
    findClassification: (
      tenant_id: string,
      connector_id: string,
      classification_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Classification>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findClassification',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/classifications/${encodeURIComponent(String(classification_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileClassification: (
      tenant_id: string,
      connector_id: string,
      classification_id: string,
      body: Classification,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Classification>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileClassification',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/classifications/${encodeURIComponent(String(classification_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileClassificationsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchClassificationsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileClassificationsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/classifications/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteComplianceBaselineByRef: (
      tenant_id: string,
      connector_id: string,
      query: { compliance_baseline_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteComplianceBaselineByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/compliance-baselines` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listComplianceBaselines: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageComplianceBaseline>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listComplianceBaselines',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/compliance-baselines` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileComplianceBaselineWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ComplianceBaseline,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileComplianceBaselineWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/compliance-baselines`,
        body,
        undefined,
        finalOptions
      );
    },
    findComplianceBaseline: (
      tenant_id: string,
      connector_id: string,
      compliance_baseline_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ComplianceBaseline>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findComplianceBaseline',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/compliance-baselines/${encodeURIComponent(String(compliance_baseline_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileComplianceBaseline: (
      tenant_id: string,
      connector_id: string,
      compliance_baseline_id: string,
      body: ComplianceBaseline,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ComplianceBaseline>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileComplianceBaseline',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/compliance-baselines/${encodeURIComponent(String(compliance_baseline_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileComplianceBaselinesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchComplianceBaselinesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileComplianceBaselinesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/compliance-baselines/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteContactByRef: (
      tenant_id: string,
      connector_id: string,
      query: { contact_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteContactByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/contacts` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listContacts: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageContact>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listContacts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/contacts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileContactWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Contact,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileContactWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/contacts`,
        body,
        undefined,
        finalOptions
      );
    },
    findContact: (
      tenant_id: string,
      connector_id: string,
      contact_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Contact>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findContact',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/contacts/${encodeURIComponent(String(contact_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileContact: (
      tenant_id: string,
      connector_id: string,
      contact_id: string,
      body: Contact,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Contact>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileContact',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/contacts/${encodeURIComponent(String(contact_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileContactsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchContactsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileContactsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/contacts/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteDeviceAccountByRef: (
      tenant_id: string,
      connector_id: string,
      query: { device_ref: string; account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteDeviceAccountByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-accounts` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listDeviceAccounts: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageDeviceAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listDeviceAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileDeviceAccountWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: DeviceAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileDeviceAccountWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-accounts`,
        body,
        undefined,
        finalOptions
      );
    },
    findDeviceAccount: (
      tenant_id: string,
      connector_id: string,
      device_account_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<DeviceAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findDeviceAccount',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-accounts/${encodeURIComponent(String(device_account_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileDeviceAccount: (
      tenant_id: string,
      connector_id: string,
      device_account_id: string,
      body: DeviceAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<DeviceAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileDeviceAccount',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-accounts/${encodeURIComponent(String(device_account_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileDeviceAccountsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchDeviceAccountsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileDeviceAccountsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-accounts/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteDeviceApplicationByRef: (
      tenant_id: string,
      connector_id: string,
      query: { device_ref: string; application_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteDeviceApplicationByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-applications` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listDeviceApplications: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageDeviceApplication>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listDeviceApplications',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-applications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileDeviceApplicationWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: DeviceApplication,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileDeviceApplicationWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-applications`,
        body,
        undefined,
        finalOptions
      );
    },
    findDeviceApplication: (
      tenant_id: string,
      connector_id: string,
      device_application_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<DeviceApplication>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findDeviceApplication',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-applications/${encodeURIComponent(String(device_application_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileDeviceApplication: (
      tenant_id: string,
      connector_id: string,
      device_application_id: string,
      body: DeviceApplication,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<DeviceApplication>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileDeviceApplication',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-applications/${encodeURIComponent(String(device_application_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileDeviceApplicationsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchDeviceApplicationsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileDeviceApplicationsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/device-applications/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteDeviceByRef: (
      tenant_id: string,
      connector_id: string,
      query: { device_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteDeviceByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/devices` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listDevices: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageDevice>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listDevices',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/devices` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileDeviceWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Device,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileDeviceWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/devices`,
        body,
        undefined,
        finalOptions
      );
    },
    findDevice: (
      tenant_id: string,
      connector_id: string,
      device_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Device>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findDevice',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/devices/${encodeURIComponent(String(device_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileDevice: (
      tenant_id: string,
      connector_id: string,
      device_id: string,
      body: Device,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Device>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileDevice',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/devices/${encodeURIComponent(String(device_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileDevicesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchDevicesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileDevicesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/devices/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteEmployeeByRef: (
      tenant_id: string,
      connector_id: string,
      query: { employee_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteEmployeeByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/employees` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listEmployees: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageEmployee>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listEmployees',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/employees` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileEmployeeWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Employee,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileEmployeeWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/employees`,
        body,
        undefined,
        finalOptions
      );
    },
    findEmployee: (
      tenant_id: string,
      connector_id: string,
      employee_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Employee>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findEmployee',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/employees/${encodeURIComponent(String(employee_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileEmployee: (
      tenant_id: string,
      connector_id: string,
      employee_id: string,
      body: Employee,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Employee>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileEmployee',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/employees/${encodeURIComponent(String(employee_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileEmployeesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchEmployeesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileEmployeesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/employees/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    listEntityKinds: (
      tenant_id: string,
      connector_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<EntityKindSummary>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listEntityKinds',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/entities`,
        undefined,
        finalOptions
      );
    },
    deleteFederationServiceProviderByRef: (
      tenant_id: string,
      connector_id: string,
      query: { pool_ref: string; provider_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'deleteFederationServiceProviderByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-service-providers` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listFederationServiceProviders: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageFederationServiceProvider>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listFederationServiceProviders',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-service-providers` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileFederationServiceProviderWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: FederationServiceProvider,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileFederationServiceProviderWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-service-providers`,
        body,
        undefined,
        finalOptions
      );
    },
    findFederationServiceProvider: (
      tenant_id: string,
      connector_id: string,
      federation_service_provider_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<FederationServiceProvider>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findFederationServiceProvider',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-service-providers/${encodeURIComponent(String(federation_service_provider_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileFederationServiceProvider: (
      tenant_id: string,
      connector_id: string,
      federation_service_provider_id: string,
      body: FederationServiceProvider,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<FederationServiceProvider>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileFederationServiceProvider',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-service-providers/${encodeURIComponent(String(federation_service_provider_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileFederationServiceProvidersBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchFederationServiceProvidersRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileFederationServiceProvidersBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-service-providers/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteFederationServiceByRef: (
      tenant_id: string,
      connector_id: string,
      query: { federation_service_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteFederationServiceByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-services` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listFederationServices: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageFederationService>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listFederationServices',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-services` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileFederationServiceWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: FederationService,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileFederationServiceWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-services`,
        body,
        undefined,
        finalOptions
      );
    },
    findFederationService: (
      tenant_id: string,
      connector_id: string,
      federation_service_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<FederationService>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findFederationService',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-services/${encodeURIComponent(String(federation_service_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileFederationService: (
      tenant_id: string,
      connector_id: string,
      federation_service_id: string,
      body: FederationService,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<FederationService>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileFederationService',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-services/${encodeURIComponent(String(federation_service_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileFederationServicesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchFederationServicesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileFederationServicesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/federation-services/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteGroupAttributeByRef: (
      tenant_id: string,
      connector_id: string,
      query: { group_ref: string; attribute_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteGroupAttributeByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-attributes` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listGroupAttributes: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroupAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupAttributes',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-attributes` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileGroupAttributeWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: GroupAttribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupAttributeWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-attributes`,
        body,
        undefined,
        finalOptions
      );
    },
    findGroupAttribute: (
      tenant_id: string,
      connector_id: string,
      group_attribute_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findGroupAttribute',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-attributes/${encodeURIComponent(String(group_attribute_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileGroupAttribute: (
      tenant_id: string,
      connector_id: string,
      group_attribute_id: string,
      body: GroupAttribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupAttribute',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-attributes/${encodeURIComponent(String(group_attribute_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileGroupAttributesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchGroupAttributesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupAttributesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-attributes/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteGroupExtensionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { group_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteGroupExtensionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-extensions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listGroupExtensions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroupExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupExtensions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-extensions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileGroupExtensionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: GroupExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupExtensionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-extensions`,
        body,
        undefined,
        finalOptions
      );
    },
    findGroupExtension: (
      tenant_id: string,
      connector_id: string,
      group_extension_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findGroupExtension',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-extensions/${encodeURIComponent(String(group_extension_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileGroupExtension: (
      tenant_id: string,
      connector_id: string,
      group_extension_id: string,
      body: GroupExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupExtension',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-extensions/${encodeURIComponent(String(group_extension_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileGroupExtensionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchGroupExtensionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupExtensionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-extensions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteGroupLicenseByRef: (
      tenant_id: string,
      connector_id: string,
      query: { group_ref: string; license_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteGroupLicenseByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-licenses` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listGroupLicenses: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroupLicense>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupLicenses',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-licenses` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileGroupLicenseWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: GroupLicense,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupLicenseWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-licenses`,
        body,
        undefined,
        finalOptions
      );
    },
    findGroupLicense: (
      tenant_id: string,
      connector_id: string,
      group_license_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupLicense>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findGroupLicense',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-licenses/${encodeURIComponent(String(group_license_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileGroupLicense: (
      tenant_id: string,
      connector_id: string,
      group_license_id: string,
      body: GroupLicense,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupLicense>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupLicense',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-licenses/${encodeURIComponent(String(group_license_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileGroupLicensesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchGroupLicensesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupLicensesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-licenses/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteGroupMemberByRef: (
      tenant_id: string,
      connector_id: string,
      query: { group_ref: string; account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteGroupMemberByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-members` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listGroupMembers: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroupMember>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupMembers',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-members` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileGroupMemberWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: GroupMember,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupMemberWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-members`,
        body,
        undefined,
        finalOptions
      );
    },
    findGroupMember: (
      tenant_id: string,
      connector_id: string,
      group_member_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupMember>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findGroupMember',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-members/${encodeURIComponent(String(group_member_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileGroupMember: (
      tenant_id: string,
      connector_id: string,
      group_member_id: string,
      body: GroupMember,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupMember>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupMember',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-members/${encodeURIComponent(String(group_member_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileGroupMembersBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchGroupMembersRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupMembersBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-members/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteGroupMembershipRoleByRef: (
      tenant_id: string,
      connector_id: string,
      query: { group_ref: string; principal_ref: string; role: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteGroupMembershipRoleByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-membership-roles` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listGroupMembershipRoles: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroupMembershipRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupMembershipRoles',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-membership-roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileGroupMembershipRoleWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: GroupMembershipRole,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileGroupMembershipRoleWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-membership-roles`,
        body,
        undefined,
        finalOptions
      );
    },
    findGroupMembershipRole: (
      tenant_id: string,
      connector_id: string,
      group_membership_role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupMembershipRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findGroupMembershipRole',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-membership-roles/${encodeURIComponent(String(group_membership_role_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileGroupMembershipRole: (
      tenant_id: string,
      connector_id: string,
      group_membership_role_id: string,
      body: GroupMembershipRole,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupMembershipRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupMembershipRole',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-membership-roles/${encodeURIComponent(String(group_membership_role_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileGroupMembershipRolesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchGroupMembershipRolesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileGroupMembershipRolesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-membership-roles/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteGroupPermissionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { group_ref: string; permission_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteGroupPermissionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-permissions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listGroupPermissions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroupPermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupPermissions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileGroupPermissionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: GroupPermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileGroupPermissionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-permissions`,
        body,
        undefined,
        finalOptions
      );
    },
    findGroupPermission: (
      tenant_id: string,
      connector_id: string,
      group_permission_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupPermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findGroupPermission',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-permissions/${encodeURIComponent(String(group_permission_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileGroupPermission: (
      tenant_id: string,
      connector_id: string,
      group_permission_id: string,
      body: GroupPermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupPermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupPermission',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-permissions/${encodeURIComponent(String(group_permission_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileGroupPermissionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchGroupPermissionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupPermissionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-permissions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteGroupRoleByRef: (
      tenant_id: string,
      connector_id: string,
      query: { group_ref: string; role_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteGroupRoleByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-roles` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listGroupRoles: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroupRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupRoles',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileGroupRoleWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: GroupRole,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupRoleWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-roles`,
        body,
        undefined,
        finalOptions
      );
    },
    findGroupRole: (
      tenant_id: string,
      connector_id: string,
      group_role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findGroupRole',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-roles/${encodeURIComponent(String(group_role_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileGroupRole: (
      tenant_id: string,
      connector_id: string,
      group_role_id: string,
      body: GroupRole,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupRole',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-roles/${encodeURIComponent(String(group_role_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileGroupRolesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchGroupRolesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupRolesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/group-roles/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteGroupByRef: (
      tenant_id: string,
      connector_id: string,
      query: { group_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteGroupByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/groups` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listGroups: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroup>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroups',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/groups` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileGroupWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Group,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/groups`,
        body,
        undefined,
        finalOptions
      );
    },
    findGroup: (
      tenant_id: string,
      connector_id: string,
      group_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Group>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findGroup',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/groups/${encodeURIComponent(String(group_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileGroup: (
      tenant_id: string,
      connector_id: string,
      group_id: string,
      body: Group,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Group>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroup',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/groups/${encodeURIComponent(String(group_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileGroupsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchGroupsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileGroupsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/groups/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteLicenseByRef: (
      tenant_id: string,
      connector_id: string,
      query: { license_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteLicenseByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/licenses` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listLicenses: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageLicense>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listLicenses',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/licenses` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileLicenseWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: License,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileLicenseWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/licenses`,
        body,
        undefined,
        finalOptions
      );
    },
    findLicense: (
      tenant_id: string,
      connector_id: string,
      license_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<License>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findLicense',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/licenses/${encodeURIComponent(String(license_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileLicense: (
      tenant_id: string,
      connector_id: string,
      license_id: string,
      body: License,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<License>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileLicense',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/licenses/${encodeURIComponent(String(license_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileLicensesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchLicensesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileLicensesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/licenses/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteLocationByRef: (
      tenant_id: string,
      connector_id: string,
      query: { location_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteLocationByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/locations` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listLocations: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageLocation>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listLocations',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/locations` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileLocationWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Location,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileLocationWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/locations`,
        body,
        undefined,
        finalOptions
      );
    },
    findLocation: (
      tenant_id: string,
      connector_id: string,
      location_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Location>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findLocation',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/locations/${encodeURIComponent(String(location_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileLocation: (
      tenant_id: string,
      connector_id: string,
      location_id: string,
      body: Location,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Location>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileLocation',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/locations/${encodeURIComponent(String(location_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileLocationsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchLocationsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileLocationsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/locations/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteMultiFactorByRef: (
      tenant_id: string,
      connector_id: string,
      query: { multi_factor_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteMultiFactorByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/multi-factors` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listMultiFactors: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageMultiFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listMultiFactors',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/multi-factors` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileMultiFactorWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: MultiFactor,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileMultiFactorWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/multi-factors`,
        body,
        undefined,
        finalOptions
      );
    },
    findMultiFactor: (
      tenant_id: string,
      connector_id: string,
      multi_factor_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<MultiFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findMultiFactor',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/multi-factors/${encodeURIComponent(String(multi_factor_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileMultiFactor: (
      tenant_id: string,
      connector_id: string,
      multi_factor_id: string,
      body: MultiFactor,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<MultiFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileMultiFactor',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/multi-factors/${encodeURIComponent(String(multi_factor_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileMultiFactorsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchMultiFactorsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileMultiFactorsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/multi-factors/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteNetworkObjectByRef: (
      tenant_id: string,
      connector_id: string,
      query: { network_object_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteNetworkObjectByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/network-objects` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listNetworkObjects: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageNetworkObject>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listNetworkObjects',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/network-objects` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileNetworkObjectWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: NetworkObject,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileNetworkObjectWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/network-objects`,
        body,
        undefined,
        finalOptions
      );
    },
    findNetworkObject: (
      tenant_id: string,
      connector_id: string,
      network_object_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<NetworkObject>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findNetworkObject',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/network-objects/${encodeURIComponent(String(network_object_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileNetworkObject: (
      tenant_id: string,
      connector_id: string,
      network_object_id: string,
      body: NetworkObject,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<NetworkObject>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileNetworkObject',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/network-objects/${encodeURIComponent(String(network_object_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileNetworkObjectsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchNetworkObjectsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileNetworkObjectsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/network-objects/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteOrganizationalUnitMemberByRef: (
      tenant_id: string,
      connector_id: string,
      query: { ou_ref: string; principal_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'deleteOrganizationalUnitMemberByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-unit-members` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listOrganizationalUnitMembers: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageOrganizationalUnitMember>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listOrganizationalUnitMembers',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-unit-members` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileOrganizationalUnitMemberWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: OrganizationalUnitMember,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileOrganizationalUnitMemberWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-unit-members`,
        body,
        undefined,
        finalOptions
      );
    },
    findOrganizationalUnitMember: (
      tenant_id: string,
      connector_id: string,
      organizational_unit_member_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OrganizationalUnitMember>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findOrganizationalUnitMember',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-unit-members/${encodeURIComponent(String(organizational_unit_member_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileOrganizationalUnitMember: (
      tenant_id: string,
      connector_id: string,
      organizational_unit_member_id: string,
      body: OrganizationalUnitMember,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OrganizationalUnitMember>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileOrganizationalUnitMember',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-unit-members/${encodeURIComponent(String(organizational_unit_member_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileOrganizationalUnitMembersBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchOrganizationalUnitMembersRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileOrganizationalUnitMembersBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-unit-members/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteOrganizationalUnitByRef: (
      tenant_id: string,
      connector_id: string,
      query: { organizational_unit_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteOrganizationalUnitByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-units` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listOrganizationalUnits: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageOrganizationalUnit>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listOrganizationalUnits',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-units` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileOrganizationalUnitWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: OrganizationalUnit,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileOrganizationalUnitWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-units`,
        body,
        undefined,
        finalOptions
      );
    },
    findOrganizationalUnit: (
      tenant_id: string,
      connector_id: string,
      organizational_unit_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OrganizationalUnit>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findOrganizationalUnit',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-units/${encodeURIComponent(String(organizational_unit_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileOrganizationalUnit: (
      tenant_id: string,
      connector_id: string,
      organizational_unit_id: string,
      body: OrganizationalUnit,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OrganizationalUnit>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileOrganizationalUnit',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-units/${encodeURIComponent(String(organizational_unit_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileOrganizationalUnitsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchOrganizationalUnitsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileOrganizationalUnitsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/organizational-units/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deletePasswordPolicyByRef: (
      tenant_id: string,
      connector_id: string,
      query: { password_policy_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deletePasswordPolicyByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/password-policies` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listPasswordPolicies: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePasswordPolicy>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPasswordPolicies',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/password-policies` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcilePasswordPolicyWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: PasswordPolicy,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePasswordPolicyWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/password-policies`,
        body,
        undefined,
        finalOptions
      );
    },
    findPasswordPolicy: (
      tenant_id: string,
      connector_id: string,
      password_policy_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PasswordPolicy>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findPasswordPolicy',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/password-policies/${encodeURIComponent(String(password_policy_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcilePasswordPolicy: (
      tenant_id: string,
      connector_id: string,
      password_policy_id: string,
      body: PasswordPolicy,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PasswordPolicy>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePasswordPolicy',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/password-policies/${encodeURIComponent(String(password_policy_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcilePasswordPoliciesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchPasswordPoliciesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePasswordPoliciesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/password-policies/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deletePeopleExtensionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { person_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deletePeopleExtensionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/people-extensions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listPeopleExtensions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePeopleExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPeopleExtensions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/people-extensions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcilePeopleExtensionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: PeopleExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcilePeopleExtensionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/people-extensions`,
        body,
        undefined,
        finalOptions
      );
    },
    findPeopleExtension: (
      tenant_id: string,
      connector_id: string,
      people_extension_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PeopleExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findPeopleExtension',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/people-extensions/${encodeURIComponent(String(people_extension_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcilePeopleExtension: (
      tenant_id: string,
      connector_id: string,
      people_extension_id: string,
      body: PeopleExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PeopleExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePeopleExtension',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/people-extensions/${encodeURIComponent(String(people_extension_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcilePeopleExtensionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchPeopleExtensionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePeopleExtensionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/people-extensions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deletePermissionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { permission_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deletePermissionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/permissions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listPermissions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPermissions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcilePermissionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Permission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePermissionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/permissions`,
        body,
        undefined,
        finalOptions
      );
    },
    findPermission: (
      tenant_id: string,
      connector_id: string,
      permission_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Permission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findPermission',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/permissions/${encodeURIComponent(String(permission_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcilePermission: (
      tenant_id: string,
      connector_id: string,
      permission_id: string,
      body: Permission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Permission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePermission',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/permissions/${encodeURIComponent(String(permission_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcilePermissionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchPermissionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePermissionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/permissions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deletePersonAccountByRef: (
      tenant_id: string,
      connector_id: string,
      query: { person_ref: string; account_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deletePersonAccountByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-accounts` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listPersonAccounts: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePersonAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPersonAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcilePersonAccountWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: PersonAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePersonAccountWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-accounts`,
        body,
        undefined,
        finalOptions
      );
    },
    findPersonAccount: (
      tenant_id: string,
      connector_id: string,
      person_account_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PersonAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findPersonAccount',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-accounts/${encodeURIComponent(String(person_account_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcilePersonAccount: (
      tenant_id: string,
      connector_id: string,
      person_account_id: string,
      body: PersonAccount,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PersonAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePersonAccount',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-accounts/${encodeURIComponent(String(person_account_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcilePersonAccountsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchPersonAccountsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePersonAccountsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-accounts/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deletePersonAttributeByRef: (
      tenant_id: string,
      connector_id: string,
      query: { person_ref: string; attribute_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deletePersonAttributeByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-attributes` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listPersonAttributes: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePersonAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPersonAttributes',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-attributes` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcilePersonAttributeWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: PersonAttribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcilePersonAttributeWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-attributes`,
        body,
        undefined,
        finalOptions
      );
    },
    findPersonAttribute: (
      tenant_id: string,
      connector_id: string,
      person_attribute_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PersonAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findPersonAttribute',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-attributes/${encodeURIComponent(String(person_attribute_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcilePersonAttribute: (
      tenant_id: string,
      connector_id: string,
      person_attribute_id: string,
      body: PersonAttribute,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PersonAttribute>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePersonAttribute',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-attributes/${encodeURIComponent(String(person_attribute_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcilePersonAttributesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchPersonAttributesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePersonAttributesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/person-attributes/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deletePersonByRef: (
      tenant_id: string,
      connector_id: string,
      query: { person_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deletePersonByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/persons` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listPersons: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePerson>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPersons',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/persons` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcilePersonWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Person,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePersonWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/persons`,
        body,
        undefined,
        finalOptions
      );
    },
    findPerson: (
      tenant_id: string,
      connector_id: string,
      person_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Person>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findPerson',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/persons/${encodeURIComponent(String(person_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcilePerson: (
      tenant_id: string,
      connector_id: string,
      person_id: string,
      body: Person,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Person>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePerson',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/persons/${encodeURIComponent(String(person_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcilePersonsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchPersonsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePersonsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/persons/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deletePolicyByRef: (
      tenant_id: string,
      connector_id: string,
      query: { policy_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deletePolicyByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policies` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listPolicies: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePolicy>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPolicies',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policies` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcilePolicyWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Policy,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePolicyWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policies`,
        body,
        undefined,
        finalOptions
      );
    },
    findPolicy: (
      tenant_id: string,
      connector_id: string,
      policy_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Policy>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findPolicy',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policies/${encodeURIComponent(String(policy_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcilePolicy: (
      tenant_id: string,
      connector_id: string,
      policy_id: string,
      body: Policy,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Policy>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePolicy',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policies/${encodeURIComponent(String(policy_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcilePoliciesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchPoliciesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePoliciesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policies/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deletePolicyExtensionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { policy_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deletePolicyExtensionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policy-extensions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listPolicyExtensions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePolicyExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPolicyExtensions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policy-extensions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcilePolicyExtensionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: PolicyExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcilePolicyExtensionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policy-extensions`,
        body,
        undefined,
        finalOptions
      );
    },
    findPolicyExtension: (
      tenant_id: string,
      connector_id: string,
      policy_extension_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PolicyExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findPolicyExtension',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policy-extensions/${encodeURIComponent(String(policy_extension_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcilePolicyExtension: (
      tenant_id: string,
      connector_id: string,
      policy_extension_id: string,
      body: PolicyExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PolicyExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePolicyExtension',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policy-extensions/${encodeURIComponent(String(policy_extension_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcilePolicyExtensionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchPolicyExtensionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcilePolicyExtensionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/policy-extensions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    getConnectorCatalogProjectorStatus: (
      tenant_id: string,
      connector_id: string,
      space: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<CatalogProjectorStatusResult>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'getConnectorCatalogProjectorStatus',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/projector/${encodeURIComponent(String(space))}/status`,
        undefined,
        finalOptions
      );
    },
    resolveRBACPolicies: (
      tenant_id: string,
      connector_id: string,
      query: { role_ref: Array<string> },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<rbacResolveResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'resolveRBACPolicies',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/rbac/resolve` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    deleteResourceContainerExtensionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { container_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'deleteResourceContainerExtensionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-extensions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listResourceContainerExtensions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageResourceContainerExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listResourceContainerExtensions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-extensions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileResourceContainerExtensionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ResourceContainerExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ??
          'reconcileResourceContainerExtensionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-extensions`,
        body,
        undefined,
        finalOptions
      );
    },
    findResourceContainerExtension: (
      tenant_id: string,
      connector_id: string,
      resource_container_extension_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceContainerExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findResourceContainerExtension',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-extensions/${encodeURIComponent(String(resource_container_extension_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileResourceContainerExtension: (
      tenant_id: string,
      connector_id: string,
      resource_container_extension_id: string,
      body: ResourceContainerExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceContainerExtension>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourceContainerExtension',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-extensions/${encodeURIComponent(String(resource_container_extension_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileResourceContainerExtensionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchResourceContainerExtensionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourceContainerExtensionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-extensions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteResourceContainerResourceContainerByRef: (
      tenant_id: string,
      connector_id: string,
      query: { parent_container_ref: string; child_container_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ??
          'deleteResourceContainerResourceContainerByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resource-containers` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listResourceContainerResourceContainers: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageResourceContainerResourceContainer>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'listResourceContainerResourceContainers',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resource-containers` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileResourceContainerResourceContainerWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ResourceContainerResourceContainer,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ??
          'reconcileResourceContainerResourceContainerWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resource-containers`,
        body,
        undefined,
        finalOptions
      );
    },
    findResourceContainerResourceContainer: (
      tenant_id: string,
      connector_id: string,
      resource_container_resource_container_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceContainerResourceContainer>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'findResourceContainerResourceContainer',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resource-containers/${encodeURIComponent(String(resource_container_resource_container_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileResourceContainerResourceContainer: (
      tenant_id: string,
      connector_id: string,
      resource_container_resource_container_id: string,
      body: ResourceContainerResourceContainer,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceContainerResourceContainer>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourceContainerResourceContainer',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resource-containers/${encodeURIComponent(String(resource_container_resource_container_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileResourceContainerResourceContainersBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchResourceContainerResourceContainersRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ??
          'reconcileResourceContainerResourceContainersBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resource-containers/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteResourceContainerResourceByRef: (
      tenant_id: string,
      connector_id: string,
      query: { container_ref: string; resource_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'deleteResourceContainerResourceByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resources` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listResourceContainerResources: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageResourceContainerResource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listResourceContainerResources',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resources` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileResourceContainerResourceWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ResourceContainerResource,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourceContainerResourceWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resources`,
        body,
        undefined,
        finalOptions
      );
    },
    findResourceContainerResource: (
      tenant_id: string,
      connector_id: string,
      resource_container_resource_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceContainerResource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findResourceContainerResource',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resources/${encodeURIComponent(String(resource_container_resource_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileResourceContainerResource: (
      tenant_id: string,
      connector_id: string,
      resource_container_resource_id: string,
      body: ResourceContainerResource,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceContainerResource>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourceContainerResource',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resources/${encodeURIComponent(String(resource_container_resource_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileResourceContainerResourcesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchResourceContainerResourcesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourceContainerResourcesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-container-resources/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteResourceContainerByRef: (
      tenant_id: string,
      connector_id: string,
      query: { container_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteResourceContainerByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-containers` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listResourceContainers: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageResourceContainer>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listResourceContainers',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-containers` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileResourceContainerWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ResourceContainer,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourceContainerWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-containers`,
        body,
        undefined,
        finalOptions
      );
    },
    findResourceContainer: (
      tenant_id: string,
      connector_id: string,
      resource_container_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceContainer>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findResourceContainer',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-containers/${encodeURIComponent(String(resource_container_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileResourceContainer: (
      tenant_id: string,
      connector_id: string,
      resource_container_id: string,
      body: ResourceContainer,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceContainer>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileResourceContainer',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-containers/${encodeURIComponent(String(resource_container_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileResourceContainersBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchResourceContainersRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileResourceContainersBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-containers/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteResourceExtensionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { resource_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteResourceExtensionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-extensions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listResourceExtensions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageResourceExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listResourceExtensions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-extensions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileResourceExtensionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ResourceExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourceExtensionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-extensions`,
        body,
        undefined,
        finalOptions
      );
    },
    findResourceExtension: (
      tenant_id: string,
      connector_id: string,
      resource_extension_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findResourceExtension',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-extensions/${encodeURIComponent(String(resource_extension_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileResourceExtension: (
      tenant_id: string,
      connector_id: string,
      resource_extension_id: string,
      body: ResourceExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileResourceExtension',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-extensions/${encodeURIComponent(String(resource_extension_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileResourceExtensionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchResourceExtensionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileResourceExtensionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-extensions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteResourcePermissionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { permission_ref: string; resource_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteResourcePermissionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-permissions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listResourcePermissions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageResourcePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listResourcePermissions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileResourcePermissionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ResourcePermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourcePermissionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-permissions`,
        body,
        undefined,
        finalOptions
      );
    },
    findResourcePermission: (
      tenant_id: string,
      connector_id: string,
      resource_permission_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourcePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findResourcePermission',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-permissions/${encodeURIComponent(String(resource_permission_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileResourcePermission: (
      tenant_id: string,
      connector_id: string,
      resource_permission_id: string,
      body: ResourcePermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourcePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileResourcePermission',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-permissions/${encodeURIComponent(String(resource_permission_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileResourcePermissionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchResourcePermissionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileResourcePermissionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resource-permissions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteResourceByRef: (
      tenant_id: string,
      connector_id: string,
      query: { resource_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteResourceByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resources` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listResources: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageResource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listResources',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resources` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileResourceWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Resource,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileResourceWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resources`,
        body,
        undefined,
        finalOptions
      );
    },
    findResource: (
      tenant_id: string,
      connector_id: string,
      resource_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Resource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findResource',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resources/${encodeURIComponent(String(resource_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileResource: (
      tenant_id: string,
      connector_id: string,
      resource_id: string,
      body: Resource,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Resource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileResource',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resources/${encodeURIComponent(String(resource_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileResourcesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchResourcesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileResourcesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/resources/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteRiskFactorByRef: (
      tenant_id: string,
      connector_id: string,
      query: { risk_factor_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteRiskFactorByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/risk-factors` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listRiskFactors: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageRiskFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRiskFactors',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/risk-factors` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileRiskFactorWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: RiskFactor,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRiskFactorWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/risk-factors`,
        body,
        undefined,
        finalOptions
      );
    },
    findRiskFactor: (
      tenant_id: string,
      connector_id: string,
      risk_factor_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RiskFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findRiskFactor',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/risk-factors/${encodeURIComponent(String(risk_factor_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileRiskFactor: (
      tenant_id: string,
      connector_id: string,
      risk_factor_id: string,
      body: RiskFactor,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RiskFactor>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRiskFactor',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/risk-factors/${encodeURIComponent(String(risk_factor_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileRiskFactorsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchRiskFactorsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRiskFactorsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/risk-factors/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteRoleAssignmentTypeByRef: (
      tenant_id: string,
      connector_id: string,
      query: {
        principal_ref: string;
        role_ref: string;
        assignment_type: string;
        scope_ref?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteRoleAssignmentTypeByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-assignment-types` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listRoleAssignmentTypes: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageRoleAssignmentType>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRoleAssignmentTypes',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-assignment-types` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileRoleAssignmentTypeWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: RoleAssignmentType,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileRoleAssignmentTypeWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-assignment-types`,
        body,
        undefined,
        finalOptions
      );
    },
    findRoleAssignmentType: (
      tenant_id: string,
      connector_id: string,
      role_assignment_type_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RoleAssignmentType>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findRoleAssignmentType',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-assignment-types/${encodeURIComponent(String(role_assignment_type_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileRoleAssignmentType: (
      tenant_id: string,
      connector_id: string,
      role_assignment_type_id: string,
      body: RoleAssignmentType,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RoleAssignmentType>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRoleAssignmentType',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-assignment-types/${encodeURIComponent(String(role_assignment_type_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileRoleAssignmentTypesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchRoleAssignmentTypesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileRoleAssignmentTypesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-assignment-types/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteRoleExtensionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { role_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteRoleExtensionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-extensions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listRoleExtensions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageRoleExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRoleExtensions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-extensions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileRoleExtensionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: RoleExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRoleExtensionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-extensions`,
        body,
        undefined,
        finalOptions
      );
    },
    findRoleExtension: (
      tenant_id: string,
      connector_id: string,
      role_extension_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RoleExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findRoleExtension',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-extensions/${encodeURIComponent(String(role_extension_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileRoleExtension: (
      tenant_id: string,
      connector_id: string,
      role_extension_id: string,
      body: RoleExtension,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RoleExtension>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRoleExtension',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-extensions/${encodeURIComponent(String(role_extension_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileRoleExtensionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchRoleExtensionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRoleExtensionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-extensions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteRolePermissionByRef: (
      tenant_id: string,
      connector_id: string,
      query: { role_ref: string; permission_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteRolePermissionByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-permissions` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listRolePermissions: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageRolePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRolePermissions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileRolePermissionWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: RolePermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRolePermissionWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-permissions`,
        body,
        undefined,
        finalOptions
      );
    },
    findRolePermission: (
      tenant_id: string,
      connector_id: string,
      role_permission_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RolePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findRolePermission',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-permissions/${encodeURIComponent(String(role_permission_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileRolePermission: (
      tenant_id: string,
      connector_id: string,
      role_permission_id: string,
      body: RolePermission,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RolePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRolePermission',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-permissions/${encodeURIComponent(String(role_permission_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileRolePermissionsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchRolePermissionsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRolePermissionsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/role-permissions/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteRoleByRef: (
      tenant_id: string,
      connector_id: string,
      query: { role_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteRoleByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/roles` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listRoles: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRoles',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileRoleWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Role,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRoleWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/roles`,
        body,
        undefined,
        finalOptions
      );
    },
    findRole: (
      tenant_id: string,
      connector_id: string,
      role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Role>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findRole',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/roles/${encodeURIComponent(String(role_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileRole: (
      tenant_id: string,
      connector_id: string,
      role_id: string,
      body: Role,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Role>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRole',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/roles/${encodeURIComponent(String(role_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileRolesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchRolesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRolesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/roles/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    getConnectorRSOSRangeEntries: (
      tenant_id: string,
      connector_id: string,
      space: string,
      query?: { lo?: string; hi?: string; version?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<RSOSEntry>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getConnectorRSOSRangeEntries',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/rsos/${encodeURIComponent(String(space))}/range-entries` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getConnectorRSOSRangeFingerprint: (
      tenant_id: string,
      connector_id: string,
      space: string,
      query?: { lo?: string; hi?: string; version?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RSOSRange>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getConnectorRSOSRangeFingerprint',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/rsos/${encodeURIComponent(String(space))}/range-fingerprint` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getConnectorRSOSRangeSplit: (
      tenant_id: string,
      connector_id: string,
      space: string,
      query?: { lo?: string; hi?: string; k?: number; version?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<RSOSChildRange>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getConnectorRSOSRangeSplit',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/rsos/${encodeURIComponent(String(space))}/range-split` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    deleteScopedAssignmentByRef: (
      tenant_id: string,
      connector_id: string,
      query: { principal_ref: string; role_ref: string; scope_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteScopedAssignmentByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-assignments` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listScopedAssignments: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageScopedAssignment>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listScopedAssignments',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-assignments` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileScopedAssignmentWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ScopedAssignment,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileScopedAssignmentWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-assignments`,
        body,
        undefined,
        finalOptions
      );
    },
    findScopedAssignment: (
      tenant_id: string,
      connector_id: string,
      scoped_assignment_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ScopedAssignment>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findScopedAssignment',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-assignments/${encodeURIComponent(String(scoped_assignment_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileScopedAssignment: (
      tenant_id: string,
      connector_id: string,
      scoped_assignment_id: string,
      body: ScopedAssignment,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ScopedAssignment>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileScopedAssignment',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-assignments/${encodeURIComponent(String(scoped_assignment_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileScopedAssignmentsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchScopedAssignmentsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileScopedAssignmentsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-assignments/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteScopedDenialByRef: (
      tenant_id: string,
      connector_id: string,
      query: {
        effect: string;
        principal_ref: string;
        permission_ref: string;
        scope_ref: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteScopedDenialByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-denials` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listScopedDenials: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageScopedDenial>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listScopedDenials',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-denials` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileScopedDenialWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ScopedDenial,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileScopedDenialWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-denials`,
        body,
        undefined,
        finalOptions
      );
    },
    findScopedDenial: (
      tenant_id: string,
      connector_id: string,
      scoped_denial_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ScopedDenial>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findScopedDenial',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-denials/${encodeURIComponent(String(scoped_denial_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileScopedDenial: (
      tenant_id: string,
      connector_id: string,
      scoped_denial_id: string,
      body: ScopedDenial,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ScopedDenial>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileScopedDenial',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-denials/${encodeURIComponent(String(scoped_denial_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileScopedDenialsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchScopedDenialsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileScopedDenialsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/scoped-denials/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteSecretResourceByRef: (
      tenant_id: string,
      connector_id: string,
      query: { secret_ref: string; resource_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteSecretResourceByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secret-resources` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listSecretResources: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageSecretResource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listSecretResources',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secret-resources` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileSecretResourceWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: SecretResource,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSecretResourceWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secret-resources`,
        body,
        undefined,
        finalOptions
      );
    },
    findSecretResource: (
      tenant_id: string,
      connector_id: string,
      secret_resource_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SecretResource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findSecretResource',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secret-resources/${encodeURIComponent(String(secret_resource_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileSecretResource: (
      tenant_id: string,
      connector_id: string,
      secret_resource_id: string,
      body: SecretResource,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SecretResource>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSecretResource',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secret-resources/${encodeURIComponent(String(secret_resource_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileSecretResourcesBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchSecretResourcesRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSecretResourcesBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secret-resources/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteSecretByRef: (
      tenant_id: string,
      connector_id: string,
      query: { secret_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteSecretByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secrets` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listSecrets: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageSecret>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listSecrets',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secrets` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileSecretWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Secret,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSecretWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secrets`,
        body,
        undefined,
        finalOptions
      );
    },
    findSecret: (
      tenant_id: string,
      connector_id: string,
      secret_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Secret>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findSecret',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secrets/${encodeURIComponent(String(secret_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileSecret: (
      tenant_id: string,
      connector_id: string,
      secret_id: string,
      body: Secret,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Secret>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSecret',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secrets/${encodeURIComponent(String(secret_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileSecretsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchSecretsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSecretsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/secrets/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteSecurityConfigurationByRef: (
      tenant_id: string,
      connector_id: string,
      query: { security_configuration_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteSecurityConfigurationByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/security-configurations` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listSecurityConfigurations: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageSecurityConfiguration>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listSecurityConfigurations',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/security-configurations` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileSecurityConfigurationWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: SecurityConfiguration,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileSecurityConfigurationWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/security-configurations`,
        body,
        undefined,
        finalOptions
      );
    },
    findSecurityConfiguration: (
      tenant_id: string,
      connector_id: string,
      security_configuration_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SecurityConfiguration>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findSecurityConfiguration',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/security-configurations/${encodeURIComponent(String(security_configuration_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileSecurityConfiguration: (
      tenant_id: string,
      connector_id: string,
      security_configuration_id: string,
      body: SecurityConfiguration,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SecurityConfiguration>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSecurityConfiguration',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/security-configurations/${encodeURIComponent(String(security_configuration_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileSecurityConfigurationsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchSecurityConfigurationsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileSecurityConfigurationsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/security-configurations/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteServiceAccountImpersonationByRef: (
      tenant_id: string,
      connector_id: string,
      query: {
        principal_ref: string;
        role_ref: string;
        service_account_ref: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'deleteServiceAccountImpersonationByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/service_account_impersonations` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listServiceAccountImpersonations: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageServiceAccountImpersonation>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listServiceAccountImpersonations',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/service_account_impersonations` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileServiceAccountImpersonationWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: ServiceAccountImpersonation,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ??
          'reconcileServiceAccountImpersonationWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/service_account_impersonations`,
        body,
        undefined,
        finalOptions
      );
    },
    findServiceAccountImpersonation: (
      tenant_id: string,
      connector_id: string,
      service_account_impersonation_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ServiceAccountImpersonation>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findServiceAccountImpersonation',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/service_account_impersonations/${encodeURIComponent(String(service_account_impersonation_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileServiceAccountImpersonation: (
      tenant_id: string,
      connector_id: string,
      service_account_impersonation_id: string,
      body: ServiceAccountImpersonation,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ServiceAccountImpersonation>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileServiceAccountImpersonation',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/service_account_impersonations/${encodeURIComponent(String(service_account_impersonation_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileServiceAccountImpersonationsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchServiceAccountImpersonationsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileServiceAccountImpersonationsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/service_account_impersonations/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteSpecialPrincipalByRef: (
      tenant_id: string,
      connector_id: string,
      query: { principal_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteSpecialPrincipalByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/special_principals` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listSpecialPrincipals: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageSpecialPrincipal>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listSpecialPrincipals',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/special_principals` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileSpecialPrincipalWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: SpecialPrincipal,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'reconcileSpecialPrincipalWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/special_principals`,
        body,
        undefined,
        finalOptions
      );
    },
    findSpecialPrincipal: (
      tenant_id: string,
      connector_id: string,
      special_principal_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SpecialPrincipal>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findSpecialPrincipal',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/special_principals/${encodeURIComponent(String(special_principal_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileSpecialPrincipal: (
      tenant_id: string,
      connector_id: string,
      special_principal_id: string,
      body: SpecialPrincipal,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SpecialPrincipal>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSpecialPrincipal',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/special_principals/${encodeURIComponent(String(special_principal_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileSpecialPrincipalsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchSpecialPrincipalsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSpecialPrincipalsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/special_principals/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteTicketCommentByRef: (
      tenant_id: string,
      connector_id: string,
      query: { ticket_ref: string; comment_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteTicketCommentByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/ticket-comments` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listTicketComments: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageTicketComment>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listTicketComments',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/ticket-comments` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileTicketCommentWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: TicketComment,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileTicketCommentWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/ticket-comments`,
        body,
        undefined,
        finalOptions
      );
    },
    findTicketComment: (
      tenant_id: string,
      connector_id: string,
      ticket_comment_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<TicketComment>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findTicketComment',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/ticket-comments/${encodeURIComponent(String(ticket_comment_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileTicketComment: (
      tenant_id: string,
      connector_id: string,
      ticket_comment_id: string,
      body: TicketComment,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<TicketComment>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileTicketComment',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/ticket-comments/${encodeURIComponent(String(ticket_comment_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileTicketCommentsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchTicketCommentsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileTicketCommentsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/ticket-comments/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteTicketByRef: (
      tenant_id: string,
      connector_id: string,
      query: { ticket_ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteTicketByRef',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/tickets` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listTickets: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageTicket>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listTickets',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/tickets` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reconcileTicketWithoutID: (
      tenant_id: string,
      connector_id: string,
      body: Ticket,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityMetadata>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileTicketWithoutID',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/tickets`,
        body,
        undefined,
        finalOptions
      );
    },
    findTicket: (
      tenant_id: string,
      connector_id: string,
      ticket_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Ticket>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findTicket',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/tickets/${encodeURIComponent(String(ticket_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileTicket: (
      tenant_id: string,
      connector_id: string,
      ticket_id: string,
      body: Ticket,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Ticket>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileTicket',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/tickets/${encodeURIComponent(String(ticket_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    reconcileTicketsBatch: (
      tenant_id: string,
      connector_id: string,
      body: batchTicketsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchReconcileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileTicketsBatch',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/tickets/batch`,
        body,
        undefined,
        finalOptions
      );
    },
    getEntitlementAgentAccess: (
      tenant_id: string,
      query?: { ref?: string; limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentAccess>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntitlementAgentAccess',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/agent-access` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getEntitlementAgentCapabilityReach: (
      tenant_id: string,
      query: {
        ref: string;
        connector_id?: string;
        limit?: number;
        offset?: number;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentCapabilityReach>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'getEntitlementAgentCapabilityReach',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/agent-capability-reach` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getEntitlementAgentDeploymentReach: (
      tenant_id: string,
      query: {
        ref: string;
        connector_id?: string;
        limit?: number;
        offset?: number;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDeploymentReach>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'getEntitlementAgentDeploymentReach',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/agent-deployment-reach` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getEntitlementAgentIndirectAccess: (
      tenant_id: string,
      query: { ref: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentIndirectAccess>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'getEntitlementAgentIndirectAccess',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/agent-indirect-access` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listEntitlementClusters: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<clustersResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listEntitlementClusters',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/clusters`,
        undefined,
        finalOptions
      );
    },
    getEntitlementCluster: (
      tenant_id: string,
      cluster_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<clusterDetail>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntitlementCluster',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/clusters/${encodeURIComponent(String(cluster_id))}`,
        undefined,
        finalOptions
      );
    },
    getEntitlementIndirectAccess: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<IndirectAccess>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntitlementIndirectAccess',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/indirect-access` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getEntitlementNeighborhood: (
      tenant_id: string,
      query: {
        ref: string;
        label?: string;
        direction?: string;
        hops?: number;
        label_limit?: number;
        fanout?: number;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Neighborhood>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntitlementNeighborhood',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/neighborhood` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getEntitlementAccessPaths: (
      tenant_id: string,
      query?: { from?: string; to?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccessPaths>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntitlementAccessPaths',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/paths` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getEntitlementPrincipalAccess: (
      tenant_id: string,
      query?: { ref?: string; limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PrincipalAccess>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntitlementPrincipalAccess',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/principal-access` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getAccountAnalysis: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccountAnalysis>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAccountAnalysis',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/privilege` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    postPrivilegeFeedback: (
      tenant_id: string,
      body: feedbackRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<feedbackResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'postPrivilegeFeedback',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/privilege/feedback`,
        body,
        undefined,
        finalOptions
      );
    },
    getPrivilegeRanking: (
      tenant_id: string,
      query?: { kind?: string; verdict?: string; limit?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<rankingResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getPrivilegeRanking',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/privilege/ranking` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getEntitlementResourceAccess: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ResourceAccess>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntitlementResourceAccess',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/resource-access` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getEntitlementRoleAccess: (
      tenant_id: string,
      query?: { ref?: string; limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RoleAccess>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntitlementRoleAccess',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/role-access` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getEntitlementRoleReach: (
      tenant_id: string,
      query: {
        ref: string;
        connector_id?: string;
        limit?: number;
        offset?: number;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RoleReach>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getEntitlementRoleReach',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/entitlements/role-reach` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    postCatalogEvent: (
      tenant_id: string,
      log_name: string,
      body: catalogEventBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<BatchPublishEventResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'postCatalogEvent',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/events/${encodeURIComponent(String(log_name))}`,
        body,
        undefined,
        finalOptions
      );
    },
    listCatalogEventKinds: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<CatalogEventKindsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listCatalogEventKinds',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/events/kinds`,
        undefined,
        finalOptions
      );
    },
    searchCatalogEvents: (
      tenant_id: string,
      query?: {
        q?: string;
        actor_ref?: string;
        actor_email?: string;
        actor_upn?: string;
        person_ref?: string;
        target_ref?: string;
        campaign_ref?: string;
        review_ref?: string;
        ip_address?: string;
        session_id?: string;
        device_ref?: string;
        risk_level?: string;
        privileged?: boolean;
        kinds?: string;
        spaces?: string;
        connector_ids?: string;
        outcome_result?: string;
        target_types?: Array<string>;
        target_rel?: Array<string>;
        event_ids?: string;
        from?: string;
        to?: string;
        since?: string;
        limit?: number;
        cursor?: string;
        include_facets?: boolean;
        count_only?: boolean;
        hydrate?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EventSearchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'searchCatalogEvents',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/events/search` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getCatalogEventStats: (
      tenant_id: string,
      query?: {
        from?: string;
        to?: string;
        bucket?: string;
        group_by?: string;
        q?: string;
        actor_ref?: string;
        actor_email?: string;
        actor_upn?: string;
        person_ref?: string;
        target_ref?: string;
        ip_address?: string;
        session_id?: string;
        device_ref?: string;
        risk_level?: string;
        event_ids?: string;
        kinds?: string;
        spaces?: string;
        connector_ids?: string;
        outcome_result?: string;
        target_types?: Array<string>;
        target_rel?: Array<string>;
        privileged?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EventStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getCatalogEventStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/events/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    exploreSearchCatalog: (
      tenant_id: string,
      query: {
        q: string;
        limit?: number;
        cursor?: string;
        counts?: boolean;
        counts_only?: boolean;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SearchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'exploreSearchCatalog',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/explore-search` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listGroupsTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        search?: string;
        filter?: Array<string>;
        ref?: string;
        connector_id?: string;
        include_system?: boolean;
        facets?: Array<string>;
        hydrate?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagegroupRow>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupsTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/groups` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listApplicationsForGroup: (
      tenant_id: string,
      group_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplication>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationsForGroup',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/groups/${encodeURIComponent(String(group_id))}/applications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listLicensesForGroup: (
      tenant_id: string,
      group_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageLicense>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listLicensesForGroup',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/groups/${encodeURIComponent(String(group_id))}/licenses` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listMembersForGroup: (
      tenant_id: string,
      group_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listMembersForGroup',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/groups/${encodeURIComponent(String(group_id))}/members` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listPermissionsForGroup: (
      tenant_id: string,
      group_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPermissionsForGroup',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/groups/${encodeURIComponent(String(group_id))}/permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getGroupProfile: (
      tenant_id: string,
      group_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<GroupProfile>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getGroupProfile',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/groups/${encodeURIComponent(String(group_id))}/profile`,
        undefined,
        finalOptions
      );
    },
    listRolesForGroup: (
      tenant_id: string,
      group_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRolesForGroup',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/groups/${encodeURIComponent(String(group_id))}/roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    computeGroupsEntityStats: (
      tenant_id: string,
      body: EntityStatsBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsBatchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'computeGroupsEntityStats',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/groups/stats`,
        body,
        undefined,
        finalOptions
      );
    },
    getGroupsEntityStats: (
      tenant_id: string,
      query?: {
        agg?: string;
        value?: string;
        group_by?: string;
        buckets?: string;
        filter?: Array<string>;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getGroupsEntityStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/groups/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listActivityLogs: (
      tenant_id: string,
      query?: {
        connector_ids?: string;
        space?: string;
        from?: string;
        to?: string;
        kinds?: string;
        target_ref?: string;
        distinct_id?: string;
        limit?: number;
        next?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ActivityLogPage>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listActivityLogs',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/logs` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    deleteOwnerRule: (
      tenant_id: string,
      query?: { rule_id?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OwnerRulesResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteOwnerRule',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/owners/rules` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listOwnerRules: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OwnerRulesResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listOwnerRules',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/owners/rules`,
        undefined,
        finalOptions
      );
    },
    putOwnerRule: (
      tenant_id: string,
      body: OwnerRuleRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OwnerRulesResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'putOwnerRule',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/owners/rules`,
        body,
        undefined,
        finalOptions
      );
    },
    listOwnerRuleFields: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OwnerRuleFieldsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listOwnerRuleFields',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/owners/rules/fields`,
        undefined,
        finalOptions
      );
    },
    previewOwnerRule: (
      tenant_id: string,
      body: OwnerPreviewRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OwnerPreviewResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'previewOwnerRule',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/owners/rules/preview`,
        body,
        undefined,
        finalOptions
      );
    },
    validateOwnerRule: (
      tenant_id: string,
      body: OwnerRuleRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OwnerRuleValidateResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'validateOwnerRule',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/owners/rules/validate`,
        body,
        undefined,
        finalOptions
      );
    },
    listPersonsTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        search?: string;
        filter?: Array<string>;
        ref?: string;
        connector_id?: string;
        include_system?: boolean;
        facets?: Array<string>;
        hydrate?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagepersonRow>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPersonsTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/persons` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listAccountsForPerson: (
      tenant_id: string,
      person_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountsForPerson',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/persons/${encodeURIComponent(String(person_id))}/accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listDirectReportsForPerson: (
      tenant_id: string,
      person_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listDirectReportsForPerson',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/persons/${encodeURIComponent(String(person_id))}/direct-reports` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getPersonOwnership: (
      tenant_id: string,
      person_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PersonOwnership>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getPersonOwnership',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/persons/${encodeURIComponent(String(person_id))}/ownership`,
        undefined,
        finalOptions
      );
    },
    getPersonProfile: (
      tenant_id: string,
      person_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PersonProfile>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getPersonProfile',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/persons/${encodeURIComponent(String(person_id))}/profile`,
        undefined,
        finalOptions
      );
    },
    listRolesForPerson: (
      tenant_id: string,
      person_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageRole>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRolesForPerson',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/persons/${encodeURIComponent(String(person_id))}/roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    computePersonsEntityStats: (
      tenant_id: string,
      body: EntityStatsBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsBatchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'computePersonsEntityStats',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/persons/stats`,
        body,
        undefined,
        finalOptions
      );
    },
    getPersonsEntityStats: (
      tenant_id: string,
      query?: {
        agg?: string;
        value?: string;
        group_by?: string;
        buckets?: string;
        filter?: Array<string>;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getPersonsEntityStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/persons/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    executeQuery: (
      tenant_id: string,
      name: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<QueryResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'executeQuery',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/queries/${encodeURIComponent(String(name))}`,
        undefined,
        finalOptions
      );
    },
    executeQueryWithBody: (
      tenant_id: string,
      name: string,
      body: QueryRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<QueryResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'executeQueryWithBody',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/queries/${encodeURIComponent(String(name))}`,
        body,
        undefined,
        finalOptions
      );
    },
    assertRelationship: (
      tenant_id: string,
      body: relationshipDecisionRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<relationshipDecisionResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'assertRelationship',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/assert`,
        body,
        undefined,
        finalOptions
      );
    },
    clearRelationshipDecision: (
      tenant_id: string,
      body: relationshipDecisionRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<relationshipDecisionResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'clearRelationshipDecision',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/clear-decision`,
        body,
        undefined,
        finalOptions
      );
    },
    getHeldAccounts: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<HeldAccountsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getHeldAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/held-accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getAccountImpersonation: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ImpersonationResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAccountImpersonation',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/impersonation` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getLinkedAccounts: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<LinkedAccountsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getLinkedAccounts',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/linked-accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getPersonManagement: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ManagementResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getPersonManagement',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/management` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getOwnerManagerChain: (
      tenant_id: string,
      query?: { ref?: string; depth?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OwnerManagerChainResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getOwnerManagerChain',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/owner-manager-chain` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getAccountOwners: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<OwnersResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getAccountOwners',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/owners` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    deleteRelationshipRule: (
      tenant_id: string,
      query?: { rule_id?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RelationshipRulesResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteRelationshipRule',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/rules` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    listRelationshipRules: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RelationshipRulesResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRelationshipRules',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/rules`,
        undefined,
        finalOptions
      );
    },
    putRelationshipRule: (
      tenant_id: string,
      body: RelationshipRuleOverlayRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RelationshipRulesResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'putRelationshipRule',
      };
      return client.put(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/rules`,
        body,
        undefined,
        finalOptions
      );
    },
    previewRelationshipRules: (
      tenant_id: string,
      body: RelationshipPreviewRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RelationshipPreviewResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'previewRelationshipRules',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/rules/preview`,
        body,
        undefined,
        finalOptions
      );
    },
    previewRelationshipSelector: (
      tenant_id: string,
      body: RelationshipSelectorPreviewRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RelationshipSelectorPreviewResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'previewRelationshipSelector',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/rules/selector-preview`,
        body,
        undefined,
        finalOptions
      );
    },
    validateRelationshipRule: (
      tenant_id: string,
      body: RelationshipRuleValidateRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RelationshipRuleValidateResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'validateRelationshipRule',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/rules/validate`,
        body,
        undefined,
        finalOptions
      );
    },
    getVaultedCredentials: (
      tenant_id: string,
      query?: { ref?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<VaultedCredentialsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getVaultedCredentials',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/vaulted-credentials` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    vetoRelationship: (
      tenant_id: string,
      body: relationshipDecisionRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<relationshipDecisionResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'vetoRelationship',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/relationships/veto`,
        body,
        undefined,
        finalOptions
      );
    },
    listResourcesTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        search?: string;
        filter?: Array<string>;
        ref?: string;
        connector_id?: string;
        include_system?: boolean;
        facets?: Array<string>;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageresourceRow>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listResourcesTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/resources` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listRolesTenantWide: (
      tenant_id: string,
      query?: {
        limit?: number;
        offset?: number;
        sort?: string;
        sort_dir?: string;
        search?: string;
        filter?: Array<string>;
        ref?: string;
        connector_id?: string;
        include_system?: boolean;
        facets?: Array<string>;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageroleRow>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRolesTenantWide',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listAccountsForRole: (
      tenant_id: string,
      role_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageAccount>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAccountsForRole',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}/accounts` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listApplicationsForRole: (
      tenant_id: string,
      role_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageApplication>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listApplicationsForRole',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}/applications` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listGroupsForRole: (
      tenant_id: string,
      role_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageGroup>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listGroupsForRole',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}/groups` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getRoleOverlapAnalysis: (
      tenant_id: string,
      role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<roleOverlapAnalysisResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getRoleOverlapAnalysis',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}/overlap-analysis`,
        undefined,
        finalOptions
      );
    },
    listPermissionsForRole: (
      tenant_id: string,
      role_id: string,
      query?: { limit?: number; offset?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPagePermission>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listPermissionsForRole',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}/permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getRoleProfile: (
      tenant_id: string,
      role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RoleProfile>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getRoleProfile',
      };
      return client.get(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}/profile`,
        undefined,
        finalOptions
      );
    },
    computeRolesEntityStats: (
      tenant_id: string,
      body: EntityStatsBatchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsBatchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'computeRolesEntityStats',
      };
      return client.post(
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/stats`,
        body,
        undefined,
        finalOptions
      );
    },
    getRolesEntityStats: (
      tenant_id: string,
      query?: {
        agg?: string;
        value?: string;
        group_by?: string;
        buckets?: string;
        filter?: Array<string>;
        include_system?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityStatsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getRolesEntityStats',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/stats` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    searchCatalog: (
      tenant_id: string,
      query?: { filter?: string; limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EntityPageSearchResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'searchCatalog',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/catalog/v1/tenants/${encodeURIComponent(String(tenant_id))}/search` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
  };
}

/** AccessPath schema */
export interface AccessPath {
  edges?: Array<PathEdge>;
  kind?: 'grant' | 'denied';
  nodes?: Array<PathNode>;
}

/** AccessPaths schema */
export interface AccessPaths {
  from?: PrincipalRef;
  paths?: Array<AccessPath>;
  to?: ResourceInfo;
}

/** AccessReview schema */
export interface AccessReview {
  _metadata?: EntityMetadata;
  /** Reference to the reviewed account */
  account_ref?: string;
  /** Account refs assigned to review this item (links pending items to reviewers) */
  assigned_reviewers?: Array<string>;
  /** Reference to the parent campaign */
  campaign_ref?: string;
  /** When the decision was made */
  decided_at?: string;
  /** Reference to the application/endpoint the entitlement belongs to */
  endpoint_ref?: string;
  /** Kind of entitlement reviewed (e.g. role, group, permission) */
  entitlement_type?: string;
  /** Identifier or name of the reviewed entitlement */
  entitlement_value?: string;
  /** Reviewer comment or reason for the decision */
  justification?: string;
  /** Decision: Approved, Revoked, Flagged, Modified, Pending, Unknown */
  result?: string;
  /** Stable per-item identifier in the source system (unique within the campaign) */
  review_ref?: string;
  /** Account ref of the reviewer who decided this item */
  reviewer_ref?: string;
  /** System that produced this review (e.g. control, saviynt, sailpoint) */
  source_system?: string;
}

/** Account schema */
export interface Account {
  _metadata?: EntityMetadata;
  /** Unique reference for this account */
  account_ref?: string;
  /** Type of account (User, Service, Guest, etc.) */
  account_type?: string;
  alternate_emails?: Array<Email>;
  alternate_phones?: Array<Phone>;
  /** Timestamp when the account was created in the source system */
  created_at?: string;
  /** Human-readable description of the account */
  description?: string;
  /** Timestamp when the account was disabled in the source system */
  disabled_date?: string;
  /** Display name for UI presentation */
  display_name?: string;
  /** Whether the account is enabled and active */
  enabled?: boolean;
  /** First name (given name) of the account holder */
  first_name?: string;
  /** Network location (host, FQDN, domain, IP, or URL) the account exists at in the source system; with Name it is the correlation identity of an account that has no UPN */
  host_address?: string;
  /** Most recent login timestamp from the source system */
  last_login_date?: string;
  /** Last name (surname) of the account holder */
  last_name?: string;
  /** Middle name of the account holder */
  middle_name?: string;
  /** Full name or principal name of the account */
  name?: string;
  primary_email?: Email;
  primary_phone?: Phone;
  /** Account status (enabled/disabled; extensible) */
  status?: string;
  /** User principal name (login/UPN) in the source system */
  upn?: string;
}

/** AccountAnalysis schema */
export interface AccountAnalysis {
  DetectionRow?: DetectionRow;
  feedback?: string;
}

/** AccountAttribute schema */
export interface AccountAttribute {
  _metadata?: EntityMetadata;
  /** Reference to the account this attribute value belongs to */
  account_ref?: string;
  /** Reference to the attribute definition */
  attribute_ref?: string;
  /** Type hint for parsing the value (e.g. string, int, bool, datetime) */
  type?: string;
  /** Value of the attribute for this account */
  value?: string;
}

/** AccountClassification schema */
export interface AccountClassification {
  _metadata?: EntityMetadata;
  /** Reference to the classified account */
  account_ref?: string;
  /** Reference to the classification definition */
  classification_ref?: string;
  /** Confidence of the classification assignment (0..1) */
  confidence?: number;
}

/** AccountClassificationsResponse schema */
export interface AccountClassificationsResponse {
  account?: Endpoint;
  classifications?: Array<ClassificationRow>;
  projection_enabled?: boolean;
}

/** AccountExtension schema */
export interface AccountExtension {
  _metadata?: EntityMetadata;
  /** Reference to the account this extension belongs to */
  account_ref?: string;
  attributes?: Array<AttributeEntry>;
  classifications?: Array<ClassificationEntry>;
  permissions?: Array<PermissionEntry>;
  risk_factors?: Array<RiskFactorEntry>;
}

/** AccountLicense schema */
export interface AccountLicense {
  _metadata?: EntityMetadata;
  /** Reference to the account this license is assigned to */
  account_ref?: string;
  /** How the assignment is held: direct or group (group-based licensing) */
  assigned_via?: string;
  /** List of service plans disabled for this license assignment */
  disabled_service_plans?: Array<string>;
  /** List of service plans enabled for this license assignment */
  enabled_service_plans?: Array<string>;
  /** Reference to the license being assigned */
  license_ref?: string;
  /** Group that confers the license when assigned_via is group */
  source_group_ref?: string;
  /** Provider assignment state (e.g. Active, Error) */
  status?: string;
  /** Provider error detail when the assignment state is an error */
  status_error?: string;
}

/** AccountManager schema */
export interface AccountManager {
  _metadata?: EntityMetadata;
  /** AccountRef of the account being managed, within the same connector's accounts space */
  account_ref?: string;
  /** AccountRef of the manager's account, within the same connector's accounts space */
  manager_account_ref?: string;
}

/** AccountMultiFactor schema */
export interface AccountMultiFactor {
  _metadata?: EntityMetadata;
  /** Reference to the enrolled account */
  account_ref?: string;
  /** When the account enrolled in the method */
  created_at?: string;
  /** Provider identifier of the concrete enrollment or device, when the provider has one; part of the entity identity */
  instance_ref?: string;
  /** Reference to the multi-factor method */
  multi_factor_ref?: string;
  /** State of this account's enrollment in the method */
  status?:
    | 'UNKNOWN'
    | 'NOT_ENROLLED'
    | 'PENDING'
    | 'ENABLED'
    | 'DISABLED'
    | 'EXPIRED';
}

/** AccountPermission schema */
export interface AccountPermission {
  _metadata?: EntityMetadata;
  /** Reference to the account granted this permission */
  account_ref?: string;
  /** Reference to the permission being granted */
  permission_ref?: string;
}

/** AccountPolicy schema */
export interface AccountPolicy {
  _metadata?: EntityMetadata;
  /** AccountRef of the governed account */
  account_ref?: string;
  /** PolicyRef of the governing policy */
  policy_ref?: string;
}

/** AccountProfile schema */
export interface AccountProfile {
  account?: Account;
  applications?: ProfileBlockApplication;
  attributes?: Array<AttributeValue>;
  classifications?: ProfileBlockClassification;
  groups?: ProfileBlockGroup;
  licenses?: ProfileBlockLicense;
  manager?: Account;
  multi_factors?: ProfileBlockMultiFactor;
  permissions?: ProfileBlockPermission;
  persons?: ProfileBlockPerson;
  roles?: ProfileBlockRole;
}

/** AccountRiskFactor schema */
export interface AccountRiskFactor {
  _metadata?: EntityMetadata;
  /** Reference to the account this risk factor applies to */
  account_ref?: string;
  /** Confidence that the risk factor applies (0..1) */
  confidence?: number;
  /** Reference to the risk-factor definition */
  risk_factor_ref?: string;
}

/** AccountRole schema */
export interface AccountRole {
  _metadata?: EntityMetadata;
  /** Reference to the account assigned this role */
  account_ref?: string;
  /** Reference to the role being assigned */
  role_ref?: string;
}

/** AccountSecret schema */
export interface AccountSecret {
  _metadata?: EntityMetadata;
  /** AccountRef of the owning account */
  account_ref?: string;
  /** SecretRef of the owned secret */
  secret_ref?: string;
}

/** ActivityLogPage schema */
export interface ActivityLogPage {
  models?: Array<Envelope>;
  next?: string;
  total?: number;
  truncated?: boolean;
}

/** Agent schema */
export interface Agent {
  _metadata?: EntityMetadata;
  /** Primary key for this agent in the source system */
  agent_ref?: string;
  /** How the agent is driven */
  agent_type?: 'unknown' | 'assistant' | 'autonomous' | 'bot' | 'workflow';
  /** End-user authentication posture the agent presents */
  auth_mode?: 'unknown' | 'none' | 'integrated' | 'custom';
  /** When the agent was created in the source system */
  created_at?: string;
  /** Description of what the agent does */
  description?: string;
  /** Whether the agent is active/published rather than disabled or draft */
  enabled?: boolean;
  /** Foundation-model identifier as reported by the platform */
  model_name?: string;
  /** Foundation-model provider (e.g. anthropic, openai, google) */
  model_provider?: string;
  /** Foundation-model version, where reported separately */
  model_version?: string;
  /** Display name of the agent */
  name?: string;
  /** The platform's single agent-level invocation endpoint, URL, or ARN, where one exists; per-deployment endpoints are AgentDeployment.Target */
  runtime_endpoint?: string;
  /** Platform-native status verbatim (e.g. PREPARED, published) */
  status?: string;
  /** When the agent was last modified in the source system */
  updated_at?: string;
  /** Current version of the agent definition, where the platform versions agents; the version a deployment routes to is AgentDeployment.AgentVersion */
  version?: string;
}

/** AgentAccess schema */
export interface AgentAccess {
  agent?: AgentNode;
  capabilities?: Array<AgentCapabilityNode>;
  hosting_application_details?: Array<NamedRef>;
  hosting_applications?: Array<string>;
  owner_accounts?: Array<NamedRef>;
  owners?: Array<string>;
  runtime_accounts?: Array<AgentRuntimeAccess>;
}

/** AgentAccount schema */
export interface AgentAccount {
  _metadata?: EntityMetadata;
  /** AccountRef of the runtime identity the agent runs as */
  account_ref?: string;
  /** AgentRef of the agent */
  agent_ref?: string;
  /** Kind of runtime identity the agent runs as */
  identity_type?:
    | 'unknown'
    | 'service_role'
    | 'service_account'
    | 'service_principal'
    | 'agent_user'
    | 'isu'
    | 'robot_account'
    | 'instance_connection';
  /** Whether the agent executes as the invoking user rather than a fixed identity */
  is_run_as_user?: boolean;
}

/** AgentCapability schema */
export interface AgentCapability {
  _metadata?: EntityMetadata;
  /** AgentRef of the agent */
  agent_ref?: string;
  /** Stable platform-native identifier for this capability */
  capability_ref?: string;
  /** Kind of capability */
  capability_type?:
    | 'unknown'
    | 'action'
    | 'tool'
    | 'topic'
    | 'skill'
    | 'knowledge_base'
    | 'flow'
    | 'action_group'
    | 'collaborator';
  /** Description of what the capability does */
  description?: string;
  /** Reference to what executes the capability (e.g. Lambda ARN, flow API name) */
  implementation_ref?: string;
  /** Display name of the capability */
  name?: string;
  /** I/O contract (OpenAPI or function schema JSON) verbatim, where the platform exposes one */
  schema?: string;
  /** Reference to what the capability acts on */
  target_resource_ref?: string;
}

/** AgentCapabilityNode schema */
export interface AgentCapabilityNode {
  capability_type?: string;
  implementation_ref?: string;
  name?: string;
  ref?: string;
  target_resource_ref?: string;
}

/** AgentCapabilityReach schema */
export interface AgentCapabilityReach {
  agent?: AgentNode;
  limit?: number;
  offset?: number;
  reach?: Array<AgentCapabilityReachEntry>;
  total_count?: number;
}

/** AgentCapabilityReachEntry schema */
export interface AgentCapabilityReachEntry {
  capability?: AgentCapabilityNode;
  resource?: ResourceInfo;
}

/** AgentDeployment schema */
export interface AgentDeployment {
  _metadata?: EntityMetadata;
  /** AgentRef of the deployed agent */
  agent_ref?: string;
  /** Agent version the deployment is pinned to; empty when the platform has no versions */
  agent_version?: string;
  /** When the deployment was created */
  created_at?: string;
  /** Connector-unique id of the deployment (alias ARN, endpoint ARN, deployment id); never an id that repeats per agent */
  deployment_ref?: string;
  /** How the deployment exposes the agent */
  deployment_type?:
    | 'unknown'
    | 'endpoint'
    | 'alias'
    | 'channel'
    | 'schedule'
    | 'published';
  /** Human-readable description of the deployment */
  description?: string;
  /** When the deployment last ran, if the platform reports it */
  last_run_at?: string;
  /** Human-readable name of the deployment */
  name?: string;
  /** When the deployment next runs, for scheduled deployments */
  next_run_at?: string;
  /** Lifecycle state as the platform reports it */
  status?: 'unknown' | 'active' | 'paused' | 'archived' | 'failed';
  /** Platform reason for the status, e.g. manual or agent_archived_error */
  status_reason?: string;
  /** Where the deployment runs or is reached: endpoint, ARN, URL, channel, or environment ref */
  target?: string;
  /** What starts a run: invocation, channel:<name>, or cron:<5-field POSIX expression>@<IANA timezone> (no macros, so the single @ splits it) */
  trigger?: string;
  /** When the deployment was last updated */
  updated_at?: string;
}

/** AgentDeploymentAttribute schema */
export interface AgentDeploymentAttribute {
  _metadata?: EntityMetadata;
  /** Reference to the attribute definition */
  attribute_ref?: string;
  /** Reference to the deployment this attribute value belongs to */
  deployment_ref?: string;
  /** Type hint for parsing the value (e.g. string, int, bool, datetime) */
  type?: string;
  /** Value of the attribute for this deployment */
  value?: string;
}

/** AgentDeploymentExtension schema */
export interface AgentDeploymentExtension {
  _metadata?: EntityMetadata;
  attributes?: Array<AttributeEntry>;
  /** Reference to the deployment this extension belongs to */
  deployment_ref?: string;
}

/** AgentDeploymentNode schema */
export interface AgentDeploymentNode {
  connector_id?: string;
  deployment_type?: string;
  name?: string;
  ref?: string;
  status?: string;
  target?: string;
  trigger?: string;
}

/** AgentDeploymentReach schema */
export interface AgentDeploymentReach {
  agent?: AgentNode;
  limit?: number;
  offset?: number;
  reach?: Array<AgentDeploymentReachEntry>;
  total_count?: number;
}

/** AgentDeploymentReachEntry schema */
export interface AgentDeploymentReachEntry {
  deployment?: AgentDeploymentNode;
  relationship?: 'runs_in' | 'uses';
  resource?: ResourceInfo;
  retrieval?: PermissionRef;
}

/** AgentDeploymentResource schema */
export interface AgentDeploymentResource {
  _metadata?: EntityMetadata;
  /** DeploymentRef of the deployment */
  deployment_ref?: string;
  /** How the deployment relates to the resource */
  relationship?: 'unknown' | 'runs_in' | 'uses';
  /** ResourceRef of the resource */
  resource_ref?: string;
}

/** AgentIndirectAccess schema */
export interface AgentIndirectAccess {
  agent?: AgentNode;
  grants?: Array<AgentIndirectGrant>;
  total_count?: number;
}

/** AgentIndirectGrant schema */
export interface AgentIndirectGrant {
  assignment_type?: string;
  permissions?: Array<PermissionRef>;
  resource?: ResourceInfo;
  via?: AgentIndirectVia;
}

/** AgentIndirectVia schema */
export interface AgentIndirectVia {
  deployment?: AgentDeploymentNode;
  retrieval?: PermissionRef;
  safe?: NamedRef;
  secret?: NamedRef;
  target_account?: PrincipalRef;
}

/** AgentNode schema */
export interface AgentNode {
  agent_type?: string;
  connector_id?: string;
  enabled?: boolean;
  name?: string;
  ref?: string;
}

/** AgentOwner schema */
export interface AgentOwner {
  _metadata?: EntityMetadata;
  /** AgentRef of the agent */
  agent_ref?: string;
  /** AccountRef of the owning account (owner, creator, or shared editor) */
  owner_account_ref?: string;
  /** Where the edge comes from: owner (platform-recorded), creator (inferred from the creation audit record), editor (platform-recorded shared editor) */
  ownership_kind?: 'owner' | 'creator' | 'editor';
}

/** AgentProfile schema */
export interface AgentProfile {
  agent?: Agent;
  applications?: Array<Application>;
  capabilities?: Array<AgentCapability>;
  owners?: Array<Account>;
  runtimeAccounts?: Array<AgentRuntimeAccount>;
}

/** AgentRuntimeAccess schema */
export interface AgentRuntimeAccess {
  access?: PrincipalAccess;
  account_ref?: string;
  identity_type?: string;
  is_run_as_user?: boolean;
}

/** AgentRuntimeAccount schema */
export interface AgentRuntimeAccount {
  account?: Account;
  identity_type?: string;
  is_run_as_user?: boolean;
}

/** AnomalyResult schema */
export interface AnomalyResult {
  account_ref?: string;
  explanation?: Explanation;
  nearest_cluster_id?: string;
  score?: number;
  verdict?: string;
}

/** Application schema */
export interface Application {
  _metadata?: EntityMetadata;
  /** Primary key for this application in the source system */
  application_ref?: string;
  /** Human-readable description of the application */
  description?: string;
  /** Name of the application or system */
  name?: string;
}

/** ApplicationAccount schema */
export interface ApplicationAccount {
  _metadata?: EntityMetadata;
  /** Reference to the account in this application */
  account_ref?: string;
  /** Reference to the application */
  application_ref?: string;
}

/** ApplicationAgent schema */
export interface ApplicationAgent {
  _metadata?: EntityMetadata;
  /** AgentRef of the hosted agent */
  agent_ref?: string;
  /** ApplicationRef of the hosting platform */
  application_ref?: string;
}

/** ApplicationExtension schema */
export interface ApplicationExtension {
  _metadata?: EntityMetadata;
  /** Reference to the application this extension belongs to */
  application_ref?: string;
  attributes?: Array<AttributeEntry>;
}

/** ApplicationGroup schema */
export interface ApplicationGroup {
  _metadata?: EntityMetadata;
  /** Reference to the application */
  application_ref?: string;
  /** Reference to the group in this application */
  group_ref?: string;
}

/** ApplicationPermission schema */
export interface ApplicationPermission {
  _metadata?: EntityMetadata;
  /** Reference to the application */
  application_ref?: string;
  /** Reference to the permission in this application */
  permission_ref?: string;
}

/** ApplicationRole schema */
export interface ApplicationRole {
  _metadata?: EntityMetadata;
  /** Reference to the application */
  application_ref?: string;
  /** Reference to the role in this application */
  role_ref?: string;
}

/** ApplicationWithCount schema */
export interface ApplicationWithCount {
  Application?: Application;
  total_accounts?: number;
}

/** ApplyCatalogChanges schema */
export interface ApplyCatalogChanges {
  connector_id: string;
  deletes?: Array<CatalogDeleteKey>;
  max_lag?: number;
  tenant_id: string;
  upserts?: Array<Envelope>;
}

/** Attribute schema */
export interface Attribute {
  _metadata?: EntityMetadata;
  /** Stable external identifier for this attribute (the attribute key, e.g. 'az-custom-field') */
  attribute_ref?: string;
  /** Human-readable description of the attribute */
  description?: string;
  /** Human-readable name of the attribute */
  name?: string;
}

/** AttributeEntry schema */
export interface AttributeEntry {
  /** Human-readable name of the attribute */
  name?: string;
  /** Stable external identifier for the attribute (the key) */
  ref?: string;
  /** Type hint for parsing the value (e.g. string, int, bool, datetime) */
  type?: string;
  /** Value of the attribute for this parent */
  value?: string;
}

/** AttributeKey schema */
export interface AttributeKey {
  /** Source-system attribute key, usable verbatim as attr:<Key> */
  attribute_ref?: string;
  /** Connector that collected the attribute */
  connector_id?: string;
  /** Description from the attributes dictionary; may be empty */
  description?: string;
  /** Distinct entities of this kind carrying the key */
  entity_count?: number;
  /** Display name from the attributes dictionary; may be empty */
  name?: string;
  /** Modal per-row type hint; empty when no collected row carries one */
  type?: string;
}

/** AttributeKeysResult schema */
export interface AttributeKeysResult {
  accounts?: Array<AttributeKey>;
  applications?: Array<AttributeKey>;
  groups?: Array<AttributeKey>;
  persons?: Array<AttributeKey>;
}

/** AttributeValue schema */
export interface AttributeValue {
  name?: string;
  type?: string;
  value?: string;
}

/** BatchPublishEventResponse schema */
export interface BatchPublishEventResponse {
  results?: Array<BatchPublishEventResultItem>;
}

/** BatchPublishEventResultItem schema */
export interface BatchPublishEventResultItem {
  error?: string;
  event_id?: string;
}

/** BatchReconcileResponse schema */
export interface BatchReconcileResponse {
  results?: Array<BatchResultItem>;
}

/** BatchResultItem schema */
export interface BatchResultItem {
  applied?: boolean;
  error?: string;
  metadata?: EntityMetadata;
}

/** Campaign schema */
export interface Campaign {
  _metadata?: EntityMetadata;
  /** Stable campaign identifier in the source system */
  campaign_ref?: string;
  /** What is being certified: GROUP, ROLE, MANAGER, APPLICATION, PLATFORM, CLASSIFICATION, QUERY, UNKNOWN */
  campaign_type?: string;
  /** Free-text description of the campaign */
  description?: string;
  /** Review window end */
  end_date?: string;
  /** Display name of the campaign */
  name?: string;
  /** Account ref of the campaign owner */
  owner_ref?: string;
  /** Who approves items: manager, owner, custom, hybrid, unknown */
  reviewer_type?: string;
  /** System that produced this campaign (e.g. control, saviynt, sailpoint) */
  source_system?: string;
  /** Review window start */
  start_date?: string;
  /** Lifecycle state: DRAFT, STARTING, ACTIVE, COMPLETED, CANCELLED, UNKNOWN */
  status?: string;
}

/** CatalogApplyResponse schema */
export interface CatalogApplyResponse {
  deletes_applied?: number;
  deletes_skipped_absent?: number;
  upserts_applied?: number;
  upserts_unchanged?: number;
}

/** CatalogDeleteKey schema */
export interface CatalogDeleteKey {
  distinct_id?: string;
  ref?: string;
  space: string;
}

/** CatalogEventKind schema */
export interface CatalogEventKind {
  /** Polymorphic discriminator, accepted as $type on POST .../events/{log_name} */
  kind?: string;
  /** Human-readable name derived from the discriminator */
  name?: string;
  /** Where the kind is defined: mesh-sdk (connector-emitted) or mesh-core (platform-internal) */
  source?: string;
  /** Catalog space the event routes to; empty when the space is derived from the entity the event carries (entity_patched, entity_removed) */
  space?: string;
}

/** CatalogEventKindsResponse schema */
export interface CatalogEventKindsResponse {
  /** Number of kinds in kinds[] */
  count?: number;
  kinds?: Array<CatalogEventKind>;
  /** Short digest of the kind set; changes only when the set changes, so clients can cache the taxonomy and detect skew across builds */
  registry_hash?: string;
}

/** CatalogProjectorStatusResult schema */
export interface CatalogProjectorStatusResult {
  head_sequence?: number;
  next_sequence?: number;
  pending?: number;
  ready?: boolean;
  retry_after_seconds?: number;
}

/** Channel schema */
export interface Channel {
  _metadata?: EntityMetadata;
  /** Whether this channel is archived */
  archived?: boolean;
  /** Primary key for this Channel in the source system */
  channel_ref?: string;
  /** Human-readable description of the Channel */
  description?: string;
  /** Name of the Channel or system */
  name?: string;
  /** Whether this channel is private */
  private?: boolean;
}

/** ChannelAccount schema */
export interface ChannelAccount {
  _metadata?: EntityMetadata;
  /** Reference to the account in this Channel */
  account_ref?: string;
  /** Reference to the Channel */
  channel_ref?: string;
}

/** Classification schema */
export interface Classification {
  _metadata?: EntityMetadata;
  /** Stable external identifier for this classification */
  classification_ref?: string;
  /** Human-readable description of the classification */
  description?: string;
  /** Human-readable name of the classification */
  name?: string;
}

/** ClassificationEntry schema */
export interface ClassificationEntry {
  /** Confidence of the classification assignment (0..1) */
  confidence?: number;
  /** Human-readable name of the classification */
  name?: string;
  /** Stable external identifier for the classification */
  ref?: string;
}

/** ClassificationFieldsResponse schema */
export interface ClassificationFieldsResponse {
  accepts_other_names?: boolean;
  classifications_enabled?: boolean;
  fields?: Array<string>;
}

/** ClassificationLabel schema */
export interface ClassificationLabel {
  description?: string;
  name?: string;
  ref?: string;
}

/** ClassificationPreviewLabel schema */
export interface ClassificationPreviewLabel {
  classification_ref?: string;
  connector?: string;
  origin?: string;
}

/** ClassificationPreviewMatch schema */
export interface ClassificationPreviewMatch {
  account_ref?: string;
  labels?: Array<ClassificationPreviewLabel>;
}

/** ClassificationPreviewRequest schema */
export interface ClassificationPreviewRequest {
  limit?: number;
  rule?: ClassificationRule;
}

/** ClassificationPreviewResponse schema */
export interface ClassificationPreviewResponse {
  advisory?: boolean;
  classifications_enabled?: boolean;
  evaluated?: number;
  matched?: number;
  matches?: Array<ClassificationPreviewMatch>;
  truncated?: boolean;
}

/** ClassificationProvenance schema */
export interface ClassificationProvenance {
  actor?: string;
  applied_at?: string;
  asserted_by?: string;
  origin?: string;
  reason?: string;
  rule?: string;
}

/** ClassificationRow schema */
export interface ClassificationRow {
  ClassificationLabel?: ClassificationLabel;
  ClassificationProvenance?: ClassificationProvenance;
}

/** ClassificationRule schema */
export interface ClassificationRule {
  applies_collected?: boolean;
  applies_to_everything?: boolean;
  classification_ref?: string;
  conditions?: Array<Predicate>;
  description?: string;
  enabled?: boolean;
  id?: string;
  name?: string;
}

/** ClassificationRuleProblem schema */
export interface ClassificationRuleProblem {
  message?: string;
  path?: string;
}

/** ClassificationRuleRequest schema */
export interface ClassificationRuleRequest {
  rule?: ClassificationRule;
}

/** ClassificationRuleValidateRequest schema */
export interface ClassificationRuleValidateRequest {
  rule?: ClassificationRule;
}

/** ClassificationRuleValidateResponse schema */
export interface ClassificationRuleValidateResponse {
  classifications_enabled?: boolean;
  problems?: Array<ClassificationRuleProblem>;
  set_problems?: Array<ClassificationRuleProblem>;
  valid?: boolean;
}

/** ClassificationRuleView schema */
export interface ClassificationRuleView {
  definition?: ClassificationRule;
  id?: string;
  source?: string;
}

/** ClassificationRulesResponse schema */
export interface ClassificationRulesResponse {
  classifications_enabled?: boolean;
  load_problems?: Array<ClassificationRuleProblem>;
  rules?: Array<ClassificationRuleView>;
}

/** ClassifiedAccountRow schema */
export interface ClassifiedAccountRow {
  ClassificationProvenance?: ClassificationProvenance;
  account?: Endpoint;
}

/** ClassifiedAccountsResponse schema */
export interface ClassifiedAccountsResponse {
  accounts?: Array<ClassifiedAccountRow>;
  classification?: ClassificationLabel;
  next_cursor?: string;
  projection_enabled?: boolean;
}

/** ComplianceBaseline schema */
export interface ComplianceBaseline {
  _metadata?: EntityMetadata;
  /** Primary key for this compliance baseline in the source system */
  baseline_ref?: string;
  /** Human-readable description of the compliance baseline */
  description?: string;
  /** Date the baseline becomes effective */
  effective_date?: string;
  /** Date the baseline expires, if applicable */
  expiration_date?: string;
  /** Whether this baseline is currently active */
  is_active?: boolean;
  /** Display name of the compliance baseline */
  name?: string;
  /** Requirements and controls defined by this baseline (key/value) */
  requirements?: Record<string, string>;
  /** Compliance standard or framework name (for example: CIS, NIST, ISO) */
  standard?: string;
  /** Version of the baseline or standard */
  version?: string;
}

/** Contact schema */
export interface Contact {
  _metadata?: EntityMetadata;
  /** Primary key for this contact in the source system */
  contact_ref?: string;
  /** When the contact record was created */
  created_at?: string;
  /** Display name of the contact */
  name?: string;
  primary_email?: Email;
  primary_phone?: Phone;
}

/** ContainerNode schema */
export interface ContainerNode {
  container_type?: string;
  name?: string;
  ref?: string;
}

/** Degree schema */
export interface Degree {
  in?: number;
  out?: number;
}

/** DetectionRow schema */
export interface DetectionRow {
  account_ref?: string;
  anomaly_score?: number;
  anomaly_verdict?: string;
  cluster_id?: string;
  connector_id?: string;
  detections?: StoredDetections;
  privilege_score?: number;
  privilege_verdict?: string;
}

/** Device schema */
export interface Device {
  _metadata?: EntityMetadata;
  /** Primary key for this device in the source system */
  device_ref?: string;
  /** Type of device (Desktop, Mobile, Tablet, etc.) */
  device_type?: string;
  /** Date when the device was enrolled */
  enrollment_date?: string;
  /** Whether the device meets compliance requirements */
  is_compliant?: boolean;
  /** Whether the device is managed by the organization */
  is_managed?: boolean;
  /** Timestamp when the device was last seen online */
  last_seen_at?: string;
  /** Manufacturer of the device */
  manufacturer?: string;
  /** Model identifier of the device */
  model?: string;
  /** Name or hostname of the device */
  name?: string;
  /** Operating system running on the device */
  operating_system?: string;
  /** Version of the operating system */
  os_version?: string;
  /** Serial number of the device */
  serial_number?: string;
  /** Trust level of the device (AzureAD, Hybrid, etc.) */
  trust_type?: string;
}

/** DeviceAccount schema */
export interface DeviceAccount {
  _metadata?: EntityMetadata;
  /** Reference to the account using this device */
  account_ref?: string;
  /** Reference to the device */
  device_ref?: string;
  /** Whether this account is the primary user of the device */
  is_primary_user?: boolean;
}

/** DeviceApplication schema */
export interface DeviceApplication {
  _metadata?: EntityMetadata;
  /** Reference to the application installed on this device */
  application_ref?: string;
  /** Reference to the device */
  device_ref?: string;
  /** Whether this application installation is approved by the organization */
  is_approved?: boolean;
  /** Version of the application installed */
  version?: string;
}

/** Email schema */
export interface Email {
  address?: string;
  verified?: boolean;
}

/** Employee schema */
export interface Employee {
  _metadata?: EntityMetadata;
  /** When the employee record was created */
  created_at?: string;
  /** Department or organization unit */
  department?: string;
  /** Employee identifier in the source system */
  employee_id?: string;
  /** Primary key for this employee in the source system */
  employee_ref?: string;
  /** Given name */
  first_name?: string;
  /** Hire date */
  hired_on?: string;
  /** Family name */
  last_name?: string;
  /** EmployeeRef of the employee's manager within the same connector's employees space (not an email or account ref) */
  manager_ref?: string;
  primary_email?: Email;
  primary_phone?: Phone;
  /** Employment status (for example: active, terminated, on_leave) */
  status?: string;
  /** Job title */
  title?: string;
}

/** Endpoint schema */
export interface Endpoint {
  connector_id?: string;
  ref?: string;
  space?: string;
}

/** EntityKindSummary schema */
export interface EntityKindSummary {
  catalog_space?: string;
  description?: string;
  name?: string;
  singular_slug?: string;
  slug?: string;
  total?: number;
}

/** EntityMetadata schema */
export interface EntityMetadata {
  connector_id?: string;
  discriminator?: string;
  distinct_id?: string;
  segment_id?: string;
  space?: string;
  tenant_id?: string;
}

/** EntityPageAccessReview schema */
export interface EntityPageAccessReview {
  facets?: Record<string, facetDimension>;
  models?: Array<AccessReview>;
  next?: string;
  total?: number;
}

/** EntityPageAccount schema */
export interface EntityPageAccount {
  facets?: Record<string, facetDimension>;
  models?: Array<Account>;
  next?: string;
  total?: number;
}

/** EntityPageAccountAttribute schema */
export interface EntityPageAccountAttribute {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountAttribute>;
  next?: string;
  total?: number;
}

/** EntityPageAccountClassification schema */
export interface EntityPageAccountClassification {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountClassification>;
  next?: string;
  total?: number;
}

/** EntityPageAccountExtension schema */
export interface EntityPageAccountExtension {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountExtension>;
  next?: string;
  total?: number;
}

/** EntityPageAccountLicense schema */
export interface EntityPageAccountLicense {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountLicense>;
  next?: string;
  total?: number;
}

/** EntityPageAccountManager schema */
export interface EntityPageAccountManager {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountManager>;
  next?: string;
  total?: number;
}

/** EntityPageAccountMultiFactor schema */
export interface EntityPageAccountMultiFactor {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountMultiFactor>;
  next?: string;
  total?: number;
}

/** EntityPageAccountPermission schema */
export interface EntityPageAccountPermission {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountPermission>;
  next?: string;
  total?: number;
}

/** EntityPageAccountPolicy schema */
export interface EntityPageAccountPolicy {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountPolicy>;
  next?: string;
  total?: number;
}

/** EntityPageAccountRiskFactor schema */
export interface EntityPageAccountRiskFactor {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountRiskFactor>;
  next?: string;
  total?: number;
}

/** EntityPageAccountRole schema */
export interface EntityPageAccountRole {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountRole>;
  next?: string;
  total?: number;
}

/** EntityPageAccountSecret schema */
export interface EntityPageAccountSecret {
  facets?: Record<string, facetDimension>;
  models?: Array<AccountSecret>;
  next?: string;
  total?: number;
}

/** EntityPageAgent schema */
export interface EntityPageAgent {
  facets?: Record<string, facetDimension>;
  models?: Array<Agent>;
  next?: string;
  total?: number;
}

/** EntityPageAgentAccount schema */
export interface EntityPageAgentAccount {
  facets?: Record<string, facetDimension>;
  models?: Array<AgentAccount>;
  next?: string;
  total?: number;
}

/** EntityPageAgentCapability schema */
export interface EntityPageAgentCapability {
  facets?: Record<string, facetDimension>;
  models?: Array<AgentCapability>;
  next?: string;
  total?: number;
}

/** EntityPageAgentDeployment schema */
export interface EntityPageAgentDeployment {
  facets?: Record<string, facetDimension>;
  models?: Array<AgentDeployment>;
  next?: string;
  total?: number;
}

/** EntityPageAgentDeploymentAttribute schema */
export interface EntityPageAgentDeploymentAttribute {
  facets?: Record<string, facetDimension>;
  models?: Array<AgentDeploymentAttribute>;
  next?: string;
  total?: number;
}

/** EntityPageAgentDeploymentExtension schema */
export interface EntityPageAgentDeploymentExtension {
  facets?: Record<string, facetDimension>;
  models?: Array<AgentDeploymentExtension>;
  next?: string;
  total?: number;
}

/** EntityPageAgentDeploymentResource schema */
export interface EntityPageAgentDeploymentResource {
  facets?: Record<string, facetDimension>;
  models?: Array<AgentDeploymentResource>;
  next?: string;
  total?: number;
}

/** EntityPageAgentOwner schema */
export interface EntityPageAgentOwner {
  facets?: Record<string, facetDimension>;
  models?: Array<AgentOwner>;
  next?: string;
  total?: number;
}

/** EntityPageApplication schema */
export interface EntityPageApplication {
  facets?: Record<string, facetDimension>;
  models?: Array<Application>;
  next?: string;
  total?: number;
}

/** EntityPageApplicationAccount schema */
export interface EntityPageApplicationAccount {
  facets?: Record<string, facetDimension>;
  models?: Array<ApplicationAccount>;
  next?: string;
  total?: number;
}

/** EntityPageApplicationAgent schema */
export interface EntityPageApplicationAgent {
  facets?: Record<string, facetDimension>;
  models?: Array<ApplicationAgent>;
  next?: string;
  total?: number;
}

/** EntityPageApplicationExtension schema */
export interface EntityPageApplicationExtension {
  facets?: Record<string, facetDimension>;
  models?: Array<ApplicationExtension>;
  next?: string;
  total?: number;
}

/** EntityPageApplicationGroup schema */
export interface EntityPageApplicationGroup {
  facets?: Record<string, facetDimension>;
  models?: Array<ApplicationGroup>;
  next?: string;
  total?: number;
}

/** EntityPageApplicationPermission schema */
export interface EntityPageApplicationPermission {
  facets?: Record<string, facetDimension>;
  models?: Array<ApplicationPermission>;
  next?: string;
  total?: number;
}

/** EntityPageApplicationRole schema */
export interface EntityPageApplicationRole {
  facets?: Record<string, facetDimension>;
  models?: Array<ApplicationRole>;
  next?: string;
  total?: number;
}

/** EntityPageAttribute schema */
export interface EntityPageAttribute {
  facets?: Record<string, facetDimension>;
  models?: Array<Attribute>;
  next?: string;
  total?: number;
}

/** EntityPageCampaign schema */
export interface EntityPageCampaign {
  facets?: Record<string, facetDimension>;
  models?: Array<Campaign>;
  next?: string;
  total?: number;
}

/** EntityPageChannel schema */
export interface EntityPageChannel {
  facets?: Record<string, facetDimension>;
  models?: Array<Channel>;
  next?: string;
  total?: number;
}

/** EntityPageChannelAccount schema */
export interface EntityPageChannelAccount {
  facets?: Record<string, facetDimension>;
  models?: Array<ChannelAccount>;
  next?: string;
  total?: number;
}

/** EntityPageClassification schema */
export interface EntityPageClassification {
  facets?: Record<string, facetDimension>;
  models?: Array<Classification>;
  next?: string;
  total?: number;
}

/** EntityPageComplianceBaseline schema */
export interface EntityPageComplianceBaseline {
  facets?: Record<string, facetDimension>;
  models?: Array<ComplianceBaseline>;
  next?: string;
  total?: number;
}

/** EntityPageContact schema */
export interface EntityPageContact {
  facets?: Record<string, facetDimension>;
  models?: Array<Contact>;
  next?: string;
  total?: number;
}

/** EntityPageDevice schema */
export interface EntityPageDevice {
  facets?: Record<string, facetDimension>;
  models?: Array<Device>;
  next?: string;
  total?: number;
}

/** EntityPageDeviceAccount schema */
export interface EntityPageDeviceAccount {
  facets?: Record<string, facetDimension>;
  models?: Array<DeviceAccount>;
  next?: string;
  total?: number;
}

/** EntityPageDeviceApplication schema */
export interface EntityPageDeviceApplication {
  facets?: Record<string, facetDimension>;
  models?: Array<DeviceApplication>;
  next?: string;
  total?: number;
}

/** EntityPageEmployee schema */
export interface EntityPageEmployee {
  facets?: Record<string, facetDimension>;
  models?: Array<Employee>;
  next?: string;
  total?: number;
}

/** EntityPageFederationService schema */
export interface EntityPageFederationService {
  facets?: Record<string, facetDimension>;
  models?: Array<FederationService>;
  next?: string;
  total?: number;
}

/** EntityPageFederationServiceProvider schema */
export interface EntityPageFederationServiceProvider {
  facets?: Record<string, facetDimension>;
  models?: Array<FederationServiceProvider>;
  next?: string;
  total?: number;
}

/** EntityPageGroup schema */
export interface EntityPageGroup {
  facets?: Record<string, facetDimension>;
  models?: Array<Group>;
  next?: string;
  total?: number;
}

/** EntityPageGroupAttribute schema */
export interface EntityPageGroupAttribute {
  facets?: Record<string, facetDimension>;
  models?: Array<GroupAttribute>;
  next?: string;
  total?: number;
}

/** EntityPageGroupExtension schema */
export interface EntityPageGroupExtension {
  facets?: Record<string, facetDimension>;
  models?: Array<GroupExtension>;
  next?: string;
  total?: number;
}

/** EntityPageGroupLicense schema */
export interface EntityPageGroupLicense {
  facets?: Record<string, facetDimension>;
  models?: Array<GroupLicense>;
  next?: string;
  total?: number;
}

/** EntityPageGroupMember schema */
export interface EntityPageGroupMember {
  facets?: Record<string, facetDimension>;
  models?: Array<GroupMember>;
  next?: string;
  total?: number;
}

/** EntityPageGroupMembershipRole schema */
export interface EntityPageGroupMembershipRole {
  facets?: Record<string, facetDimension>;
  models?: Array<GroupMembershipRole>;
  next?: string;
  total?: number;
}

/** EntityPageGroupPermission schema */
export interface EntityPageGroupPermission {
  facets?: Record<string, facetDimension>;
  models?: Array<GroupPermission>;
  next?: string;
  total?: number;
}

/** EntityPageGroupRole schema */
export interface EntityPageGroupRole {
  facets?: Record<string, facetDimension>;
  models?: Array<GroupRole>;
  next?: string;
  total?: number;
}

/** EntityPageLicense schema */
export interface EntityPageLicense {
  facets?: Record<string, facetDimension>;
  models?: Array<License>;
  next?: string;
  total?: number;
}

/** EntityPageLocation schema */
export interface EntityPageLocation {
  facets?: Record<string, facetDimension>;
  models?: Array<Location>;
  next?: string;
  total?: number;
}

/** EntityPageMultiFactor schema */
export interface EntityPageMultiFactor {
  facets?: Record<string, facetDimension>;
  models?: Array<MultiFactor>;
  next?: string;
  total?: number;
}

/** EntityPageNetworkObject schema */
export interface EntityPageNetworkObject {
  facets?: Record<string, facetDimension>;
  models?: Array<NetworkObject>;
  next?: string;
  total?: number;
}

/** EntityPageOrganizationalUnit schema */
export interface EntityPageOrganizationalUnit {
  facets?: Record<string, facetDimension>;
  models?: Array<OrganizationalUnit>;
  next?: string;
  total?: number;
}

/** EntityPageOrganizationalUnitMember schema */
export interface EntityPageOrganizationalUnitMember {
  facets?: Record<string, facetDimension>;
  models?: Array<OrganizationalUnitMember>;
  next?: string;
  total?: number;
}

/** EntityPagePasswordPolicy schema */
export interface EntityPagePasswordPolicy {
  facets?: Record<string, facetDimension>;
  models?: Array<PasswordPolicy>;
  next?: string;
  total?: number;
}

/** EntityPagePeopleExtension schema */
export interface EntityPagePeopleExtension {
  facets?: Record<string, facetDimension>;
  models?: Array<PeopleExtension>;
  next?: string;
  total?: number;
}

/** EntityPagePermission schema */
export interface EntityPagePermission {
  facets?: Record<string, facetDimension>;
  models?: Array<Permission>;
  next?: string;
  total?: number;
}

/** EntityPagePerson schema */
export interface EntityPagePerson {
  facets?: Record<string, facetDimension>;
  models?: Array<Person>;
  next?: string;
  total?: number;
}

/** EntityPagePersonAccount schema */
export interface EntityPagePersonAccount {
  facets?: Record<string, facetDimension>;
  models?: Array<PersonAccount>;
  next?: string;
  total?: number;
}

/** EntityPagePersonAttribute schema */
export interface EntityPagePersonAttribute {
  facets?: Record<string, facetDimension>;
  models?: Array<PersonAttribute>;
  next?: string;
  total?: number;
}

/** EntityPagePolicy schema */
export interface EntityPagePolicy {
  facets?: Record<string, facetDimension>;
  models?: Array<Policy>;
  next?: string;
  total?: number;
}

/** EntityPagePolicyExtension schema */
export interface EntityPagePolicyExtension {
  facets?: Record<string, facetDimension>;
  models?: Array<PolicyExtension>;
  next?: string;
  total?: number;
}

/** EntityPageResource schema */
export interface EntityPageResource {
  facets?: Record<string, facetDimension>;
  models?: Array<Resource>;
  next?: string;
  total?: number;
}

/** EntityPageResourceContainer schema */
export interface EntityPageResourceContainer {
  facets?: Record<string, facetDimension>;
  models?: Array<ResourceContainer>;
  next?: string;
  total?: number;
}

/** EntityPageResourceContainerExtension schema */
export interface EntityPageResourceContainerExtension {
  facets?: Record<string, facetDimension>;
  models?: Array<ResourceContainerExtension>;
  next?: string;
  total?: number;
}

/** EntityPageResourceContainerResource schema */
export interface EntityPageResourceContainerResource {
  facets?: Record<string, facetDimension>;
  models?: Array<ResourceContainerResource>;
  next?: string;
  total?: number;
}

/** EntityPageResourceContainerResourceContainer schema */
export interface EntityPageResourceContainerResourceContainer {
  facets?: Record<string, facetDimension>;
  models?: Array<ResourceContainerResourceContainer>;
  next?: string;
  total?: number;
}

/** EntityPageResourceExtension schema */
export interface EntityPageResourceExtension {
  facets?: Record<string, facetDimension>;
  models?: Array<ResourceExtension>;
  next?: string;
  total?: number;
}

/** EntityPageResourcePermission schema */
export interface EntityPageResourcePermission {
  facets?: Record<string, facetDimension>;
  models?: Array<ResourcePermission>;
  next?: string;
  total?: number;
}

/** EntityPageRiskFactor schema */
export interface EntityPageRiskFactor {
  facets?: Record<string, facetDimension>;
  models?: Array<RiskFactor>;
  next?: string;
  total?: number;
}

/** EntityPageRole schema */
export interface EntityPageRole {
  facets?: Record<string, facetDimension>;
  models?: Array<Role>;
  next?: string;
  total?: number;
}

/** EntityPageRoleAssignmentType schema */
export interface EntityPageRoleAssignmentType {
  facets?: Record<string, facetDimension>;
  models?: Array<RoleAssignmentType>;
  next?: string;
  total?: number;
}

/** EntityPageRoleExtension schema */
export interface EntityPageRoleExtension {
  facets?: Record<string, facetDimension>;
  models?: Array<RoleExtension>;
  next?: string;
  total?: number;
}

/** EntityPageRolePermission schema */
export interface EntityPageRolePermission {
  facets?: Record<string, facetDimension>;
  models?: Array<RolePermission>;
  next?: string;
  total?: number;
}

/** EntityPageScopedAssignment schema */
export interface EntityPageScopedAssignment {
  facets?: Record<string, facetDimension>;
  models?: Array<ScopedAssignment>;
  next?: string;
  total?: number;
}

/** EntityPageScopedDenial schema */
export interface EntityPageScopedDenial {
  facets?: Record<string, facetDimension>;
  models?: Array<ScopedDenial>;
  next?: string;
  total?: number;
}

/** EntityPageSearchResult schema */
export interface EntityPageSearchResult {
  facets?: Record<string, facetDimension>;
  models?: Array<SearchResult_35270541>;
  next?: string;
  total?: number;
}

/** EntityPageSecret schema */
export interface EntityPageSecret {
  facets?: Record<string, facetDimension>;
  models?: Array<Secret>;
  next?: string;
  total?: number;
}

/** EntityPageSecretResource schema */
export interface EntityPageSecretResource {
  facets?: Record<string, facetDimension>;
  models?: Array<SecretResource>;
  next?: string;
  total?: number;
}

/** EntityPageSecurityConfiguration schema */
export interface EntityPageSecurityConfiguration {
  facets?: Record<string, facetDimension>;
  models?: Array<SecurityConfiguration>;
  next?: string;
  total?: number;
}

/** EntityPageServiceAccountImpersonation schema */
export interface EntityPageServiceAccountImpersonation {
  facets?: Record<string, facetDimension>;
  models?: Array<ServiceAccountImpersonation>;
  next?: string;
  total?: number;
}

/** EntityPageSpecialPrincipal schema */
export interface EntityPageSpecialPrincipal {
  facets?: Record<string, facetDimension>;
  models?: Array<SpecialPrincipal>;
  next?: string;
  total?: number;
}

/** EntityPageTicket schema */
export interface EntityPageTicket {
  facets?: Record<string, facetDimension>;
  models?: Array<Ticket>;
  next?: string;
  total?: number;
}

/** EntityPageTicketComment schema */
export interface EntityPageTicketComment {
  facets?: Record<string, facetDimension>;
  models?: Array<TicketComment>;
  next?: string;
  total?: number;
}

/** EntityPageaccountRow schema */
export interface EntityPageaccountRow {
  facets?: Record<string, facetDimension>;
  models?: Array<accountRow>;
  next?: string;
  total?: number;
}

/** EntityPageagentRow schema */
export interface EntityPageagentRow {
  facets?: Record<string, facetDimension>;
  models?: Array<agentRow>;
  next?: string;
  total?: number;
}

/** EntityPageapplicationRow schema */
export interface EntityPageapplicationRow {
  facets?: Record<string, facetDimension>;
  models?: Array<applicationRow>;
  next?: string;
  total?: number;
}

/** EntityPageclassificationWithStats schema */
export interface EntityPageclassificationWithStats {
  facets?: Record<string, facetDimension>;
  models?: Array<classificationWithStats>;
  next?: string;
  total?: number;
}

/** EntityPagegroupRow schema */
export interface EntityPagegroupRow {
  facets?: Record<string, facetDimension>;
  models?: Array<groupRow>;
  next?: string;
  total?: number;
}

/** EntityPagepersonRow schema */
export interface EntityPagepersonRow {
  facets?: Record<string, facetDimension>;
  models?: Array<personRow>;
  next?: string;
  total?: number;
}

/** EntityPageresourceRow schema */
export interface EntityPageresourceRow {
  facets?: Record<string, facetDimension>;
  models?: Array<resourceRow>;
  next?: string;
  total?: number;
}

/** EntityPageroleRow schema */
export interface EntityPageroleRow {
  facets?: Record<string, facetDimension>;
  models?: Array<roleRow>;
  next?: string;
  total?: number;
}

/** EntityStatsBatchRequest schema */
export interface EntityStatsBatchRequest {
  /** Base filter clauses, same grammar as the list endpoint */
  filter?: Array<string>;
  /** Include rows bridged from mesh's own domain (default no) */
  include_system?: boolean;
  metrics?: Array<EntityStatsMetric>;
}

/** EntityStatsBatchResult schema */
export interface EntityStatsBatchResult {
  /** Entities matching the base filter set */
  cohort_size?: number;
  metrics?: Array<EntityStatsMetricResult>;
}

/** EntityStatsGroup schema */
export interface EntityStatsGroup {
  /** Group value, bucket label, or (unknown) */
  key?: string;
  /** Aggregated value within this group */
  value?: number;
}

/** EntityStatsMetric schema */
export interface EntityStatsMetric {
  /** count, distinct_count, sum, avg, min or max */
  agg?: string;
  /** Extra filter clauses AND-ed onto the base filter */
  filter?: Array<string>;
  /** Column or attr:Key to aggregate; required except for count */
  value?: string;
}

/** EntityStatsMetricResult schema */
export interface EntityStatsMetricResult {
  totals?: EntityStatsTotals;
}

/** EntityStatsResult schema */
export interface EntityStatsResult {
  /** Entities matching the filter set */
  cohort_size?: number;
  groups?: Array<EntityStatsGroup>;
  totals?: EntityStatsTotals;
  /** True when groups were capped and the list is incomplete */
  truncated?: boolean;
}

/** EntityStatsTotals schema */
export interface EntityStatsTotals {
  /** Values excluded from a numeric aggregation as unparseable */
  unparsed?: number;
  /** Aggregated value over the cohort */
  value?: number;
}

/** Envelope schema */
export interface Envelope {
  $type?: string;
}

/** EventSearchFacets schema */
export interface EventSearchFacets {
  kinds?: Array<facetBucket>;
  /** True when more distinct kinds exist than the buckets returned */
  kinds_truncated?: boolean;
  outcome_results?: Array<facetBucket>;
  spaces?: Array<facetBucket>;
  /** True when more distinct spaces exist than the buckets returned */
  spaces_truncated?: boolean;
  target_types?: Array<facetBucket>;
  /** True when more distinct target types exist than the buckets returned */
  target_types_truncated?: boolean;
}

/** EventSearchHit schema */
export interface EventSearchHit {
  /** Display name of the actor, when enriched */
  actor_display_name?: string;
  /** Email of the actor, when enriched */
  actor_email?: string;
  /** Person the actor's account maps to */
  actor_person_ref?: string;
  /** Source-system reference for the acting principal */
  actor_ref?: string;
  /** Entity type of the actor, e.g. account */
  actor_type?: string;
  /** UPN of the actor, when enriched */
  actor_upn?: string;
  /** Connector that produced the event */
  connector_id?: string;
  /** Device reference */
  device_ref?: string;
  /** Event identifier; also the by-id filter operand */
  event_id?: string;
  /** Producer-supplied event reference */
  event_ref?: string;
  /** Why this row's payload could not be fetched */
  hydration_error?: string;
  /** Time the row was written to the index */
  indexed_at?: string;
  /** Source IP recorded on the event */
  ip_address?: string;
  /** Registry discriminator, as published by events/kinds */
  kind?: string;
  /** Location reference */
  location_ref?: string;
  /** Log name for events ingested over POST /events */
  log_name?: string;
  /** Provider action verb */
  outcome_action?: string;
  /** Provider-supplied outcome reason */
  outcome_reason?: string;
  /** Normalized outcome bucket */
  outcome_result?: string;
  /** Verbatim stream envelope; null when this row could not be fetched */
  payload?: any;
  /** Whether the event concerned privileged access; null where the kind carries no marker */
  privileged?: boolean;
  /** Risk level from the security block */
  risk_level?: string;
  /** Risk state from the security block */
  risk_state?: string;
  /** Physical stream segment holding the entry */
  segment_id?: string;
  /** Sequence of the entry within its segment */
  sequence?: number;
  /** Provider session identifier */
  session_id?: string;
  /** Catalog space the entry was read from */
  space?: string;
  /** Display name of the target, when enriched */
  target_display_name?: string;
  /** Email of the target, when enriched */
  target_email?: string;
  /** Person the target's account maps to */
  target_person_ref?: string;
  /** Source-system reference for the target */
  target_ref?: string;
  /** Entity type of the target */
  target_type?: string;
  /** UPN of the target, when enriched */
  target_upn?: string;
  /** Threat type from the security block */
  threat_type?: string;
  /** Source event time */
  timestamp?: string;
  /** User agent string */
  user_agent?: string;
}

/** EventSearchResult schema */
export interface EventSearchResult {
  events?: Array<EventSearchHit>;
  facets?: EventSearchFacets;
  /** Cursor for the next page, or null when exhausted */
  next?: string;
  /** Rows matching the filter set, not the page length */
  total?: number;
}

/** EventStatsResult schema */
export interface EventStatsResult {
  connectors?: Array<StatsConnector>;
  groups?: Array<StatsGroup>;
  liveness?: StatsLiveness;
  /** counters or index */
  plan?: string;
  totals?: StatsTotals;
  window?: StatsWindow;
  window_available?: StatsWindowAvailable;
}

/** Explanation schema */
export interface Explanation {
  absences?: Array<string>;
  top_features?: Array<string>;
}

/** FederationService schema */
export interface FederationService {
  _metadata?: EntityMetadata;
  /** Human-readable description of the federation service */
  description?: string;
  /** Whether the federation service is enabled */
  enabled?: boolean;
  /** Primary key for this federation service in the source system */
  federation_service_ref?: string;
  /** External trust anchor — the issuer/identity provider that federated identities originate from (for example: an OIDC issuer URI or AWS account ID) */
  issuer?: string;
  /** Display name of the federation service */
  name?: string;
  /** Source system that owns this federation service (for example: gcp) */
  provider?: string;
  /** Lifecycle/operational status in the source system (for example: ACTIVE, DELETED) */
  status?: string;
  /** Kind of federation (for example: workload_identity_pool, workforce_pool, oidc, saml, aws, x509) */
  type?: string;
}

/** FederationServiceProvider schema */
export interface FederationServiceProvider {
  _metadata?: EntityMetadata;
  /** FederationServiceRef of the parent pool */
  pool_ref?: string;
  /** FederationServiceRef of the child provider */
  provider_ref?: string;
}

/** Group schema */
export interface Group {
  _metadata?: EntityMetadata;
  /** Timestamp when the group was created in the source system */
  created_at?: string;
  /** Human-readable description of the group's purpose */
  description?: string;
  /** Primary key for this group in the source system */
  group_ref?: string;
  /** Name of the group */
  name?: string;
}

/** GroupAttribute schema */
export interface GroupAttribute {
  _metadata?: EntityMetadata;
  /** Reference to the attribute definition */
  attribute_ref?: string;
  /** Reference to the group this attribute value belongs to */
  group_ref?: string;
  /** Type hint for parsing the value (e.g. string, int, bool, datetime) */
  type?: string;
  /** Value of the attribute for this group */
  value?: string;
}

/** GroupExtension schema */
export interface GroupExtension {
  _metadata?: EntityMetadata;
  attributes?: Array<AttributeEntry>;
  /** Reference to the group this extension belongs to */
  group_ref?: string;
  permissions?: Array<PermissionEntry>;
}

/** GroupLicense schema */
export interface GroupLicense {
  _metadata?: EntityMetadata;
  /** Service plan identifiers disabled for this group */
  disabled_service_plans?: Array<string>;
  /** Service plan identifiers enabled for this group */
  enabled_service_plans?: Array<string>;
  /** Primary key for the group in the source system */
  group_ref?: string;
  /** Primary key for the license in the source system */
  license_ref?: string;
}

/** GroupMember schema */
export interface GroupMember {
  _metadata?: EntityMetadata;
  /** Primary key for the account in the source system */
  account_ref?: string;
  /** Primary key for the group in the source system */
  group_ref?: string;
  /** RoleRef of the role associated with the membership within the same connector's roles space, if provided */
  role_ref?: string;
}

/** GroupMembership schema */
export interface GroupMembership {
  group_ref?: string;
  role_ref?: string;
}

/** GroupMembershipRole schema */
export interface GroupMembershipRole {
  _metadata?: EntityMetadata;
  /** Primary key for the group in the source system */
  group_ref?: string;
  /** Provider's raw role string, when it differs from the normalized role */
  native_role?: string;
  /** Reference to the account or group holding the role */
  principal_ref?: string;
  /** Kind of principal holding the role */
  principal_type?:
    | 'unknown'
    | 'account'
    | 'group'
    | 'device'
    | 'federation'
    | 'other';
  /** Normalized governance role: owner, manager, admin, member, or other */
  role?: 'unknown' | 'owner' | 'manager' | 'admin' | 'member' | 'other';
}

/** GroupPermission schema */
export interface GroupPermission {
  _metadata?: EntityMetadata;
  /** Primary key for the group in the source system */
  group_ref?: string;
  /** Primary key for the permission in the source system */
  permission_ref?: string;
}

/** GroupProfile schema */
export interface GroupProfile {
  applications?: ProfileBlockApplication;
  attributes?: Array<AttributeValue>;
  group?: Group;
  licenses?: ProfileBlockLicense;
  members?: ProfileBlockAccount;
  permissions?: ProfileBlockPermission;
  roles?: ProfileBlockRole;
}

/** GroupRole schema */
export interface GroupRole {
  _metadata?: EntityMetadata;
  /** Primary key for the group in the source system */
  group_ref?: string;
  /** Primary key for the role in the source system */
  role_ref?: string;
}

/** GroupSummary schema */
export interface GroupSummary {
  /** Grant entries attributed to this via-group (or to the null bucket) */
  grant_count?: number;
  group?: NamedRef;
  /** Distinct resources reached via this group (or the null bucket) */
  resource_count?: number;
}

/** GroupWithCount schema */
export interface GroupWithCount {
  member_count?: number;
}

/** HeldAccountsResponse schema */
export interface HeldAccountsResponse {
  candidates?: Array<Relationship>;
  held?: Array<Relationship>;
  person?: Endpoint;
  projection_enabled?: boolean;
}

/** IPAddress schema */
export interface IPAddress {
  address?: string;
  type?: string;
}

/** ImpersonationCondition schema */
export interface ImpersonationCondition {
  /** Human-readable description of the condition */
  description?: string;
  /** CEL condition expression, recorded verbatim (not evaluated) */
  expression?: string;
  /** Short human-readable title of the condition */
  title?: string;
}

/** ImpersonationResponse schema */
export interface ImpersonationResponse {
  account?: Endpoint;
  acted_for_by?: Array<Relationship>;
  can_act_as?: Array<Relationship>;
  projection_enabled?: boolean;
}

/** IndirectAccess schema */
export interface IndirectAccess {
  grants?: Array<IndirectGrant>;
  principal?: PrincipalRef;
  total_count?: number;
}

/** IndirectGrant schema */
export interface IndirectGrant {
  assignment_type?: string;
  permissions?: Array<PermissionRef>;
  resource?: ResourceInfo;
  via?: IndirectVia;
}

/** IndirectVia schema */
export interface IndirectVia {
  safe?: NamedRef;
  secret?: NamedRef;
  target_account?: PrincipalRef;
}

/** License schema */
export interface License {
  _metadata?: EntityMetadata;
  /** Number of license units currently in use */
  consumed_units?: number;
  /** Primary key for this license in the source system */
  license_ref?: string;
  /** Human-readable product name */
  product_name?: string;
  /** List of service plans included in this license */
  service_plans?: Array<string>;
  /** Stock Keeping Unit identifier for the license */
  sku?: string;
  /** Current status of the license (Active, Expired, etc.) */
  status?: string;
  /** Total number of license units available */
  total_units?: number;
}

/** LinkedAccountsResponse schema */
export interface LinkedAccountsResponse {
  account?: Endpoint;
  candidates?: Array<Relationship>;
  linked?: Array<Relationship>;
  projection_enabled?: boolean;
}

/** Location schema */
export interface Location {
  _metadata?: EntityMetadata;
  /** Street address */
  address?: string;
  /** City */
  city?: string;
  /** Country */
  country?: string;
  /** Latitude coordinate */
  latitude?: number;
  /** Primary key for this location in the source system */
  location_ref?: string;
  /** Longitude coordinate */
  longitude?: number;
  /** Name of the location */
  name?: string;
  /** Postal or ZIP code */
  postal_code?: string;
  /** State, province, or region */
  state?: string;
  /** Location type (for example: office, datacenter) */
  type?: string;
}

/** ManagementHop schema */
export interface ManagementHop {
  depth?: number;
  exactness?: string;
  manager_account?: Endpoint;
  origin?: string;
  person?: Endpoint;
  proposed?: boolean;
  rank?: number;
  rule?: string;
  via_account?: Endpoint;
}

/** ManagementResponse schema */
export interface ManagementResponse {
  candidate_managers?: Array<ManagementHop>;
  candidate_reports?: Array<ManagementHop>;
  managers?: Array<ManagementHop>;
  person?: Endpoint;
  projection_enabled?: boolean;
  reports?: Array<ManagementHop>;
}

/** MultiFactor schema */
export interface MultiFactor {
  _metadata?: EntityMetadata;
  /** Type of multi-factor method */
  kind?:
    | 'UNKNOWN'
    | 'SMS'
    | 'EMAIL'
    | 'AUTHENTICATOR'
    | 'TOTP'
    | 'PHONE_CALL'
    | 'PASSKEY'
    | 'SECURITY_KEY'
    | 'TEMPORARY_ACCESS_PASS'
    | 'OTHER';
  /** Primary key for this multi-factor method in the source system */
  multi_factor_ref?: string;
  /** Whether the tenant requires, permits or disallows this method; UNKNOWN when the provider exposes no policy */
  org_policy?: 'UNKNOWN' | 'REQUIRED' | 'OPTIONAL' | 'NOT_ALLOWED';
}

/** NamedRef schema */
export interface NamedRef {
  name?: string;
  ref?: string;
}

/** Neighborhood schema */
export interface Neighborhood {
  capped_labels?: Record<string, number>;
  direction?: 'both' | 'downstream' | 'upstream';
  edge_count?: number;
  edges?: Array<NeighborhoodEdge>;
  fanout?: number;
  hops?: number;
  label_limit?: number;
  node_count?: number;
  nodes?: Array<NeighborhoodNode>;
  seed?: NeighborhoodNode;
  truncated?: boolean;
}

/** NeighborhoodEdge schema */
export interface NeighborhoodEdge {
  from?: string;
  props?: Record<string, string>;
  to?: string;
  type?: string;
}

/** NeighborhoodNode schema */
export interface NeighborhoodNode {
  connector_id?: string;
  degree?: Degree;
  depth?: number;
  direction?: 'out' | 'in' | 'any';
  expandable?: boolean;
  id?: string;
  label?:
    | 'Account'
    | 'Group'
    | 'Role'
    | 'Permission'
    | 'Resource'
    | 'Container'
    | 'Secret'
    | 'Agent'
    | 'Capability'
    | 'Application'
    | 'Deployment';
  name?: string;
  props?: Record<string, string>;
  ref?: string;
  shown?: Degree;
}

/** NetworkObject schema */
export interface NetworkObject {
  _metadata?: EntityMetadata;
  ip_addresses?: Array<IPAddress>;
  /** Location reference or label */
  location?: string;
  /** MAC address, if applicable */
  mac_address?: string;
  /** Display name of the network object */
  name?: string;
  /** Primary key for this network object in the source system */
  network_object_ref?: string;
  /** Operational status of the network object */
  status?: string;
  /** Tags associated with the network object */
  tags?: Array<string>;
  /** Type of network object (for example: device, interface, subnet) */
  type?: string;
}

/** OrganizationalUnit schema */
export interface OrganizationalUnit {
  _metadata?: EntityMetadata;
  /** Cost center code */
  cost_center?: string;
  /** Human-readable description of the organizational unit */
  description?: string;
  /** Name of the organizational unit */
  name?: string;
  /** Primary key for this organizational unit in the source system */
  ou_ref?: string;
  /** Reference to the parent organizational unit */
  parent_ou_ref?: string;
  /** Lifecycle/operational status in the source system (e.g. ACTIVE, DELETE_REQUESTED) */
  status?: string;
  /** Organizational unit type */
  type?: string;
}

/** OrganizationalUnitMember schema */
export interface OrganizationalUnitMember {
  _metadata?: EntityMetadata;
  /** Primary key for the organizational unit in the source system */
  ou_ref?: string;
  /** Reference to the account, group, or device contained in the unit */
  principal_ref?: string;
  /** Kind of principal contained in the unit */
  principal_type?:
    | 'unknown'
    | 'account'
    | 'group'
    | 'device'
    | 'federation'
    | 'other';
}

/** OwnerManagerChainResponse schema */
export interface OwnerManagerChainResponse {
  account?: Endpoint;
  chain?: Array<ManagementHop>;
  owner?: Endpoint;
  projection_enabled?: boolean;
  truncated?: boolean;
}

/** OwnerMapping schema */
export interface OwnerMapping {
  source?: Source;
  target?: string;
  transforms?: Array<Transform>;
}

/** OwnerPreviewMatch schema */
export interface OwnerPreviewMatch {
  account_ref?: string;
  fields?: Record<string, string>;
  key?: string;
  outcome?: string;
  person_ref?: string;
}

/** OwnerPreviewRequest schema */
export interface OwnerPreviewRequest {
  limit?: number;
  rule?: OwnerRule;
}

/** OwnerPreviewResponse schema */
export interface OwnerPreviewResponse {
  advisory?: boolean;
  counts?: Record<string, number>;
  evaluated?: number;
  matches?: Array<OwnerPreviewMatch>;
  owners_enabled?: boolean;
  truncated?: boolean;
}

/** OwnerRule schema */
export interface OwnerRule {
  conditions?: Array<Predicate>;
  connector_id?: string;
  description?: string;
  enabled?: boolean;
  id?: string;
  key?: string;
  mappings?: Array<OwnerMapping>;
  name?: string;
  priority?: number;
}

/** OwnerRuleFieldsResponse schema */
export interface OwnerRuleFieldsResponse {
  accepts_other_names?: boolean;
  attribute_target_prefix?: string;
  owners_enabled?: boolean;
  source_fields?: Array<string>;
  targets?: Array<string>;
}

/** OwnerRuleProblem schema */
export interface OwnerRuleProblem {
  message?: string;
  path?: string;
}

/** OwnerRuleRequest schema */
export interface OwnerRuleRequest {
  rule?: OwnerRule;
}

/** OwnerRuleValidateResponse schema */
export interface OwnerRuleValidateResponse {
  owners_enabled?: boolean;
  problems?: Array<OwnerRuleProblem>;
  valid?: boolean;
}

/** OwnerRuleView schema */
export interface OwnerRuleView {
  definition?: OwnerRule;
  id?: string;
}

/** OwnerRulesResponse schema */
export interface OwnerRulesResponse {
  load_problems?: Array<OwnerRuleProblem>;
  owners_enabled?: boolean;
  rules?: Array<OwnerRuleView>;
}

/** OwnersResponse schema */
export interface OwnersResponse {
  account?: Endpoint;
  candidates?: Array<Relationship>;
  owners?: Array<Relationship>;
  projection_enabled?: boolean;
}

/** PasswordPolicy schema */
export interface PasswordPolicy {
  _metadata?: EntityMetadata;
  /** Human-readable description of the password policy */
  description?: string;
  /** Whether this password policy is enabled */
  is_enabled?: boolean;
  /** Lockout duration (usually in minutes) */
  lockout_duration?: number;
  /** Number of failed attempts before lockout */
  lockout_threshold?: number;
  /** Maximum password age (usually in days) */
  max_age?: number;
  /** Maximum allowed password length */
  max_length?: number;
  /** Minimum password age before it can be changed (usually in days) */
  min_age?: number;
  /** Minimum required password length */
  min_length?: number;
  /** Name of the password policy */
  name?: string;
  /** Primary key for this password policy in the source system */
  policy_ref?: string;
  /** Number of previous passwords that cannot be reused */
  prevent_reuse?: number;
  /** Whether passwords must include a lowercase character */
  require_lowercase?: boolean;
  /** Whether passwords must include a numeric character */
  require_numbers?: boolean;
  /** Whether passwords must include a special character */
  require_special_chars?: boolean;
  /** Whether passwords must include an uppercase character */
  require_uppercase?: boolean;
  /** Time after which the lockout counter resets (usually in minutes) */
  reset_lockout_after?: number;
}

/** PathEdge schema */
export interface PathEdge {
  condition?: string;
  expires_at?: string;
  scope_ref?: string;
  type?: string;
}

/** PathNode schema */
export interface PathNode {
  label?:
    | 'Account'
    | 'Group'
    | 'Agent'
    | 'Role'
    | 'Permission'
    | 'Resource'
    | 'Container';
  name?: string;
  ref?: string;
}

/** PeopleExtension schema */
export interface PeopleExtension {
  _metadata?: EntityMetadata;
  attributes?: Array<AttributeEntry>;
  /** Reference to the person this extension belongs to */
  person_ref?: string;
}

/** Permission schema */
export interface Permission {
  _metadata?: EntityMetadata;
  /** Provider-native resource type pattern this permission acts on, e.g. Microsoft.Storage/storageAccounts or storage.googleapis.com/Bucket; * matches one segment, trailing * matches the rest; empty means unknown */
  applies_to?: string;
  /** Human-readable description of what this permission grants */
  description?: string;
  /** Name of the permission */
  name?: string;
  /** Primary key for this permission in the source system */
  permission_ref?: string;
  /** Normalized CRUDE verb this permission grants */
  permission_type?: string;
}

/** PermissionEntry schema */
export interface PermissionEntry {
  /** Human-readable name of the permission */
  name?: string;
  /** Stable external identifier for the permission */
  ref?: string;
  /** Normalized CRUDE verb this permission grants */
  type?: string;
}

/** PermissionRef schema */
export interface PermissionRef {
  name?: string;
  ref?: string;
  /** Normalized permission action */
  type?: string;
}

/** Person schema */
export interface Person {
  _metadata?: EntityMetadata;
  alternate_emails?: Array<Email>;
  /** When the person record was created */
  created_at?: string;
  /** Display name used in UIs */
  display_name?: string;
  /** Given name */
  first_name?: string;
  /** Family name */
  last_name?: string;
  /** Middle name */
  middle_name?: string;
  /** Full name */
  name?: string;
  /** Primary key for this person in the source system */
  person_ref?: string;
  primary_email?: Email;
  primary_phone?: Phone;
}

/** PersonAccount schema */
export interface PersonAccount {
  _metadata?: EntityMetadata;
  /** AccountRef of the owned account, within the same connector's accounts space */
  account_ref?: string;
  /** PersonRef of the person who owns the account, within the same connector's persons space */
  person_ref?: string;
}

/** PersonAttribute schema */
export interface PersonAttribute {
  _metadata?: EntityMetadata;
  /** Reference to the attribute definition */
  attribute_ref?: string;
  /** Reference to the person this attribute value belongs to */
  person_ref?: string;
  /** Type hint for parsing the value (e.g. string, int, bool, datetime) */
  type?: string;
  /** Value of the attribute for this person */
  value?: string;
}

/** PersonOwnership schema */
export interface PersonOwnership {
  directReports?: Array<Account>;
  managedAccounts?: Array<Account>;
  manager?: Account;
  person?: Person;
  roles?: Array<Role>;
}

/** PersonProfile schema */
export interface PersonProfile {
  accounts?: ProfileBlockAccount;
  attributes?: Array<AttributeValue>;
  direct_reports?: ProfileBlockAccount;
  manager?: Account;
  person?: Person;
  roles?: ProfileBlockRole;
}

/** Phone schema */
export interface Phone {
  number?: string;
  type?: string;
  verified?: boolean;
}

/** Policy schema */
export interface Policy {
  _metadata?: EntityMetadata;
  /** Human-readable description of the policy */
  description?: string;
  /** Name of the policy */
  name?: string;
  /** Primary key for this policy in the source system */
  policy_ref?: string;
  /** Type or category of the policy */
  policy_type?: string;
  /** Priority or order used to evaluate this policy */
  priority?: number;
  /** Current state of the policy (for example: enabled, disabled) */
  state?: string;
}

/** PolicyExtension schema */
export interface PolicyExtension {
  _metadata?: EntityMetadata;
  /** Opaque machine-readable policy definition; validated by the producer, stored verbatim */
  document?: any;
  /** Discriminates the document schema (for example: rbac) */
  document_type?: string;
  /** Reference to the policy this extension belongs to */
  policy_ref?: string;
  /** References of the roles this policy is attached to */
  role_refs?: Array<string>;
}

/** Predicate schema */
export interface Predicate {
  case_sensitive?: boolean;
  field?: string;
  kind?: string;
  negate?: boolean;
  ref?: string;
  values?: Array<string>;
}

/** PreviewResult schema */
export interface PreviewResult {
  exactness?: string;
  family?: string;
  object?: RelationshipEndpoint;
  reason?: string;
  rule?: string;
  subject?: RelationshipEndpoint;
  would?: string;
}

/** PrincipalAccess schema */
export interface PrincipalAccess {
  grants?: Array<PrincipalResourceGrant>;
  limit?: number;
  offset?: number;
  principal?: PrincipalRef;
  summary?: PrincipalAccessSummary;
  total_count?: number;
}

/** PrincipalAccessSummary schema */
export interface PrincipalAccessSummary {
  by_assignment_type?: Record<string, number>;
  by_group?: Array<GroupSummary>;
  by_role?: Array<RoleSummary>;
  denied_count?: number;
  total_grants?: number;
  total_permissions?: number;
  total_resources?: number;
}

/** PrincipalRef schema */
export interface PrincipalRef {
  name?: string;
  ref?: string;
  /** Principal kind */
  type?: 'account' | 'group' | 'agent';
}

/** PrincipalResourceGrant schema */
export interface PrincipalResourceGrant {
  assignment_type?: 'direct' | 'via_group' | 'via_role' | 'scoped' | 'denied';
  permissions?: Array<PermissionRef>;
  provenance?: Provenance;
  resource?: ResourceInfo;
  role?: NamedRef;
  scope?: ScopeInfo;
  via?: NamedRef;
}

/** PrivilegeResult schema */
export interface PrivilegeResult {
  account_ref?: string;
  explanation?: Explanation;
  nearest_seeds?: Array<string>;
  score?: number;
  verdict?: string;
}

/** ProblemDetails schema */
export interface ProblemDetails {
  detail?: string;
  instance?: string;
  status?: number;
  title?: string;
  type?: string;
}

/** ProfileBlockAccount schema */
export interface ProfileBlockAccount {
  items?: Array<Account>;
  total?: number;
}

/** ProfileBlockApplication schema */
export interface ProfileBlockApplication {
  items?: Array<Application>;
  total?: number;
}

/** ProfileBlockClassification schema */
export interface ProfileBlockClassification {
  items?: Array<Classification>;
  total?: number;
}

/** ProfileBlockGroup schema */
export interface ProfileBlockGroup {
  items?: Array<Group>;
  total?: number;
}

/** ProfileBlockLicense schema */
export interface ProfileBlockLicense {
  items?: Array<License>;
  total?: number;
}

/** ProfileBlockMultiFactor schema */
export interface ProfileBlockMultiFactor {
  items?: Array<MultiFactor>;
  total?: number;
}

/** ProfileBlockPermission schema */
export interface ProfileBlockPermission {
  items?: Array<Permission>;
  total?: number;
}

/** ProfileBlockPerson schema */
export interface ProfileBlockPerson {
  items?: Array<Person>;
  total?: number;
}

/** ProfileBlockRole schema */
export interface ProfileBlockRole {
  items?: Array<Role>;
  total?: number;
}

/** Provenance schema */
export interface Provenance {
  condition?: string;
  expires_at?: string;
  is_run_as_user?: boolean;
  via_account?: NamedRef;
  via_group?: NamedRef;
}

/** QueryRequest schema */
export interface QueryRequest {
  filters?: Record<string, string>;
  limit?: number;
  next?: string;
}

/** QueryResult schema */
export interface QueryResult {
  data?: string;
  executed_at?: string;
  query_name?: string;
  row_count?: number;
}

/** RSOSChildRange schema */
export interface RSOSChildRange {
  count?: number;
  fingerprint?: string;
  hi?: string;
  lo?: string;
}

/** RSOSEntry schema */
export interface RSOSEntry {
  digest?: string;
  distinct_id?: string;
}

/** RSOSRange schema */
export interface RSOSRange {
  count?: number;
  fingerprint?: string;
  hi?: string;
  lo?: string;
  version?: number;
}

/** Relationship schema */
export interface Relationship {
  exactness?: string;
  family?: string;
  kind?: string;
  origin?: string;
  peer?: Endpoint;
  proposed?: boolean;
  rank?: number;
  rule?: string;
}

/** RelationshipEndpoint schema */
export interface RelationshipEndpoint {
  connector_id?: string;
  ref?: string;
  space?: string;
}

/** RelationshipPreviewRequest schema */
export interface RelationshipPreviewRequest {
  limit?: number;
  overlays?: Array<RelationshipRuleOverlayRequest_572e498a>;
  peer_space?: string;
  peer_values?: Record<string, Record<string, string>>;
  subject_ref?: string;
  subject_space?: string;
  subject_values?: Record<string, string>;
}

/** RelationshipPreviewResponse schema */
export interface RelationshipPreviewResponse {
  advisory?: boolean;
  evaluated?: number;
  narrowing?: string;
  results?: Array<PreviewResult>;
  truncated?: boolean;
}

/** RelationshipRuleOverlayRequest schema */
export interface RelationshipRuleOverlayRequest {
  enabled?: boolean;
  rule?: Rule;
  rule_id?: string;
}

/** RelationshipRuleOverlayRequest_572e498a schema */
export interface RelationshipRuleOverlayRequest_572e498a {
  enabled?: boolean;
  rule?: Rule;
  rule_id?: string;
}

/** RelationshipRuleProblem schema */
export interface RelationshipRuleProblem {
  message?: string;
  path?: string;
}

/** RelationshipRuleValidateRequest schema */
export interface RelationshipRuleValidateRequest {
  rule?: Rule;
}

/** RelationshipRuleValidateResponse schema */
export interface RelationshipRuleValidateResponse {
  problems?: Array<RelationshipRuleProblem>;
  set_problems?: Array<RelationshipRuleProblem>;
  valid?: boolean;
}

/** RelationshipRuleView schema */
export interface RelationshipRuleView {
  definition?: Rule;
  id?: string;
  source?: string;
  toggled?: boolean;
}

/** RelationshipRulesResponse schema */
export interface RelationshipRulesResponse {
  projection_enabled?: boolean;
  rules?: Array<RelationshipRuleView>;
}

/** RelationshipSelectorPreviewRequest schema */
export interface RelationshipSelectorPreviewRequest {
  selector?: Selector;
  subject_ref?: string;
  subject_space?: string;
  subject_values?: Record<string, string>;
}

/** RelationshipSelectorPreviewResponse schema */
export interface RelationshipSelectorPreviewResponse {
  admitted?: boolean;
  advisory?: boolean;
  keys?: Array<string>;
  problems?: Array<RelationshipRuleProblem>;
  read_refs?: Array<string>;
  reason?: string;
  steps?: Array<SelectorStep>;
}

/** ReplayStats schema */
export interface ReplayStats {
  events_scanned?: number;
}

/** Resource schema */
export interface Resource {
  _metadata?: EntityMetadata;
  /** Human-readable name of the resource */
  name?: string;
  /** Provider-native type of the resource, used to match Permission.applies_to; set when the ref does not carry it */
  provider_type?: string;
  /** Stable derived name of the resource, e.g. "s3" */
  resource_ref?: string;
  /** Normalized resource classification (object_storage, database, ...) */
  resource_type?: string;
}

/** ResourceAccess schema */
export interface ResourceAccess {
  assignments?: Array<ResourceAssignment>;
  container_path?: Array<ContainerNode>;
  resource?: ResourceInfo;
}

/** ResourceAssignment schema */
export interface ResourceAssignment {
  assignment_type?: 'direct' | 'via_group' | 'via_role' | 'scoped' | 'denied';
  permissions?: Array<PermissionRef>;
  principal?: PrincipalRef;
  provenance?: Provenance;
  role?: NamedRef;
  scope?: ScopeInfo;
}

/** ResourceContainer schema */
export interface ResourceContainer {
  _metadata?: EntityMetadata;
  /** Primary key for this scope container in the source system */
  container_ref?: string;
  /** Normalized scope level (see ContainerType* constants) */
  container_type?: string;
  /** Human-readable description of the container */
  description?: string;
  /** Human-readable name of the container */
  name?: string;
}

/** ResourceContainerExtension schema */
export interface ResourceContainerExtension {
  _metadata?: EntityMetadata;
  attributes?: Array<AttributeEntry>;
  /** Reference to the resource container this extension belongs to */
  container_ref?: string;
}

/** ResourceContainerResource schema */
export interface ResourceContainerResource {
  _metadata?: EntityMetadata;
  /** Reference to the containing scope container */
  container_ref?: string;
  /** Reference to the contained resource */
  resource_ref?: string;
}

/** ResourceContainerResourceContainer schema */
export interface ResourceContainerResourceContainer {
  _metadata?: EntityMetadata;
  /** Reference to the child scope container */
  child_container_ref?: string;
  /** Reference to the parent scope container */
  parent_container_ref?: string;
}

/** ResourceExtension schema */
export interface ResourceExtension {
  _metadata?: EntityMetadata;
  attributes?: Array<AttributeEntry>;
  permissions?: Array<PermissionEntry>;
  /** Reference to the resource this extension belongs to */
  resource_ref?: string;
}

/** ResourceInfo schema */
export interface ResourceInfo {
  connector_id?: string;
  is_container?: boolean;
  name?: string;
  ref?: string;
  type?: string;
}

/** ResourcePermission schema */
export interface ResourcePermission {
  _metadata?: EntityMetadata;
  /** Reference to the permission (carries the CRUDE verb) */
  permission_ref?: string;
  /** Reference to the resource the permission acts on */
  resource_ref?: string;
}

/** RiskFactor schema */
export interface RiskFactor {
  _metadata?: EntityMetadata;
  /** Category grouping for the risk factor */
  category?: string;
  /** Human-readable description of the risk factor */
  description?: string;
  /** Human-readable name of the risk factor */
  name?: string;
  /** Stable external identifier for this risk factor */
  risk_factor_ref?: string;
  /** Relative weight/severity of the risk factor */
  weight?: number;
}

/** RiskFactorEntry schema */
export interface RiskFactorEntry {
  /** Category grouping for the risk factor */
  category?: string;
  /** Confidence that the risk factor applies (0..1) */
  confidence?: number;
  /** Human-readable name of the risk factor */
  name?: string;
  /** Stable external identifier for the risk factor */
  ref?: string;
  /** Relative weight/severity of the risk factor */
  weight?: number;
}

/** Role schema */
export interface Role {
  _metadata?: EntityMetadata;
  /** Human-readable description of the role's purpose and permissions */
  description?: string;
  /** Name of the role */
  name?: string;
  /** Primary key for this role in the source system */
  role_ref?: string;
}

/** RoleAccess schema */
export interface RoleAccess {
  grants?: Array<RoleAccessGrant>;
  limit?: number;
  offset?: number;
  role?: NamedRef;
  summary?: RoleAccessSummary;
  total_count?: number;
}

/** RoleAccessGrant schema */
export interface RoleAccessGrant {
  /** direct = HAS_ROLE (global), scoped = ASSIGNED_ROLE */
  assignment_type?: 'direct' | 'scoped';
  permissions?: Array<PermissionRef>;
  principal?: PrincipalRef;
  resource?: ResourceInfo;
  scope?: ScopeInfo;
}

/** RoleAccessSummary schema */
export interface RoleAccessSummary {
  by_principal?: Array<RolePrincipalSummary>;
  by_scope?: Array<RoleScopeSummary>;
  total_grants?: number;
  total_permissions?: number;
  total_principals?: number;
  total_resources?: number;
}

/** RoleAssignment schema */
export interface RoleAssignment {
  role_ref?: string;
}

/** RoleAssignmentType schema */
export interface RoleAssignmentType {
  _metadata?: EntityMetadata;
  /** Durability of the assignment: permanent, eligible, or active */
  assignment_type?: 'unknown' | 'permanent' | 'eligible' | 'active';
  /** RFC3339 end of the assignment's validity window */
  expires_at?: string;
  /** How the assignment is held: direct or inherited (e.g. via group) */
  member_type?: 'unknown' | 'direct' | 'inherited';
  /** Reference to the account or group the role is assigned to */
  principal_ref?: string;
  /** Kind of principal holding the role */
  principal_type?:
    | 'unknown'
    | 'account'
    | 'group'
    | 'device'
    | 'federation'
    | 'other';
  /** Reference to the role being assigned */
  role_ref?: string;
  /** Provider scope identifier when the assignment is narrower than tenant-wide */
  scope_ref?: string;
  /** RFC3339 start of the assignment's validity window */
  starts_at?: string;
}

/** RoleExtension schema */
export interface RoleExtension {
  _metadata?: EntityMetadata;
  role_permissions?: Array<PermissionEntry>;
  /** Reference to the role this extension belongs to */
  role_ref?: string;
}

/** RolePermission schema */
export interface RolePermission {
  _metadata?: EntityMetadata;
  /** Primary key for the permission in the source system */
  permission_ref?: string;
  /** Primary key for the role in the source system */
  role_ref?: string;
}

/** RolePrincipalSummary schema */
export interface RolePrincipalSummary {
  grant_count?: number;
  principal?: PrincipalRef;
  resource_count?: number;
}

/** RoleProfile schema */
export interface RoleProfile {
  accounts?: ProfileBlockAccount;
  applications?: ProfileBlockApplication;
  groups?: ProfileBlockGroup;
  permissions?: ProfileBlockPermission;
  role?: Role;
}

/** RoleReach schema */
export interface RoleReach {
  limit?: number;
  offset?: number;
  reach?: Array<RoleReachEntry>;
  role?: NamedRef;
  summary?: RoleReachSummary;
  total_count?: number;
  unapplied_permissions?: Array<PermissionRef>;
}

/** RoleReachEntry schema */
export interface RoleReachEntry {
  permissions?: Array<PermissionRef>;
  resource?: ResourceInfo;
}

/** RoleReachSummary schema */
export interface RoleReachSummary {
  by_resource_type?: Array<RoleReachTypeCount>;
  /** Distinct applied permissions */
  total_permissions?: number;
  total_resources?: number;
  total_unapplied?: number;
}

/** RoleReachTypeCount schema */
export interface RoleReachTypeCount {
  count?: number;
  type?: string;
}

/** RoleScopeSummary schema */
export interface RoleScopeSummary {
  grant_count?: number;
  resource_count?: number;
  scope?: ScopeInfo;
}

/** RoleSummary schema */
export interface RoleSummary {
  /** Grant entries attributed to this role (or to the null bucket) */
  grant_count?: number;
  /** Distinct resources reached through this role (or the null bucket) */
  resource_count?: number;
  role?: NamedRef;
}

/** RoleWithCount schema */
export interface RoleWithCount {
  Role?: Role;
  owner_count?: number;
}

/** Rule schema */
export interface Rule {
  enabled?: boolean;
  exactness?: string;
  family?: string;
  id?: string;
  namespace?: string;
  object?: Selector;
  one_way?: boolean;
  priority?: number;
  subject?: Selector;
}

/** ScopeInfo schema */
export interface ScopeInfo {
  container_name?: string;
  kind?: string;
  level?: 'resource' | 'resource_container' | 'organizational_unit';
  ref?: string;
}

/** ScopedAssignment schema */
export interface ScopedAssignment {
  _metadata?: EntityMetadata;
  /** Provider condition/ABAC expression (recorded, not evaluated) */
  condition?: string;
  /** Human-readable title of the provider condition (Condition holds the expression) */
  condition_title?: string;
  /** RFC3339 expiry for time-bound/JIT grants */
  expires_at?: string;
  /** Reference to the account, group, or federation service the role is assigned to */
  principal_ref?: string;
  /** Kind of principal the role is assigned to */
  principal_type?:
    | 'unknown'
    | 'account'
    | 'group'
    | 'device'
    | 'federation'
    | 'other';
  /** Reference to the role being assigned */
  role_ref?: string;
  /** "organizational_unit" or "resource" */
  scope_kind?:
    | 'unknown'
    | 'organizational_unit'
    | 'resource_container'
    | 'resource'
    | 'other';
  /** Ref of the OrganizationalUnit or Resource the assignment applies at */
  scope_ref?: string;
}

/** ScopedDenial schema */
export interface ScopedDenial {
  _metadata?: EntityMetadata;
  /** Provider denial condition/ABAC expression (recorded, not evaluated) */
  condition?: string;
  /** Whether the principal is denied the permission or exempted from the deny */
  effect?: 'unknown' | 'deny' | 'exception';
  /** Reference to the permission being denied */
  permission_ref?: string;
  /** Ref of the Policy this denial belongs to */
  policy_ref?: string;
  /** Reference to the account, group, or federation service the permission is denied to (or exempted for) */
  principal_ref?: string;
  /** Kind of principal the denial applies to */
  principal_type?:
    | 'unknown'
    | 'account'
    | 'group'
    | 'device'
    | 'federation'
    | 'other';
  /** "organizational_unit", "resource_container", or "resource" */
  scope_kind?:
    | 'unknown'
    | 'organizational_unit'
    | 'resource_container'
    | 'resource'
    | 'other';
  /** Ref of the OrganizationalUnit, ResourceContainer, or Resource the denial applies at */
  scope_ref?: string;
}

/** SearchHit schema */
export interface SearchHit {
  description?: string;
  entity_id?: string;
  entity_type?: string;
  metadata?: Record<string, string>;
  name?: string;
}

/** SearchResult schema */
export interface SearchResult {
  counts?: Record<string, number>;
  has_more?: boolean;
  next_cursor?: string;
  query?: string;
  results?: Array<SearchHit>;
  total?: number;
}

/** SearchResult_35270541 schema */
export interface SearchResult_35270541 {
  display_name?: string;
  entity_id?: string;
  ref?: string;
  space?: string;
}

/** Secret schema */
export interface Secret {
  _metadata?: EntityMetadata;
  /** When the secret or credential was created in the source system; age is derived from this */
  created_at?: string;
  /** Whether credential rotation is enabled for this secret */
  credential_rotation_enabled?: boolean;
  /** When the secret or credential expires, if applicable */
  expires_at?: string;
  /** Whether just-in-time access is enabled for this secret */
  jit_enabled?: boolean;
  /** Display name of the secret */
  name?: string;
  /** Provider-native path or locator for the secret */
  path?: string;
  /** Provider or secret manager this secret belongs to */
  provider?: string;
  /** Primary key for this secret in the source system */
  secret_ref?: string;
  /** Lifecycle/operational status of the secret in the source system (e.g. ENABLED, DISABLED) */
  status?: string;
  /** Provider-native secret type */
  type?: string;
  /** Whether zero standing privilege is enabled for this secret */
  zsp_enabled?: boolean;
}

/** SecretResource schema */
export interface SecretResource {
  _metadata?: EntityMetadata;
  /** ResourceRef of the store holding the secret */
  resource_ref?: string;
  /** SecretRef of the stored secret */
  secret_ref?: string;
}

/** SecurityConfiguration schema */
export interface SecurityConfiguration {
  _metadata?: EntityMetadata;
  /** Configuration category */
  category?: string;
  /** Primary key for this configuration in the source system */
  config_ref?: string;
  /** Human-readable description of the configuration */
  description?: string;
  /** Whether the configuration is enabled */
  is_enabled?: boolean;
  /** Configuration key */
  key?: string;
  /** When the configuration was last modified */
  last_modified?: string;
  /** Display name of the configuration */
  name?: string;
  /** Scope the configuration applies to */
  scope?: string;
  /** Configuration value */
  value?: string;
  /** Type of the value (for example: string, number, boolean) */
  value_type?: string;
}

/** Selector schema */
export interface Selector {
  scope?: Array<Predicate>;
  source?: Source;
  spaces?: Array<string>;
  transforms?: Array<Transform>;
}

/** SelectorStep schema */
export interface SelectorStep {
  stage?: string;
  transform?: string;
  values?: Array<string>;
}

/** ServiceAccountImpersonation schema */
export interface ServiceAccountImpersonation {
  _metadata?: EntityMetadata;
  condition?: ImpersonationCondition;
  /** What the binding lets the principal do to the service account */
  kind?: 'unknown' | 'can_impersonate' | 'can_use' | 'other';
  /** Verbatim IAM member string granted the impersonation role (e.g. user:alice@example.com, serviceAccount:sa@..., group:eng@...) */
  principal_ref?: string;
  /** Kind of principal the impersonation is granted to */
  principal_type?:
    | 'unknown'
    | 'account'
    | 'group'
    | 'device'
    | 'federation'
    | 'other';
  /** The IAM role that confers the impersonation (e.g. roles/iam.serviceAccountTokenCreator) */
  role_ref?: string;
  /** Ref of the service account being impersonated; equals its Account ref (serviceAccount:{email}) so the edge joins that Account */
  service_account_ref?: string;
}

/** Snapshot schema */
export interface Snapshot {
  account?: any;
  as_of?: string;
  group_memberships?: Array<GroupMembership>;
  replay?: ReplayStats;
  role_assignments?: Array<RoleAssignment>;
}

/** Source schema */
export interface Source {
  field?: string;
  kind?: string;
  ref?: string;
  ref_pattern?: string;
}

/** SpecialPrincipal schema */
export interface SpecialPrincipal {
  _metadata?: EntityMetadata;
  /** Classification of the special principal (all_users, all_authenticated_users, domain, principal_set, other) */
  kind?: string;
  /** Human-readable name of the principal set */
  name?: string;
  /** Verbatim binding member string, e.g. allUsers, domain:example.com, principalSet://... */
  principal_ref?: string;
  /** Bounding scope where applicable, e.g. the domain or the principalSet resource path */
  scope?: string;
}

/** StatsBucket schema */
export interface StatsBucket {
  /** Events in this bucket */
  count?: number;
  /** Bucket start, inclusive */
  ts?: string;
}

/** StatsConnector schema */
export interface StatsConnector {
  /** Connector identifier */
  connector_id?: string;
  /** Events from this connector over the window */
  count?: number;
  /** Connector display name; null when the connector no longer exists */
  name?: string;
  /** Provider identifier */
  provider_id?: string;
  /** Provider logo for dark themes */
  provider_logo_dark_uri?: string;
  /** Provider logo for light themes */
  provider_logo_light_uri?: string;
  /** Provider display name */
  provider_name?: string;
}

/** StatsGroup schema */
export interface StatsGroup {
  buckets?: Array<StatsBucket>;
  /** Events in this group over the whole window */
  count?: number;
  key?: StatsGroupKey;
}

/** StatsGroupKey schema */
export interface StatsGroupKey {
  /** Connector identifier */
  connector?: string;
  /** Registry discriminator */
  kind?: string;
  /** Normalized outcome bucket */
  result?: string;
  /** Catalog space */
  space?: string;
  /** Target entity type */
  target_type?: string;
}

/** StatsLiveness schema */
export interface StatsLiveness {
  /** Seconds since the newest event */
  age_seconds?: number;
  /** Newest event time seen, or null when the index is empty */
  newest_event_at?: string;
  /** Events per second over the trailing minute cells */
  rate_per_second?: number;
  /** live, recent, or idle */
  state?: string;
}

/** StatsTotals schema */
export interface StatsTotals {
  /** Whether the count was cut short by the scan budget */
  approximate?: boolean;
  /** Events matching the filter set over the window */
  count?: number;
}

/** StatsWindow schema */
export interface StatsWindow {
  /** Granularity, or null for totals only */
  bucket?: string;
  /** Inclusive lower bound */
  from?: string;
  /** Exclusive upper bound */
  to?: string;
}

/** StatsWindowAvailable schema */
export interface StatsWindowAvailable {
  /** Oldest bucket any granularity can answer */
  from?: string;
  /** Oldest instant bucket=minute can answer */
  minute_horizon?: string;
  /** Newest bucket any granularity can answer */
  to?: string;
}

/** StoredDetections schema */
export interface StoredDetections {
  anomaly?: AnomalyResult;
  privilege?: PrivilegeResult;
  tier?: string;
}

/** Ticket schema */
export interface Ticket {
  _metadata?: EntityMetadata;
  /** Display name of the assignee */
  assignee_name?: string;
  /** Account ref of the assigned agent */
  assignee_ref?: string;
  /** Name of the assigned group */
  assignment_group_name?: string;
  /** Ref of the assigned group or team */
  assignment_group_ref?: string;
  /** Top-level classification category */
  category?: string;
  /** Closure classification if different from resolution */
  close_code?: string;
  /** Final closure summary */
  close_notes?: string;
  /** When the ticket was closed */
  closed_at?: string;
  /** When the ticket was created in the source system */
  created_at?: string;
  /** Full description or body text */
  description?: string;
  /** SLA deadline */
  due_at?: string;
  /** Cross-system correlation identifier */
  external_id?: string;
  /** Source-system impact value */
  impact?: string;
  /** Normalized priority: Critical, High, Medium, Low */
  priority?: string;
  /** Email of the requester */
  requester_email?: string;
  /** Display name of the requester */
  requester_name?: string;
  /** Account ref of the person who reported or requested the ticket */
  requester_ref?: string;
  /** Resolution classification: Solved, Workaround, Duplicate, CannotReproduce, WontFix */
  resolution_code?: string;
  /** Description of how the ticket was resolved */
  resolution_notes?: string;
  /** When the ticket was resolved */
  resolved_at?: string;
  /** Creation channel: Email, Portal, Phone, API, Chat, Other */
  source?: string;
  /** Current lifecycle state: New, Open, InProgress, Pending, Resolved, Closed, Cancelled */
  status?: string;
  /** Reason for current status (e.g., awaiting customer, awaiting vendor) */
  status_reason?: string;
  /** Second-level classification */
  subcategory?: string;
  /** Freeform labels or tags */
  tags?: Array<string>;
  /** Stable external identifier (e.g., INC0010001, PROJ-123, ZD-4567) */
  ticket_ref?: string;
  /** Classification: Incident, ServiceRequest, Problem, Change, Task, Unknown */
  ticket_type?: string;
  /** Short summary or subject line */
  title?: string;
  /** Source-system urgency (for systems that derive priority from urgency x impact) */
  urgency?: string;
}

/** TicketComment schema */
export interface TicketComment {
  _metadata?: EntityMetadata;
  /** Display name of the author */
  author_name?: string;
  /** Account ref of the comment author */
  author_ref?: string;
  /** Comment text content */
  body?: string;
  /** Unique reference for this comment in the source system */
  comment_ref?: string;
  /** When the comment was created */
  created_at?: string;
  /** Whether this is an internal work note (not visible to requester) */
  is_internal?: boolean;
  /** Reference to the parent ticket */
  ticket_ref?: string;
}

/** Transform schema */
export interface Transform {
  field?: string;
  kind?: string;
  pattern?: string;
  template?: string;
}

/** VaultedCredentialsResponse schema */
export interface VaultedCredentialsResponse {
  account?: Endpoint;
  authenticates_as?: Array<Relationship>;
  candidate_authenticates_as?: Array<Relationship>;
  candidate_credentials?: Array<Relationship>;
  credentials?: Array<Relationship>;
  projection_enabled?: boolean;
}

/** accountDistribution schema */
export interface accountDistribution {
  by_status?: byStatus;
  by_type?: byType;
}

/** accountHydrationRequest schema */
export interface accountHydrationRequest {
  account_id: string;
  at: string;
}

/** accountHydrationResponse schema */
export interface accountHydrationResponse {
  account_id?: string;
  at?: string;
  error?: string;
  expires_at?: string;
  hydration_id?: string;
  result?: Snapshot;
  status?: 'pending' | 'running' | 'completed' | 'failed';
}

/** accountRow schema */
export interface accountRow {
  attributes?: Record<string, string>;
  group_memberships?: groupMembership;
  projection_created_at?: string;
  projection_updated_at?: string;
}

/** agentRow schema */
export interface agentRow {
  Agent?: Agent;
  /** Name of the hosting application; empty when none is linked */
  application_name?: string;
  /** ApplicationRef of the hosting application; empty when none is linked */
  application_ref?: string;
  /** Capabilities (tools, actions, knowledge bases) wired to this agent */
  capability_count?: number;
  /** Whether the agent has at least one owning account */
  has_owner?: boolean;
  projection_created_at?: string;
  projection_updated_at?: string;
  /** AccountRef of the runtime identity the agent runs as; empty when none is linked */
  runtime_account_ref?: string;
}

/** applicationRow schema */
export interface applicationRow {
  ApplicationWithCount?: ApplicationWithCount;
  attributes?: Record<string, string>;
  projection_created_at?: string;
  projection_updated_at?: string;
  /** Mean risk score across linked accounts; null when none carry one */
  risk_score?: number;
}

/** applicationStatisticsResponse schema */
export interface applicationStatisticsResponse {
  account_distribution?: accountDistribution;
  application_id?: string;
  average_risk_score?: number;
  last_calculated?: string;
  risk_metrics?: riskMetricsResponse;
  total_accounts?: number;
}

/** batchAccessReviewsRequest schema */
export interface batchAccessReviewsRequest {
  items?: Array<AccessReview>;
}

/** batchAccountAttributesRequest schema */
export interface batchAccountAttributesRequest {
  items?: Array<AccountAttribute>;
}

/** batchAccountClassificationsRequest schema */
export interface batchAccountClassificationsRequest {
  items?: Array<AccountClassification>;
}

/** batchAccountExtensionsRequest schema */
export interface batchAccountExtensionsRequest {
  items?: Array<AccountExtension>;
}

/** batchAccountLicensesRequest schema */
export interface batchAccountLicensesRequest {
  items?: Array<AccountLicense>;
}

/** batchAccountManagersRequest schema */
export interface batchAccountManagersRequest {
  items?: Array<AccountManager>;
}

/** batchAccountMultiFactorsRequest schema */
export interface batchAccountMultiFactorsRequest {
  items?: Array<AccountMultiFactor>;
}

/** batchAccountPermissionsRequest schema */
export interface batchAccountPermissionsRequest {
  items?: Array<AccountPermission>;
}

/** batchAccountPoliciesRequest schema */
export interface batchAccountPoliciesRequest {
  items?: Array<AccountPolicy>;
}

/** batchAccountRiskFactorsRequest schema */
export interface batchAccountRiskFactorsRequest {
  items?: Array<AccountRiskFactor>;
}

/** batchAccountRolesRequest schema */
export interface batchAccountRolesRequest {
  items?: Array<AccountRole>;
}

/** batchAccountSecretsRequest schema */
export interface batchAccountSecretsRequest {
  items?: Array<AccountSecret>;
}

/** batchAccountsRequest schema */
export interface batchAccountsRequest {
  items?: Array<Account>;
}

/** batchAgentAccountsRequest schema */
export interface batchAgentAccountsRequest {
  items?: Array<AgentAccount>;
}

/** batchAgentCapabilitiesRequest schema */
export interface batchAgentCapabilitiesRequest {
  items?: Array<AgentCapability>;
}

/** batchAgentDeploymentAttributesRequest schema */
export interface batchAgentDeploymentAttributesRequest {
  items?: Array<AgentDeploymentAttribute>;
}

/** batchAgentDeploymentExtensionsRequest schema */
export interface batchAgentDeploymentExtensionsRequest {
  items?: Array<AgentDeploymentExtension>;
}

/** batchAgentDeploymentResourcesRequest schema */
export interface batchAgentDeploymentResourcesRequest {
  items?: Array<AgentDeploymentResource>;
}

/** batchAgentDeploymentsRequest schema */
export interface batchAgentDeploymentsRequest {
  items?: Array<AgentDeployment>;
}

/** batchAgentOwnersRequest schema */
export interface batchAgentOwnersRequest {
  items?: Array<AgentOwner>;
}

/** batchAgentsRequest schema */
export interface batchAgentsRequest {
  items?: Array<Agent>;
}

/** batchApplicationAccountsRequest schema */
export interface batchApplicationAccountsRequest {
  items?: Array<ApplicationAccount>;
}

/** batchApplicationAgentsRequest schema */
export interface batchApplicationAgentsRequest {
  items?: Array<ApplicationAgent>;
}

/** batchApplicationExtensionsRequest schema */
export interface batchApplicationExtensionsRequest {
  items?: Array<ApplicationExtension>;
}

/** batchApplicationGroupsRequest schema */
export interface batchApplicationGroupsRequest {
  items?: Array<ApplicationGroup>;
}

/** batchApplicationPermissionsRequest schema */
export interface batchApplicationPermissionsRequest {
  items?: Array<ApplicationPermission>;
}

/** batchApplicationRolesRequest schema */
export interface batchApplicationRolesRequest {
  items?: Array<ApplicationRole>;
}

/** batchApplicationsRequest schema */
export interface batchApplicationsRequest {
  items?: Array<Application>;
}

/** batchAttributesRequest schema */
export interface batchAttributesRequest {
  items?: Array<Attribute>;
}

/** batchCampaignsRequest schema */
export interface batchCampaignsRequest {
  items?: Array<Campaign>;
}

/** batchChannelAccountsRequest schema */
export interface batchChannelAccountsRequest {
  items?: Array<ChannelAccount>;
}

/** batchChannelsRequest schema */
export interface batchChannelsRequest {
  items?: Array<Channel>;
}

/** batchClassificationsRequest schema */
export interface batchClassificationsRequest {
  items?: Array<Classification>;
}

/** batchComplianceBaselinesRequest schema */
export interface batchComplianceBaselinesRequest {
  items?: Array<ComplianceBaseline>;
}

/** batchContactsRequest schema */
export interface batchContactsRequest {
  items?: Array<Contact>;
}

/** batchDeviceAccountsRequest schema */
export interface batchDeviceAccountsRequest {
  items?: Array<DeviceAccount>;
}

/** batchDeviceApplicationsRequest schema */
export interface batchDeviceApplicationsRequest {
  items?: Array<DeviceApplication>;
}

/** batchDevicesRequest schema */
export interface batchDevicesRequest {
  items?: Array<Device>;
}

/** batchEmployeesRequest schema */
export interface batchEmployeesRequest {
  items?: Array<Employee>;
}

/** batchFederationServiceProvidersRequest schema */
export interface batchFederationServiceProvidersRequest {
  items?: Array<FederationServiceProvider>;
}

/** batchFederationServicesRequest schema */
export interface batchFederationServicesRequest {
  items?: Array<FederationService>;
}

/** batchGroupAttributesRequest schema */
export interface batchGroupAttributesRequest {
  items?: Array<GroupAttribute>;
}

/** batchGroupExtensionsRequest schema */
export interface batchGroupExtensionsRequest {
  items?: Array<GroupExtension>;
}

/** batchGroupLicensesRequest schema */
export interface batchGroupLicensesRequest {
  items?: Array<GroupLicense>;
}

/** batchGroupMembersRequest schema */
export interface batchGroupMembersRequest {
  items?: Array<GroupMember>;
}

/** batchGroupMembershipRolesRequest schema */
export interface batchGroupMembershipRolesRequest {
  items?: Array<GroupMembershipRole>;
}

/** batchGroupPermissionsRequest schema */
export interface batchGroupPermissionsRequest {
  items?: Array<GroupPermission>;
}

/** batchGroupRolesRequest schema */
export interface batchGroupRolesRequest {
  items?: Array<GroupRole>;
}

/** batchGroupsRequest schema */
export interface batchGroupsRequest {
  items?: Array<Group>;
}

/** batchLicensesRequest schema */
export interface batchLicensesRequest {
  items?: Array<License>;
}

/** batchLocationsRequest schema */
export interface batchLocationsRequest {
  items?: Array<Location>;
}

/** batchMultiFactorsRequest schema */
export interface batchMultiFactorsRequest {
  items?: Array<MultiFactor>;
}

/** batchNetworkObjectsRequest schema */
export interface batchNetworkObjectsRequest {
  items?: Array<NetworkObject>;
}

/** batchOrganizationalUnitMembersRequest schema */
export interface batchOrganizationalUnitMembersRequest {
  items?: Array<OrganizationalUnitMember>;
}

/** batchOrganizationalUnitsRequest schema */
export interface batchOrganizationalUnitsRequest {
  items?: Array<OrganizationalUnit>;
}

/** batchPasswordPoliciesRequest schema */
export interface batchPasswordPoliciesRequest {
  items?: Array<PasswordPolicy>;
}

/** batchPeopleExtensionsRequest schema */
export interface batchPeopleExtensionsRequest {
  items?: Array<PeopleExtension>;
}

/** batchPermissionsRequest schema */
export interface batchPermissionsRequest {
  items?: Array<Permission>;
}

/** batchPersonAccountsRequest schema */
export interface batchPersonAccountsRequest {
  items?: Array<PersonAccount>;
}

/** batchPersonAttributesRequest schema */
export interface batchPersonAttributesRequest {
  items?: Array<PersonAttribute>;
}

/** batchPersonsRequest schema */
export interface batchPersonsRequest {
  items?: Array<Person>;
}

/** batchPoliciesRequest schema */
export interface batchPoliciesRequest {
  items?: Array<Policy>;
}

/** batchPolicyExtensionsRequest schema */
export interface batchPolicyExtensionsRequest {
  items?: Array<PolicyExtension>;
}

/** batchResourceContainerExtensionsRequest schema */
export interface batchResourceContainerExtensionsRequest {
  items?: Array<ResourceContainerExtension>;
}

/** batchResourceContainerResourceContainersRequest schema */
export interface batchResourceContainerResourceContainersRequest {
  items?: Array<ResourceContainerResourceContainer>;
}

/** batchResourceContainerResourcesRequest schema */
export interface batchResourceContainerResourcesRequest {
  items?: Array<ResourceContainerResource>;
}

/** batchResourceContainersRequest schema */
export interface batchResourceContainersRequest {
  items?: Array<ResourceContainer>;
}

/** batchResourceExtensionsRequest schema */
export interface batchResourceExtensionsRequest {
  items?: Array<ResourceExtension>;
}

/** batchResourcePermissionsRequest schema */
export interface batchResourcePermissionsRequest {
  items?: Array<ResourcePermission>;
}

/** batchResourcesRequest schema */
export interface batchResourcesRequest {
  items?: Array<Resource>;
}

/** batchRiskFactorsRequest schema */
export interface batchRiskFactorsRequest {
  items?: Array<RiskFactor>;
}

/** batchRoleAssignmentTypesRequest schema */
export interface batchRoleAssignmentTypesRequest {
  items?: Array<RoleAssignmentType>;
}

/** batchRoleExtensionsRequest schema */
export interface batchRoleExtensionsRequest {
  items?: Array<RoleExtension>;
}

/** batchRolePermissionsRequest schema */
export interface batchRolePermissionsRequest {
  items?: Array<RolePermission>;
}

/** batchRolesRequest schema */
export interface batchRolesRequest {
  items?: Array<Role>;
}

/** batchScopedAssignmentsRequest schema */
export interface batchScopedAssignmentsRequest {
  items?: Array<ScopedAssignment>;
}

/** batchScopedDenialsRequest schema */
export interface batchScopedDenialsRequest {
  items?: Array<ScopedDenial>;
}

/** batchSecretResourcesRequest schema */
export interface batchSecretResourcesRequest {
  items?: Array<SecretResource>;
}

/** batchSecretsRequest schema */
export interface batchSecretsRequest {
  items?: Array<Secret>;
}

/** batchSecurityConfigurationsRequest schema */
export interface batchSecurityConfigurationsRequest {
  items?: Array<SecurityConfiguration>;
}

/** batchServiceAccountImpersonationsRequest schema */
export interface batchServiceAccountImpersonationsRequest {
  items?: Array<ServiceAccountImpersonation>;
}

/** batchSpecialPrincipalsRequest schema */
export interface batchSpecialPrincipalsRequest {
  items?: Array<SpecialPrincipal>;
}

/** batchTicketCommentsRequest schema */
export interface batchTicketCommentsRequest {
  items?: Array<TicketComment>;
}

/** batchTicketsRequest schema */
export interface batchTicketsRequest {
  items?: Array<Ticket>;
}

/** byStatus schema */
export interface byStatus {
  active?: number;
  disabled?: number;
  other?: number;
  suspended?: number;
}

/** byType schema */
export interface byType {
  admin?: number;
  other?: number;
  service?: number;
  user?: number;
}

/** catalogEventBatchRequest schema */
export interface catalogEventBatchRequest {
  items?: Array<catalogEventEnvelopeRequest>;
}

/** catalogEventEnvelopeRequest schema */
export interface catalogEventEnvelopeRequest {
  $type?:
    | 'mesh://catalog/v1/events/access_denied'
    | 'mesh://catalog/v1/events/access_request_approved'
    | 'mesh://catalog/v1/events/access_request_denied'
    | 'mesh://catalog/v1/events/access_request_submitted'
    | 'mesh://catalog/v1/events/access_review_completed'
    | 'mesh://catalog/v1/events/access_review_item_added'
    | 'mesh://catalog/v1/events/access_review_reminder_sent'
    | 'mesh://catalog/v1/events/access_review_reviewer_assigned'
    | 'mesh://catalog/v1/events/access_review_reviewer_removed'
    | 'mesh://catalog/v1/events/account_created'
    | 'mesh://catalog/v1/events/account_deleted'
    | 'mesh://catalog/v1/events/account_disabled'
    | 'mesh://catalog/v1/events/account_display_name_changed'
    | 'mesh://catalog/v1/events/account_email_changed'
    | 'mesh://catalog/v1/events/account_enabled'
    | 'mesh://catalog/v1/events/account_locked'
    | 'mesh://catalog/v1/events/account_unlocked'
    | 'mesh://catalog/v1/events/administrative_action_performed'
    | 'mesh://catalog/v1/events/administrative_unit_member_added'
    | 'mesh://catalog/v1/events/administrative_unit_member_removed'
    | 'mesh://catalog/v1/events/agent_created'
    | 'mesh://catalog/v1/events/agent_deleted'
    | 'mesh://catalog/v1/events/agent_invoked'
    | 'mesh://catalog/v1/events/agent_login_failed'
    | 'mesh://catalog/v1/events/agent_login_succeeded'
    | 'mesh://catalog/v1/events/agent_permission_changed'
    | 'mesh://catalog/v1/events/agent_risk_state_changed'
    | 'mesh://catalog/v1/events/agent_risky_activity_detected'
    | 'mesh://catalog/v1/events/agent_task_executed'
    | 'mesh://catalog/v1/events/agent_updated'
    | 'mesh://catalog/v1/events/baseline_updated'
    | 'mesh://catalog/v1/events/campaign_completed'
    | 'mesh://catalog/v1/events/campaign_created'
    | 'mesh://catalog/v1/events/campaign_deleted'
    | 'mesh://catalog/v1/events/campaign_start_queued'
    | 'mesh://catalog/v1/events/campaign_started'
    | 'mesh://catalog/v1/events/campaign_updated'
    | 'mesh://catalog/v1/events/channel_created'
    | 'mesh://catalog/v1/events/channel_removed'
    | 'mesh://catalog/v1/events/classification_applied'
    | 'mesh://catalog/v1/events/classification_revoked'
    | 'mesh://catalog/v1/events/compliance_check_completed'
    | 'mesh://catalog/v1/events/conditional_access_blocked'
    | 'mesh://catalog/v1/events/connector_space_purged'
    | 'mesh://catalog/v1/events/consent_granted'
    | 'mesh://catalog/v1/events/consent_requested'
    | 'mesh://catalog/v1/events/credential_added'
    | 'mesh://catalog/v1/events/credential_removed'
    | 'mesh://catalog/v1/events/detection_raised'
    | 'mesh://catalog/v1/events/device_compliance_changed'
    | 'mesh://catalog/v1/events/device_registered'
    | 'mesh://catalog/v1/events/device_removed'
    | 'mesh://catalog/v1/events/entity_patched'
    | 'mesh://catalog/v1/events/entity_removed'
    | 'mesh://catalog/v1/events/exception_expired'
    | 'mesh://catalog/v1/events/exception_granted'
    | 'mesh://catalog/v1/events/exception_revoked'
    | 'mesh://catalog/v1/events/group_created'
    | 'mesh://catalog/v1/events/group_description_changed'
    | 'mesh://catalog/v1/events/group_member_added'
    | 'mesh://catalog/v1/events/group_member_removed'
    | 'mesh://catalog/v1/events/group_name_changed'
    | 'mesh://catalog/v1/events/group_removed'
    | 'mesh://catalog/v1/events/license_assigned'
    | 'mesh://catalog/v1/events/license_removed'
    | 'mesh://catalog/v1/events/login_failed'
    | 'mesh://catalog/v1/events/login_succeeded'
    | 'mesh://catalog/v1/events/mesh_action_taken'
    | 'mesh://catalog/v1/events/mfa_challenge'
    | 'mesh://catalog/v1/events/mfa_failed'
    | 'mesh://catalog/v1/events/mfa_succeeded'
    | 'mesh://catalog/v1/events/multi_factor_registered'
    | 'mesh://catalog/v1/events/multi_factor_removed'
    | 'mesh://catalog/v1/events/multi_factor_updated'
    | 'mesh://catalog/v1/events/password_changed'
    | 'mesh://catalog/v1/events/password_reset'
    | 'mesh://catalog/v1/events/permission_granted'
    | 'mesh://catalog/v1/events/permission_revoked'
    | 'mesh://catalog/v1/events/policy_modified'
    | 'mesh://catalog/v1/events/policy_violation_detected'
    | 'mesh://catalog/v1/events/process_approval_decided'
    | 'mesh://catalog/v1/events/process_approval_requested'
    | 'mesh://catalog/v1/events/process_run_completed'
    | 'mesh://catalog/v1/events/relationship_broken'
    | 'mesh://catalog/v1/events/relationship_established'
    | 'mesh://catalog/v1/events/resource_accessed'
    | 'mesh://catalog/v1/events/resource_created'
    | 'mesh://catalog/v1/events/resource_deleted'
    | 'mesh://catalog/v1/events/resource_updated'
    | 'mesh://catalog/v1/events/risk_cleared'
    | 'mesh://catalog/v1/events/risk_detected'
    | 'mesh://catalog/v1/events/role_assigned'
    | 'mesh://catalog/v1/events/role_removed'
    | 'mesh://catalog/v1/events/security_alert'
    | 'mesh://catalog/v1/events/sensitive_content_expiring'
    | 'mesh://catalog/v1/events/sensitive_data_accessed'
    | 'mesh://catalog/v1/events/session_created'
    | 'mesh://catalog/v1/events/session_terminated'
    | 'mesh://catalog/v1/events/siem_destination_unreachable'
    | 'mesh://catalog/v1/events/suspicious_activity'
    | 'mesh://catalog/v1/events/ticket_approved'
    | 'mesh://catalog/v1/events/ticket_closed'
    | 'mesh://catalog/v1/events/ticket_created'
    | 'mesh://catalog/v1/events/ticket_rejected';
  content?: any;
}

/** classificationStatisticsResponse schema */
export interface classificationStatisticsResponse {
  active_count?: number;
  admin_account_count?: number;
  breached_accounts_count?: number;
  disabled_count?: number;
  failed_logins_count?: number;
  highly_privileged_count?: number;
  mfa_disabled_count?: number;
  no_owner_count?: number;
  password_age_180_count?: number;
  password_age_365_count?: number;
  password_age_90_count?: number;
  password_never_set_count?: number;
  platform_distribution?: Record<string, number>;
  privileged_count?: number;
  privileged_not_vaulted_count?: number;
  risk_threshold?: number;
  service_account_count?: number;
  shared_accounts_count?: number;
  stale_180_days_count?: number;
  stale_365_days_count?: number;
  stale_90_days_count?: number;
  suspended_count?: number;
  top_risk_accounts?: Array<topRiskAccount>;
  user_account_count?: number;
}

/** classificationWithStats schema */
export interface classificationWithStats {
  Classification?: Classification;
  total_accounts?: number;
}

/** clusterDetail schema */
export interface clusterDetail {
  members?: Array<string>;
}

/** clusterSummary schema */
export interface clusterSummary {
  characterization?: Array<string>;
  id?: string;
  size?: number;
}

/** clustersResponse schema */
export interface clustersResponse {
  clusters?: Array<clusterSummary>;
}

/** facetBucket schema */
export interface facetBucket {
  /** Rows in the currently filtered cohort carrying this value */
  count?: number;
  /** Distinct value, usable verbatim as a filter operand */
  value?: string;
}

/** facetDimension schema */
export interface facetDimension {
  /** True when values were capped and the list is incomplete */
  truncated?: boolean;
  values?: Array<facetBucket>;
}

/** feedbackRequest schema */
export interface feedbackRequest {
  account_ref?: string;
  action?: 'confirm' | 'reject' | 'clear';
}

/** feedbackResponse schema */
export interface feedbackResponse {
  account_ref?: string;
  action?: string;
  status?: string;
}

/** groupMembership schema */
export interface groupMembership {
  group_name?: string;
  group_ref?: string;
}

/** groupRow schema */
export interface groupRow {
  GroupWithCount?: GroupWithCount;
  attributes?: Record<string, string>;
  projection_created_at?: string;
  projection_updated_at?: string;
}

/** mfaDisabled schema */
export interface mfaDisabled {
  count?: number;
  percentage?: number;
}

/** otherRisks schema */
export interface otherRisks {
  breached?: number;
  failed_logins?: number;
  no_owner?: number;
  shared_accounts?: number;
}

/** passwordIssues schema */
export interface passwordIssues {
  age_180_days?: number;
  age_365_days?: number;
  age_90_days?: number;
  never_set?: number;
}

/** personRow schema */
export interface personRow {
  /** Accounts belonging to this person, matched by email */
  account_count?: number;
  attributes?: Record<string, string>;
  projection_created_at?: string;
  projection_updated_at?: string;
}

/** privilegedMetrics schema */
export interface privilegedMetrics {
  highly_privileged?: number;
  not_vaulted?: number;
  not_vaulted_percentage?: number;
  total_privileged?: number;
}

/** rankingResponse schema */
export interface rankingResponse {
  accounts?: Array<DetectionRow>;
  kind?: 'privileged' | 'anomalous';
}

/** rbacResolveResponse schema */
export interface rbacResolveResponse {
  policies?: Array<PolicyExtension>;
}

/** relationshipDecisionRequest schema */
export interface relationshipDecisionRequest {
  family?: string;
  object_connector_id?: string;
  object_ref?: string;
  object_space?: string;
  subject_connector_id?: string;
  subject_ref?: string;
  subject_space?: string;
}

/** relationshipDecisionResponse schema */
export interface relationshipDecisionResponse {
  actor?: string;
  decision?: string;
  family?: string;
  object_ref?: string;
  recorded?: boolean;
  subject_ref?: string;
}

/** resourceRow schema */
export interface resourceRow {
  Resource?: Resource;
  /** Accounts that can reach this resource; null until the entitlement graph supplies it */
  account_count?: number;
  /** Ref of the containing resource container, if any */
  container_ref?: string;
  projection_created_at?: string;
  projection_updated_at?: string;
}

/** riskMetricsResponse schema */
export interface riskMetricsResponse {
  mfa_disabled?: mfaDisabled;
  other_risks?: otherRisks;
  password_issues?: passwordIssues;
  privileged?: privilegedMetrics;
  risk_threshold?: number;
  stale_accounts?: staleAccounts;
  top_risk_accounts?: Array<topRiskAccount>;
}

/** roleOverlap schema */
export interface roleOverlap {
  exclusive_accounts?: number;
  overlap_percentage?: number;
  role_id?: string;
  role_name?: string;
  shared_accounts?: number;
}

/** roleOverlapAnalysisResponse schema */
export interface roleOverlapAnalysisResponse {
  related_roles?: Array<roleOverlap>;
  role_id?: string;
  role_name?: string;
  total_accounts?: number;
}

/** roleRow schema */
export interface roleRow {
  RoleWithCount?: RoleWithCount;
  projection_created_at?: string;
  projection_updated_at?: string;
}

/** staleAccounts schema */
export interface staleAccounts {
  percentage_180?: number;
  stale_180_days?: number;
  stale_365_days?: number;
  stale_90_days?: number;
}

/** topRiskAccount schema */
export interface topRiskAccount {
  account_ref?: string;
  name?: string;
  risk_score?: number;
}
